import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon,  ChevronLeft,  ChevronRight,  DollarSign,  Search,  X,  RefreshCw,
  Truck,  Plus,  Trash2,  ExternalLink,  Layers,  Download,  UserCheck,  Check,  CheckCircle2,  AlertTriangle,  Settings,  Link2,  Unlink,  Package,  Eye,  EyeOff,  Import,  Lock,  Loader2,  Bell,  CheckCheck
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { api } from "../sales-components/api.js";

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/+$/, '')}/api`;
const SHIPPING_OPTIONS = [
  { value: 'PICKUP', label: 'Pickup' },
  { value: 'ASA SHIP', label: 'ASA Ship' },
  { value: 'ROUTE LACHINE', label: 'Route Lachine' },
  { value: 'ROUTE BAY', label: 'Route Bay' },
  { value: 'SHIP CLIENT PORTAL', label: 'Client Portal' }
];
const SHIPPED_STATUS_OPTIONS = [
  { value: '', label: 'Pending' },
  { value: 'Ready for shipping', label: 'Ready to shipping' },
  { value: 'Shipped', label: 'Shipped' },
  { value: 'Partially Shipped', label: 'Partially Shipped' },
  { value: 'Picked Up', label: 'Picked Up' },
  { value: 'In Transit', label: 'In Transit' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Issue', label: 'Issue' },
  { value: 'On Hold', label: 'On Hold' }
];
const INITIAL_CUSTOMER_SHIP_PREFS = {};
const CALENDAR_RANGES = [
  { value: 1, label: '1 Week' },
  { value: 2, label: '2 Weeks' },
  { value: 4, label: '4 Weeks' }
];
const STATIC_ORDERS = [];

// ─────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────

const getTodayStr = () => new Date().toISOString().split('T')[0];
const getTodayDateTimeStr = () => new Date().toISOString();

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

const calculateLeadTimeInDays = (soDateStr, deadlineStr) => {
  if (!soDateStr || !deadlineStr) return 0;
  const diffTime = new Date(deadlineStr).getTime() - new Date(soDateStr).getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

const checkPrepaymentRequired = (terms) => {
  if (!terms) return false;
  const t = String(terms).trim().toUpperCase();
  return (
    t.includes('PREPAID') ||
    t.includes('50 DEPOSIT/50 COD') ||
    t.includes('100% ADVANCE') ||
    t.includes('50% DEPOSIT / 50% NET 30')
  );
};

const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday;
};

// ─────────────────────────────────────────────
// TIMEZONE-SAFE UTILITIES
// ─────────────────────────────────────────────

const formatDateStr = (dateVal) => {
  if (!dateVal) return '';
  try {
    const valToParse = (typeof dateVal === 'object' && dateVal.$date) ? dateVal.$date : dateVal;
    const str = String(valToParse).trim();
    // Directly extract YYYY-MM-DD if already formatted or ISO string to avoid timezone offset shifts
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    const d = new Date(valToParse);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  } catch { return ''; }
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const val = (typeof dateStr === 'object' && dateStr?.$date) ? dateStr.$date : dateStr;
    const str = String(val).trim();
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, y, m, d] = match;
      const utcDate = new Date(Date.UTC(+y, +m - 1, +d));
      return utcDate.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: '2-digit'
      });
    }
    const d = new Date(str);
    return isNaN(d.getTime()) ? str : d.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: '2-digit'
    });
  } catch { return dateStr; }
};

const mapApiOrderToPlanner = (raw, revenueMap = {}, unitsMap = {}, itemCountMap = {}) => {
  const soDate = formatDateStr(raw.orderDateTime || raw.creationDate);
  const soRevenue = revenueMap[raw.salesOrderNumber] || raw.amount || 0;
  const soUnits = unitsMap[raw.salesOrderNumber] || 0;
  const soItemCount = itemCountMap[raw.salesOrderNumber] || 0;
  const termsOfPayment = raw.termsOfPayment || '';
  const isInternational = (raw.typeTaxLocation || '').toLowerCase().includes('international') ||
    (raw.internationalTrade === 1);
    
  const isReturned = (raw.status || '').toLowerCase() === 'returned' ||
    (raw.shippingStatus || '').toLowerCase() === 'returned';

  // ✅ FIX: Only mark completed if explicitly true in DB (handle boolean and string "true")
  const isCompleted = (raw.completed === true || raw.completed === 'true');
  
  // --- DEADLINE & LEAD TIME LOGIC (ENHANCED) ---
  const defaultLeadDays = isInternational ? 28 : 14;
  
  // Prioritize dynamic lead time from Customer Families, then order leadTime, then default
  const dbLeadTime = (raw.dynamicLeadTime !== undefined && raw.dynamicLeadTime !== null && !isNaN(parseInt(raw.dynamicLeadTime, 10)))
    ? parseInt(raw.dynamicLeadTime, 10)
    : (raw.leadTime !== undefined && raw.leadTime !== null && !isNaN(parseInt(raw.leadTime, 10))) 
      ? parseInt(raw.leadTime, 10) 
      : defaultLeadDays;

  const soDateObj = soDate ? new Date(soDate) : new Date();
  
  let deadline = '';
  let displayLeadTime = dbLeadTime;

  // Use calculatedDeadline or raw.deadline returned from API
  const finalDeadlineDate = raw.deadline || raw.calculatedDeadline;

  if (finalDeadlineDate) {
    deadline = formatDateStr(finalDeadlineDate);
    // Ensure display Lead Time accurately reflects this specific date
    const calculatedDays = calculateLeadTimeInDays(soDate, deadline);
    displayLeadTime = (calculatedDays !== null && !isNaN(calculatedDays)) ? calculatedDays : dbLeadTime;
  } else {
    // FALLBACK: Calculate using SO Date + dbLeadTime
    const calcDeadline = new Date(soDateObj);
    calcDeadline.setDate(calcDeadline.getDate() + dbLeadTime);
    deadline = formatDateStr(calcDeadline.toISOString().split('T')[0]);
    displayLeadTime = dbLeadTime;
  }
  // ---------------------------------------------

  const isPrepaymentRequired = checkPrepaymentRequired(termsOfPayment) || false;

  return {
    id: raw._id || `api-${raw.salesOrderNumber}`, 
    salesOrderNumber: raw.salesOrderNumber,   
    soDate,
    leadTime: displayLeadTime,
    deadline,
    cancelDate: raw.cancelDate ? formatDateStr(raw.cancelDate) : '',
    hasCancelDate: raw.hasCancelDate === true || raw.hasCancelDate === 'true' || !!raw.cancelDate,
    invoiceDate: raw.invoiceDate ? formatDateStr(raw.invoiceDate) : '',

    // ── NEW SHIPPING DEADLINE & TRANSIT FIELDS ──
    region: raw.region && raw.region !=='N/A' ? raw.region :  null,
    transitLevel: raw.transitLevel || '',
    transitDays: (raw.transitDays !== undefined && raw.transitDays !== null) ? Number(raw.transitDays) : null,
    deadlinePriority: raw.deadlinePriority || null,
    requestedDeliveryDate: (raw.requestedDeliveryDate || raw.startShipDate) 
      ? formatDateStr(raw.requestedDeliveryDate || raw.startShipDate) 
      : '',
    startShipDate: (raw.startShipDate || raw.requestedDeliveryDate) 
      ? formatDateStr(raw.startShipDate || raw.requestedDeliveryDate) 
      : '',
    requiresDeadlineConfirmation: raw.requiresDeadlineConfirmation === true || raw.requiresDeadlineConfirmation === 'true',
    confirmationMessage: raw.confirmationMessage || '',
    deadlineExceptions: Array.isArray(raw.deadlineExceptions) 
      ? raw.deadlineExceptions 
      : (raw.deadlineExceptions ? [raw.deadlineExceptions] : []),
    manualDeadlineOverride: raw.manualDeadlineOverride === true || 
      raw.manualDeadlineOverride === 'true' || 
      raw.setDeadlineManually === true || 
      raw.setDeadlineManually === 'true',
    // ──────────────────────────────────────────

    needsDeliveryAppt: raw.needsDeliveryAppt === 'true' || raw.needsDeliveryAppt === true,
    deliveryApptDate: raw.deliveryApptDate ? formatDateStr(raw.deliveryApptDate) : '',
    deliveryApptTime: raw.deliveryApptTime || '',
    deliveryConfirmationNo: raw.deliveryConfirmationNo || '',
    deliveryApptConfirmed: raw.deliveryApptConfirmed === 'true' || raw.deliveryApptConfirmed === true,

    customer: raw.customer || 'Unknown Customer',
    poTagNumber: raw.tag || '',
    soNumber: raw.salesOrderNumber || '',
    amount: soRevenue,     
    itemCount: soItemCount,
    pickupOrShip: raw.pickupOrShip || raw.shipTo || 'PICKUP',
    
    paymentTerms: termsOfPayment,
    isPrepaymentRequired,

    prepaymentReceived: raw.prepaymentReceived === true || raw.prepaymentReceived === 'true', 
    prepaymentReceivedDate: raw.prepaymentReceivedDate || '',

    received: false, 
    receivedDate: '',
    paymentStatus1: '', 
    paymentStatus2: '',

    plannedProduction: raw.plannedProduction === true || raw.plannedProduction === 'true', 
    plannedProductionDate: raw.plannedProductionDate ? formatDateStr(raw.plannedProductionDate) : '',
    plannedMaterials: raw.plannedMaterials === true || raw.plannedMaterials === 'true',
    plannedMaterialsDate: raw.plannedMaterialsDate ? formatDateStr(raw.plannedMaterialsDate) : '',

    assignedTo: (raw.assignedTo && raw.assignedTo !== 'Unassigned') ? raw.assignedTo : '',
    status: raw.status || 'Pending',
    units: soUnits,
    packingLists: raw.packingLists || '',
    packedUnits: raw.packedUnits || {},
    shippingStatus: (/^n\s*\/\s*a$/i.test(raw?.shippingStatus?.trim()))
      ? ''
      : (raw?.shippingStatus || ''),
    shippedStatus: raw.invoiceDate ? formatDateStr(raw.invoiceDate) : '',
    invoiceNumber: raw.invoiceNumber || '',
    needsCustomDocs: raw.needsCustomDocs === true || raw.needsCustomDocs === 'true',
    customDocsDate: raw.customDocsDate ? formatDateStr(raw.customDocsDate) : '',
    customDocsNote: raw.customDocsNote || '',
    trackingLink: raw.trackingLink || '',
    shippingDateManual: raw.shippingDateManual ? formatDateStr(raw.shippingDateManual) : '',
    departureDate: raw.departureDate ? formatDateStr(raw.departureDate) : '',
    eta: raw.eta ? formatDateStr(raw.eta) : '',
    accountingChecked: raw.accountingChecked === true || raw.accountingChecked === 'true', 
    accountingDate: raw.accountingDate ? formatDateStr(raw.accountingDate) : '',
    completed: isCompleted,
    completedAt: isCompleted ? formatDateStr(raw.invoiceDate || raw.updatedAt || raw.completedAt) : '',
    isReturned, 
    manualGroupId: null,
    invoiceSubmissionChecked: !!(raw.invoiceNumber),
    invoiceSubmissionDate: raw.invoiceNumber ? formatDateStr(raw.invoiceDate) : '',
    comments: raw.comments || '',
    readBy: Array.isArray(raw.readBy) 
      ? raw.readBy.map(String) 
      : (raw.readBy ? [String(raw.readBy)] : []),    
    _raw: raw
  };
};

const getStatusBadges = (so) => {
  const badges = [];
  
  if (so.isReturned) return [{ label: 'Returned', class: 'bg-red-100 text-red-700 border-red-200' }];
  if (so.completed) return [{ label: 'Complete', class: 'bg-emerald-100 text-emerald-700 border-emerald-200' }];

  if (so.invoiceNumber) badges.push({ label: 'Invoiced', class: 'bg-blue-100 text-blue-700 border-blue-200' });
  if (so.shippingStatus || so.departureDate) badges.push({ label: so.shippingStatus, class: 'bg-purple-100 text-purple-700 border-purple-200' });
  if (so.packedStatus) badges.push({ label: so.packedStatus, class: 'bg-cyan-100 text-cyan-700 border-cyan-200' });
  
  if (so.isPrepaymentRequired && !so.prepaymentReceived) {
    badges.push({ label: 'Awaiting Prepay', class: 'bg-red-100 text-red-700 border-red-200' });
  }

  if (so.plannedProduction) badges.push({ label: 'In Production', class: 'bg-amber-100 text-amber-700 border-amber-200' });
  if (so.plannedMaterials) badges.push({ label: 'Materials Ready', class: 'bg-indigo-100 text-indigo-700 border-indigo-200' });
  if (so.assignedTo && so.assignedTo !== 'Unassigned') badges.push({ label: 'Assigned', class: 'bg-indigo-100 text-indigo-700 border-indigo-200' });

  if (badges.length === 0) badges.push({ label: 'New', class: 'bg-gray-100 text-gray-600 border-gray-200' });
  
  return badges;
};

/**
 * Customer-specific auto-family rules.
 * Returns { key, familyName, poCore } or null if order should stay standalone.
 */
/**
 * Helper: Extracts uppercase initial characters from any customer name dynamically.
 * E.g.:
 *  - "Gordon Food Service Canada Ltd" -> "GFS"
 *  - "The TJX Companies, Inc."        -> "TJX"
 *  - "HomeGoods"                      -> "HG"
 *  - "12298554 Canada Inc. (The Spice Age)" -> "SA"
 */
const getCustomerInitials = (customerName) => {
  if (!customerName || typeof customerName !== 'string') return '';
  const trimmed = customerName.trim();
  if (!trimmed) return '';

  const lower = trimmed.toLowerCase();

  // Known retail / enterprise acronyms
  if (lower.includes('gordon food') || lower.includes('gfs')) return 'GFS';
  return "";

  /*
  if (lower.includes('homegoods') || lower.includes('home goods') || lower.includes('home-goods')) return 'HG';
  if (lower.includes('marmaxx') || lower.includes('marshalls')) return 'MMX';
  if (lower.includes('The TJX Companies')) return 'TJX';
  if (lower.includes('walmart')) return 'WMT';
  if (lower.includes('amazon')) return 'AMZ';
  if (lower.includes('costco')) return 'COST';
  if (lower.includes('sobeys')) return 'SOB';

  // Extract from parentheses if present (e.g. "12298554 Canada Inc. (The Spice Age)" -> "The Spice Age")
  const parenMatch = trimmed.match(/\(([^)]+)\)/);
  const textToParse = parenMatch ? parenMatch[1] : trimmed;

  // Words that should not contribute to the initials
  const stopWords = new Set([
    'THE', 'INC', 'INC.', 'LTD', 'LTD.', 'CORP', 'CORP.', 'LLC', 'CO', 'CO.',
    'COMPANY', 'COMPANIES', 'CANADA', 'USA', 'GROUP', 'ENTERPRISES'
  ]);

  const words = textToParse
    .replace(/['’]/g, '')
    .split(/[\s\-_/]+/)
    .filter(Boolean);

  const significant = words.filter(w => !stopWords.has(w.toUpperCase()));
  const targetWords = significant.length > 0 ? significant : words;

  // Single word: use up to 3-4 chars
  if (targetWords.length === 1) {
    const clean = targetWords[0].replace(/[^A-Za-z0-9]/g, '');
    return clean.length <= 4 ? clean.toUpperCase() : clean.slice(0, 3).toUpperCase();
  }

  // Multiple words: take the first letter of each significant word (up to 4 chars)
  const initials = targetWords
    .map(w => {
      const clean = w.replace(/[^A-Za-z0-9]/g, '');
      return clean ? clean[0].toUpperCase() : '';
    })
    .join('')
    .slice(0, 4);

  return initials || trimmed.slice(0, 3).toUpperCase();
  */
};

/**
 * Customer-specific auto-family rules with dynamic customer initials.
 * Returns { key, familyName, poCore } or null if order should stay standalone.
 */
const getAutoGroupMeta = (so) => {
  const rawCustomer = (so.customer || '').trim();
  const customer = rawCustomer.toLowerCase();
  const tag = (so.poTagNumber || so.tag || '').trim();
  const soDate = (so.soDate || '').trim();
  const deadline = (so.deadline || '').trim();

  // Dynamically resolve customer initials (e.g. GFS, TJX, SA, BDO)
  const initials = getCustomerInitials(rawCustomer);
  // console.log(initials);
  const prefix = initials ? `${initials} ` : '';

  // ─── GFS / Date-based Grouping: [Initials] [SO Date] ────────────
  if (
    customer.includes('gordon food') ||
    customer.includes('gfs') ||
    customer === 'gordon food service canada ltd'
  ) {
    if (!soDate) return null;
    return {
      key: `auto-gfs::${soDate}`,
      familyName: `${prefix}${soDate}`.trim(),
      poCore: null
    };
  }

  // ─── TJX HomeGoods: [Initials] [6-digit core] ─────────────────────
  if (
    customer.includes('homegoods') ||
    customer.includes('home goods') ||
    customer.includes('home-goods')
  ) {
    let match = tag.match(/^[A-Za-z0-9]*-?(\d{6})$/) || tag.match(/(\d{6})/);
    if (!match) return null;
    const core = match[1];
    const alreadyPrefixed = initials && core.toUpperCase().startsWith(initials);
    return {
      key: `auto-hg::${core}`,
      familyName: alreadyPrefixed ? core : `${prefix}${core}`.trim(),
      poCore: core
    };
  }

  // ─── TJX Marmaxx: Same SO Date, clustered by min-max PO ───────────
  if (
    customer.includes('marmaxx') ||
    customer.includes('marshall') ||
    (customer.includes('tjx') && customer.includes('mar'))
  ) {
    const match = tag.match(/(\d{6})/);
    if (!match) return null;
    const dayKey = soDate || deadline;
    if (!dayKey) return null;
    return {
      key: `auto-mm::${dayKey}`,
      familyName: null, // computed from min-max of poCores in bucket
      poCore: match[1],
      customerInitials: initials
    };
  }

  // ─── DEFAULT: Classic Customer + Full Tag ────────────────────────
  if (!tag) return null;
  // Prevent duplicate prefixes if the tag already starts with the initials (e.g. "ASA1077-1")
  const alreadyPrefixed = initials && tag.toUpperCase().startsWith(initials);
  return {
    key: `auto-default::${so.customer}::${tag}`,
    familyName: alreadyPrefixed ? tag : `${prefix}${tag}`.trim(),
    poCore: null
  };
};

const buildGroupVirtualOrder = (group) => {
  const first = group.orders[0];
  const totalAmount = group.orders.reduce((s, o) => s + (o.amount || 0), 0);
  const totalUnits = group.orders.reduce((s, o) => s + (o.units || 0), 0);
  const totalItems = group.orders.reduce((s, o) => s + (o.itemCount || 0), 0);

  const childTags = group.orders
    .map(o => ({
      id: o.id,
      soNumber: o.soNumber,
      tag: o.poTagNumber || o.tag || ''
    }))
    .filter(item => item.tag);

  // Prefer explicit parentPO / poTagNumber from group builder
  const familyLabel =
    group.parentPO ||
    group.poTagNumber ||
    group.name ||
    first.poTagNumber ||
    'GROUP';

  return {
    ...first,
    id: `group-${group.key}`,
    isGroupRow: true,
    groupKey: group.key,
    groupType: group.type,
    groupName: familyLabel,
    childOrderIds: group.orders.map(o => o.id),
    childCount: group.orders.length,
    childTags,
    soNumber: familyLabel,
    poTagNumber: familyLabel,
    amount: totalAmount,
    units: totalUnits,
    itemCount: totalItems,
    tag: familyLabel
  };
};

const TABLE_COLUMNS = [
  { key: 'A', label: 'SO Date', minW: '100px', align: 'left', sortKey: 'soDate' },
  { key: 'E', label: 'SO / Tag', minW: '130px', align: 'left' },  
  { key: 'D', label: 'Customer', minW: '180px', align: 'center' },  
  { key: 'B', label: 'Lead Time', minW: '80px', align: 'center' },
  { key: 'C', label: 'Deadline', minW: '130px', align: 'center', sortKey: 'deadline' },
  { key: 'F', label: 'AMOUNT', minW: '100px', align: 'right' },
  { key: 'G', label: 'PICKUP OR SHIP', minW: '140px', align: 'center' },
  { key: 'H', label: ( <> PAYMENT TERMS <br /> (PREPAID ONLY) </> ), minW: '120px', align: 'center' },
  { key: 'I', label: 'PLANNED (AS REQUIRED)', minW: '250px', align: 'center'},
  { key: 'J', label: 'ASSIGNED TO', minW: '120px', align: 'center' },
  { key: 'K', label: 'PACKED STATUS', minW: '130px', align: 'center' },
  { key: 'L', label: 'SHIPPED STATUS', minW: '130px', align: 'center' },
  { key: 'M', label: 'INVOICED STATUS', minW: '130px', align: 'center' },
  { key: 'N', label: ( <> CUSTOM DOCS <br /> SUBMISSION DATE </> ), minW: '170px', align: 'left' },
  { key: 'O', label: ( <> DELIVERY APPOINTMENT </> ), minW: '190px', align: 'center' },   
  { key: 'P', label: ( <> TRACKING LINK <br /> OR PRO/BOL # </> ), minW: '150px', align: 'center' },   
  { key: 'Q', label: 'DEPARTURE DATE', minW: '130px' },
  { key: 'R', label: 'ETA', minW: '110px' },
  { key: 'S', label: 'INVOICE SUBMISSION', minW: '150px' },
  { key: 'T', label: 'Actions', minW: '120px', align: 'center' },
  { key: 'U', label: 'Comments', minW: '200px', align: 'center' }
];

const TOTAL_TABLE_COL_COUNT = TABLE_COLUMNS.length + 1;

const ACCESS = {
  NO_ACCESS: "no_access",
  READ: "read",
  WRITE: "write",
};

const normalizeAccess = (value) => {
  if (value === ACCESS.READ || value === ACCESS.WRITE || value === ACCESS.NO_ACCESS) {
    return value;
  }
  if (value === true) return ACCESS.WRITE;
  return ACCESS.NO_ACCESS;
};

// ─────────────────────────────────────────────
// ROLE-BASED NOTIFICATION HELPERS
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// 1. STATIC CUSTOMERS LIST FOR CURRENT USER (TEMPORARY / TEST)
// ─────────────────────────────────────────────────────────────
// The current user will ONLY see notifications from these customers.
// Adjust or uncomment the ones you want to test right now:
const BYPASS_CUSTOMER_FILTER_FOR_TESTING = true; 
const STATIC_CURRENT_USER_ALLOWED_CUSTOMERS = [
  'Gordon Food Service Canada Ltd',
  'Sobeys Administration (2431786700000)',
  'HomeGoods',
  'Sobeys Administration',
  '12298554 Canada Inc'
];

/**
 * Retrieves the logged-in user profile and locks customer access
 * strictly to the static list above.
 */
const getUserAccessContext = () => {
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem('user')) || {};
  } catch (e) {
    console.error('Error parsing user from localStorage:', e);
  }

  const userId = user.id || user._id || user.email || user.username || 'current_logged_in_user';

  // Normalize static customers to lower case for reliable comparison
  const allowedCustomers = STATIC_CURRENT_USER_ALLOWED_CUSTOMERS
    .map(c => String(c).trim().toLowerCase())
    .filter(Boolean);

  return {
    userId,
    // Set to false so the user is strictly restricted to the static customers above
    isUnrestrictedAdmin: true,
    allowedCustomers
  };
};

/**
 * Strict validator: returns TRUE only if the SO belongs to one of
 * the allowed static customers.
 */
const isOrderAllowedForUser = (order, userContext) => {

  // ─── BYPASS FOR TESTING: Allows all SOs through immediately ───
  if (BYPASS_CUSTOMER_FILTER_FOR_TESTING) return true;
    
  if (!order || !order.customer) return false;
  if (!userContext.allowedCustomers || userContext.allowedCustomers.length === 0) return false;

  const orderCustomer = String(order.customer).trim().toLowerCase();
  const rawCustomerId = String(order._raw?.customerId || order.customerId || '').trim().toLowerCase();

  // Match by substring (e.g., "gordon food" matches "Gordon Food Service Canada Ltd")
  return userContext.allowedCustomers.some(allowed => 
    orderCustomer.includes(allowed) || 
    allowed.includes(orderCustomer) ||
    (rawCustomerId && rawCustomerId.includes(allowed))
  );
};

const getReadStorageKey = (userId) => `planner_read_notifications_${userId}`;

// ─────────────────────────────────────────────
// DATABASE PERSISTENCE FOR READ NOTIFICATIONS
// ─────────────────────────────────────────────

/**
 * Fetch the list of SO IDs marked as read by this user from the Database.
 */
const fetchUserReadNotificationsFromDb = async (userId) => {
  if (!userId) return new Set();
  try {
    // 1. Check if exposed on your api helper
    if (api?.OperationsPlanner?.getReadNotifications) {
      const res = await api.OperationsPlanner.getReadNotifications({ userId });
      if (res?.success && Array.isArray(res.readOrderIds)) {
        return new Set(res.readOrderIds.map(String));
      }
    }

    // 2. Direct REST endpoint fallback
    const res = await fetch(`${API_BASE_URL}/weeklyplanner/notifications/read?userId=${encodeURIComponent(userId)}`);
    if (res.ok) {
      const data = await res.json();
      const ids = Array.isArray(data) ? data : (data.readOrderIds || []);
      return new Set(ids.map(String));
    }
  } catch (err) {
    console.warn('Could not fetch read notifications from database, falling back to cache:', err);
  }

  // Fallback to local storage if offline / network error
  try {
    const cached = localStorage.getItem(`planner_read_notifications_${userId}`);
    return cached ? new Set(JSON.parse(cached)) : new Set();
  } catch {
    return new Set();
  }
};

/**
 * Persist marked-as-read order IDs to the Database.
 */
const persistReadNotificationsToDb = async (userId, orderIds) => {
  if (!userId || !orderIds || orderIds.length === 0) return;

  // Cache locally for offline resilience
  try {
    const existing = localStorage.getItem(`planner_read_notifications_${userId}`);
    const set = existing ? new Set(JSON.parse(existing)) : new Set();
    orderIds.forEach(id => set.add(id));
    localStorage.setItem(`planner_read_notifications_${userId}`, JSON.stringify(Array.from(set)));
  } catch (e) {
    console.error(e);
  }

  // Save to DB
  try {
    if (api?.OperationsPlanner?.markNotificationsRead) {
      await api.OperationsPlanner.markNotificationsRead({ userId, orderIds });
      return;
    }

    // Direct REST endpoint fallback
    await fetch(`${API_BASE_URL}/weeklyplanner/notifications/mark-read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, orderIds })
    });
  } catch (err) {
    console.error('Failed to persist read notifications to DB:', err);
  }
};

// ─────────────────────────────────────────────
// UNIFIED ORDER ROW
// ─────────────────────────────────────────────
function OrderView({
  so,
  employees,
  selectedOrderIds,
  onToggleSelect,
  onOpenPrepaymentModal,
  onComplete,
  isChildOfGroup,
  isGroupRow,
  isExpanded,
  onToggleExpand,
  onUngroup,
  onEditGroupTag,
  group,
  onPersistUpdate,
  isSaving,
  onDeleteSO,
  permissions,
}) {
  const groupType = group?.type || 'auto';
  const setDefault = false;

  const canWriteAccess = (value) => normalizeAccess(value) === ACCESS.WRITE;

  // ─────────────────────────────────────────────
  // PARSE PERMISSIONS ONCE (pure initializer)
  // ─────────────────────────────────────────────
  const [permissionsData] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('permissions')) || {};
    } catch (e) {
      console.error('Invalid permissions JSON in localStorage:', e);
      return {};
    }
  });

  // ─────────────────────────────────────────────
  // PLANNER FIELD-LEVEL PERMISSIONS
  // ─────────────────────────────────────────────
  const canEditDeadline = canWriteAccess(permissionsData?.allowEditDeadline) || setDefault;
  const canEditCancelDate = canWriteAccess(permissionsData?.allowEditCancelDate) || setDefault;
  const canEditPickupOrShip = canWriteAccess(permissionsData?.allowEditPickupOrShip) || setDefault;
  const canEditAssignedTo = canWriteAccess(permissionsData?.allowEditAssignedTo) || setDefault;

  // Planned section
  const canEditPaymentTerms = canWriteAccess(permissionsData?.allowEditPaymentTerms) || setDefault;
  const canEditPlannedProduction = canWriteAccess(permissionsData?.allowEditPlannedProduction) || setDefault;
  const canEditPlannedProductionDate = canWriteAccess(permissionsData?.allowEditPlannedProductionDate) || setDefault;
  const canEditPlannedMaterials = canWriteAccess(permissionsData?.allowEditPlannedMaterials) || setDefault;
  const canEditPlannedMaterialsDate = canWriteAccess(permissionsData?.allowEditPlannedMaterialsDate) || setDefault;

  // Status fields
  const canEditPackingLists = canWriteAccess(permissionsData?.allowEditPackingLists) || setDefault;
  const canEditShippingStatus = canWriteAccess(permissionsData?.allowEditShippingStatus) || setDefault;
  const canEditInvoiceNumber = canWriteAccess(permissionsData?.allowEditInvoiceNumber) || setDefault;

  // Custom documents
  const canEditCustomDocs = canWriteAccess(permissionsData?.allowEditCustomDocs) || setDefault;
  const canEditCustomDocsSubmissionDate = canWriteAccess(permissionsData?.allowEditCustomDocsSubmissionDate) || setDefault;

  // Delivery appointment
  const canEditDeliveryAppointment = canWriteAccess(permissionsData?.allowEditDeliveryAppointment) || setDefault;
  const canEditDeliveryAppointmentDate = canWriteAccess(permissionsData?.allowEditDeliveryAppointmentDate) || setDefault;
  const canEditDeliveryAppointmentTime = canWriteAccess(permissionsData?.allowEditDeliveryAppointmentTime) || setDefault;
  const canEditDeliveryConfirmationNumber = canWriteAccess(permissionsData?.allowEditDeliveryConfirmationNumber) || setDefault;
  const canEditDeliveryAppointmentConfirmed = canWriteAccess(permissionsData?.allowEditDeliveryAppointmentConfirmed) || setDefault;

  // Tracking / Departure / ETA
  const canEditTrackingLink = canWriteAccess(permissionsData?.allowEditTrackingLink) || setDefault;
  const canEditDepartureDate = canWriteAccess(permissionsData?.allowEditDepartureDate) || setDefault;
  const canEditEta = canWriteAccess(permissionsData?.allowEditEta) || setDefault;

  // Invoice submission
  const canEditInvoiceSubmission = canWriteAccess(permissionsData?.allowEditInvoiceSubmission) || setDefault;
  const canEditInvoiceSubmissionDate = canWriteAccess(permissionsData?.allowEditInvoiceSubmissionDate) || setDefault;

  // Actions
  const canMarkComplete = canWriteAccess(permissionsData?.allowMarkComplete) || setDefault;
  const canDelete = canWriteAccess(permissionsData?.allowDelete) || setDefault;

  const isSelected = isGroupRow
    ? Boolean(so.childOrderIds?.length) && so.childOrderIds.every((id) => selectedOrderIds.includes(id))
    : selectedOrderIds.includes(so.id);

  const isBlockedByPrepayment = so.isPrepaymentRequired && !so.prepaymentReceived;
  const shippingStatus = so.shippingStatus?.replace(/\s+/g, '').toLowerCase().trim() || '';

  // --- Local States for Delivery Appointment ---
  const [localNeedsAppt, setLocalNeedsAppt] = useState(so.needsDeliveryAppt || false);
  const [localApptDate, setLocalApptDate] = useState(so.deliveryApptDate || '');
  const [localApptTime, setLocalApptTime] = useState(so.deliveryApptTime || '');
  const [localConfNo, setLocalConfNo] = useState(so.deliveryConfirmationNo || '');
  const [localApptConfirmed, setLocalApptConfirmed] = useState(so.deliveryApptConfirmed || false);
  const commentTimers = useRef({});

  // --- Local State for Lead Time Hover/Click Popover ---
  const [showLeadTimePopup, setShowLeadTimePopup] = useState(false);

  useEffect(() => {
    setLocalNeedsAppt(so.needsDeliveryAppt || false);
    setLocalApptDate(so.deliveryApptDate || '');
    setLocalApptTime(so.deliveryApptTime || '');
    setLocalConfNo(so.deliveryConfirmationNo || '');
    setLocalApptConfirmed(so.deliveryApptConfirmed || false);
  }, [so.needsDeliveryAppt, so.deliveryApptDate, so.deliveryApptTime, so.deliveryConfirmationNo, so.deliveryApptConfirmed]);

  const handleDeliveryApptSave = () => {
    const payload = {
      needsDeliveryAppt: canEditDeliveryAppointment ? localNeedsAppt : so.needsDeliveryAppt || false,
      deliveryApptDate: canEditDeliveryAppointmentDate ? (localNeedsAppt ? localApptDate : '') : so.deliveryApptDate || '',
      deliveryApptTime: canEditDeliveryAppointmentTime ? (localNeedsAppt ? localApptTime : '') : so.deliveryApptTime || '',
      deliveryConfirmationNo: canEditDeliveryConfirmationNumber ? (localNeedsAppt ? localConfNo : '') : so.deliveryConfirmationNo || '',
      deliveryApptConfirmed: canEditDeliveryAppointmentConfirmed ? (localNeedsAppt ? localApptConfirmed : false) : so.deliveryApptConfirmed || false,
    };
    onPersistUpdate(so.id, 'deliveryApptPayload', payload, targetIds);
  };

  const hasApptChanges =
    localNeedsAppt !== (so.needsDeliveryAppt || false) ||
    localApptDate !== (so.deliveryApptDate || '') ||
    localApptTime !== (so.deliveryApptTime || '') ||
    localConfNo !== (so.deliveryConfirmationNo || '') ||
    localApptConfirmed !== (so.deliveryApptConfirmed || false);

  const canSaveApptChanges =
    (localNeedsAppt !== (so.needsDeliveryAppt || false) && canEditDeliveryAppointment) ||
    (localApptDate !== (so.deliveryApptDate || '') && canEditDeliveryAppointmentDate) ||
    (localApptTime !== (so.deliveryApptTime || '') && canEditDeliveryAppointmentTime) ||
    (localConfNo !== (so.deliveryConfirmationNo || '') && canEditDeliveryConfirmationNumber) ||
    (localApptConfirmed !== (so.deliveryApptConfirmed || false) && canEditDeliveryAppointmentConfirmed);

  const handleGroupToggleSelect = () => {
    if (isGroupRow) {
      const allSelected = so.childOrderIds.every((id) => selectedOrderIds.includes(id));
      onToggleSelect(so.childOrderIds, !allSelected);
    } else {
      onToggleSelect(so.id);
    }
  };

  const targetIds = isGroupRow ? so.childOrderIds : null;

  // --- STICKY ROW BACKGROUND LOGIC ---
  let rowBg = 'bg-white';
  if (isGroupRow) rowBg = 'bg-gradient-to-r from-indigo-50/60 to-blue-50/40';
  else if (isChildOfGroup) rowBg = 'bg-slate-50/70';
  if (isSelected) rowBg += ' ring-1 ring-indigo-300';

  let stickyBg = 'bg-white';
  if (isSelected) stickyBg = 'bg-[#eef2ff]';
  else if (isGroupRow) stickyBg = 'bg-[#f5f7ff]';
  else if (isChildOfGroup) stickyBg = 'bg-[#f8fafc]';

  const groupBorder = isGroupRow
    ? 'border-l-4 border-indigo-500'
    : isChildOfGroup
    ? 'border-l-4 border-indigo-300'
    : '';

  // --- Local States for Custom Documents Section ---
  const [localNeedsDocs, setLocalNeedsDocs] = useState(so.needsCustomDocs || false);
  const [localDocsNote] = useState(so.customDocsNote || '');
  const [localDocsDate, setLocalDocsDate] = useState(so.customDocsDate || '');

  // --- Local States for Invoice Submission Section ---
  const [localAccountingChecked, setLocalAccountingChecked] = useState(so.accountingChecked || false);
  const [localAccountingDate, setLocalAccountingDate] = useState(so.accountingDate || '');

  // --- Local States for PLANNED Section ---
  const [localProd, setLocalProd] = useState(!!so.plannedProduction);
  const [localProdDate, setLocalProdDate] = useState(so.plannedProductionDate || '');
  const [localMat, setLocalMat] = useState(!!so.plannedMaterials);
  const [localMatDate, setLocalMatDate] = useState(so.plannedMaterialsDate || '');

  // --- NEW: >$5,000 High Value Confirmation Modal State ---
  const [showHighValueModal, setShowHighValueModal] = useState(false);
  const [modalBusinessDays, setModalBusinessDays] = useState(5);

  useEffect(() => {
    setLocalNeedsDocs(so.needsCustomDocs || false);
    setLocalDocsDate(so.customDocsDate || '');
  }, [so.needsCustomDocs, so.customDocsNote, so.customDocsDate]);

  useEffect(() => {
    setLocalAccountingChecked(so.accountingChecked || false);
    setLocalAccountingDate(so.accountingDate || '');
  }, [so.accountingChecked, so.accountingDate]);

  useEffect(() => {
    setLocalProd(!!so.plannedProduction);
    setLocalProdDate(so.plannedProductionDate || '');
    setLocalMat(!!so.plannedMaterials);
    setLocalMatDate(so.plannedMaterialsDate || '');
  }, [so.plannedProduction, so.plannedProductionDate, so.plannedMaterials, so.plannedMaterialsDate]);

  const handleCustomDocsSave = () => {
    const payload = {
      needsCustomDocs: canEditCustomDocs ? localNeedsDocs : so.needsCustomDocs || false,
      customDocsNote: canEditCustomDocs ? (localNeedsDocs ? localDocsNote.trim() : '') : so.customDocsNote || '',
      customDocsDate: canEditCustomDocsSubmissionDate ? (localNeedsDocs ? localDocsDate : '') : so.customDocsDate || '',
    };
    onPersistUpdate(so.id, 'customDocsPayload', payload, targetIds);
  };

  const hasDocsChanges =
    localNeedsDocs !== (so.needsCustomDocs || false) ||
    localDocsDate !== (so.customDocsDate || '');

  const canSubmitDocs = localNeedsDocs && localDocsDate !== '';
  const canSaveCustomDocsChanges =
    (localNeedsDocs !== (so.needsCustomDocs || false) && canEditCustomDocs) ||
    (localDocsDate !== (so.customDocsDate || '') && canEditCustomDocsSubmissionDate);

  const handleInvoiceSubmissionSave = () => {
    const payload = {
      accountingChecked: canEditInvoiceSubmission ? localAccountingChecked : so.accountingChecked || false,
      accountingDate: canEditInvoiceSubmissionDate
        ? localAccountingChecked
          ? localAccountingDate
          : ''
        : so.accountingDate || '',
    };
    onPersistUpdate(so.id, 'accountingPayload', payload, targetIds);
  };

  const hasInvoiceSubmissionChanges =
    localAccountingChecked !== (so.accountingChecked || false) ||
    localAccountingDate !== (so.accountingDate || '');

  const canSaveInvoiceSubmissionChanges =
    (localAccountingChecked !== (so.accountingChecked || false) && canEditInvoiceSubmission) ||
    (localAccountingDate !== (so.accountingDate || '') && canEditInvoiceSubmissionDate);

  const handleProdSave = () => {
    if (canEditPlannedProduction) {
      onPersistUpdate(so.id, 'plannedProduction', localProd, targetIds);
    }
    if (canEditPlannedProductionDate) {
      onPersistUpdate(so.id, 'plannedProductionDate', localProd ? localProdDate : '', targetIds);
    }
  };

  const handleMatSave = () => {
    if (canEditPlannedMaterials) {
      onPersistUpdate(so.id, 'plannedMaterials', localMat, targetIds);
    }
    if (canEditPlannedMaterialsDate) {
      onPersistUpdate(so.id, 'plannedMaterialsDate', localMat ? localMatDate : '', targetIds);
    }
  };

  const hasProdChanges =
    localProd !== !!so.plannedProduction ||
    localProdDate !== (so.plannedProductionDate || '');

  const hasMatChanges =
    localMat !== !!so.plannedMaterials ||
    localMatDate !== (so.plannedMaterialsDate || '');

  const canSaveProdChanges =
    (localProd !== !!so.plannedProduction && canEditPlannedProduction) ||
    (localProdDate !== (so.plannedProductionDate || '') && canEditPlannedProductionDate);

  const canSaveMatChanges =
    (localMat !== !!so.plannedMaterials && canEditPlannedMaterials) ||
    (localMatDate !== (so.plannedMaterialsDate || '') && canEditPlannedMaterialsDate);

  const [pendingUpdates, setPendingUpdates] = useState({});
  const getPendingValue = (soId, field, originalValue = '') => {
    if (pendingUpdates[soId]?.[field] !== undefined) {
      return pendingUpdates[soId][field];
    }
    return originalValue || '';
  };

  const setLocalFieldValue = (soId, field, value) => {
    setPendingUpdates((prev) => ({
      ...prev,
      [soId]: {
        ...(prev[soId] || {}),
        [field]: value,
      },
    }));
  };

  const handleLocalFieldChange = setLocalFieldValue;
  const saveField = async (soId, field, value, targetIds) => {
    await onPersistUpdate(soId, field, value, targetIds);

    setPendingUpdates((prev) => {
      const updated = { ...prev };
      if (updated[soId]) {
        const soUpdates = { ...updated[soId] };
        delete soUpdates[field];
        if (Object.keys(soUpdates).length === 0) {
          delete updated[soId];
        } else {
          updated[soId] = soUpdates;
        }
      }
      return updated;
    });
  };

  const handleDeleteClick = () => {
    if (!canDelete) return;
    if (window.confirm(`Are you sure you want to delete SO ${so.soNumber}? This action cannot be undone.`)) {
      onDeleteSO(so.id, targetIds);
    }
  };

  const cancelField = (soId, field) => {
    setPendingUpdates((prev) => {
      const updated = { ...prev };
      const orderUpdates = { ...(updated[soId] || {}) };
      delete orderUpdates[field];
      if (Object.keys(orderUpdates).length === 0) {
        delete updated[soId];
      } else {
        updated[soId] = orderUpdates;
      }
      return updated;
    });
  };

  const formatNum = (val) => {
    const n = Number(val) || 0;
    return Number(n.toFixed(2)).toString();
  };

  // ─────────────────────────────────────────────────────────────
  // NEW: BUSINESS DAYS & WEEKEND ADJUSTMENT RECALCULATION
  // ─────────────────────────────────────────────────────────────
  const calculateRecalculatedDeadline = (startDateStr, numBusinessDays) => {
    if (!startDateStr) return '';
    const d = new Date(startDateStr);
    let added = 0;
    const count = parseInt(numBusinessDays, 10) || 5;

    while (added < count) {
      d.setUTCDate(d.getUTCDate() + 1);
      const day = d.getUTCDay();
      if (day !== 0 && day !== 6) added++;
    }

    // Friday rule: Saturday or Sunday -> Move backward to Friday
    const day = d.getUTCDay();
    if (day === 6) d.setUTCDate(d.getUTCDate() - 1);
    else if (day === 0) d.setUTCDate(d.getUTCDate() - 2);

    return d.toISOString().split('T')[0];
  };

  const liveRecalculatedDeadline = calculateRecalculatedDeadline(
    so.soDate || new Date().toISOString().split('T')[0],
    modalBusinessDays
  );

  const handleConfirmHighValueDeadline = async () => {
    const newDeadline = liveRecalculatedDeadline;
    await onPersistUpdate(
      so.id,
      'confirmHighValueDeadline',
      {
        deadline: newDeadline,
        leadTime: modalBusinessDays,
        requiresDeadlineConfirmation: false,
      },
      targetIds
    );
    setShowHighValueModal(false);
  };

  const hasTransitDetails = Boolean(so.region || so.transitDays != null || so.transitLevel);

  const showDeadlineCalculationPriorityLabels = false;

  return (
    <>
      <tr className={`group border-b border-gray-100 hover:bg-slate-50/80 transition-colors ${rowBg}`}>
        {/* 🧊 FROZEN: Checkbox */}
        <td
          className={`p-1.5 sm:p-2 text-center align-top pt-3 sticky z-10 max-md:!relative max-md:!left-auto max-md:!z-0 group-hover:bg-slate-50 transition-colors ${stickyBg} ${groupBorder}`}
          style={{ left: 0 }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleGroupToggleSelect}
            aria-label={`Select SO ${so.soNumber}`}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer w-4 h-4"
          />
        </td>

        {/* 🧊 FROZEN: SO Date */}
        <td
          className={`p-1.5 sm:p-2 align-top sticky z-10 max-md:!relative max-md:!left-auto max-md:!z-0 group-hover:bg-slate-50 transition-colors ${stickyBg}`}
          style={{ left: 'var(--w-check)' }}
        >
          <div className="text-[10px] sm:text-[11px] text-gray-700 font-medium whitespace-nowrap">
            {formatDateDisplay(so.soDate)}
          </div>
          {isGroupRow && (
            <button
              onClick={onToggleExpand}
              className="mt-1 flex items-center gap-1 text-[9px] text-indigo-600 hover:text-indigo-800 font-bold cursor-pointer"
            >
              {isExpanded ? <EyeOff size={10} /> : <Eye size={10} />}
              <span className="hidden sm:inline">
                {isExpanded ? 'Hide' : 'Show'} {so.childCount} SOs
              </span>
              <span className="sm:hidden">{so.childCount}</span>
            </button>
          )}
        </td>

        {/* 🧊 FROZEN: SO Number / Tag */}
        <td
          className={`p-1.5 sm:p-2 align-top sticky z-10 max-md:!relative max-md:!left-auto max-md:!z-0 group-hover:bg-slate-50 transition-colors ${stickyBg}`}
          style={{ left: 'calc(var(--w-check) + var(--w-col0))' }}
        >
          <div className="space-y-0.5">
            <button className="font-mono font-bold text-indigo-700 text-[10px] sm:text-xs hover:underline cursor-pointer break-all text-left">
              {so.soNumber}
            </button>
            {so.poTagNumber && (
              <div className="text-[9px] sm:text-[10px] text-gray-500 font-mono truncate">
                Tag: {so.poTagNumber}
              </div>
            )}
          </div>
        </td>

        {/* 🧊 ALWAYS STICKY: Customer */}
        <td
          className={`p-1.5 sm:p-2 align-top sticky z-20 max-md:!left-0 group-hover:bg-slate-50 transition-colors shadow-[inset_-1px_0_0_#cbd5e1] ${stickyBg}`}
          style={{ left: 'calc(var(--w-check) + var(--w-col0) + var(--w-col1))' }}
        >
          <div className="font-semibold text-gray-900 text-[11px] sm:text-xs leading-snug max-w-[160px] sm:max-w-[160px] md:max-w-[200px]">
            {so.customer}
          </div>

          <div className="space-y-0.5">
            {!isGroupRow && (
              <div className="text-[9px] sm:text-[10px] text-gray-500 font-mono font-semibold truncate mt-1">
                Tag:{' '}
                <span className="text-gray-800 bg-gray-100 px-1 py-0.5 rounded border border-gray-200">
                  {so.poTagNumber || '—'}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {groupType === 'manual' && (
              <button
                onClick={() => onUngroup(group.key)}
                aria-label="Ungroup orders"
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                title="Ungroup"
              >
                <Unlink size={12} />
              </button>
            )}
          </div>
        </td>

        {/* --- SCROLLABLE COLUMNS --- */}

        {/* B: Lead Time (HOVER/CLICK POPUP ONLY) */}
        <td className="p-1.5 sm:p-2 align-top text-center">
          <div
            className="relative inline-block"
            onMouseEnter={() => setShowLeadTimePopup(true)}
            onMouseLeave={() => setShowLeadTimePopup(false)}
          >
            <button
              type="button"
              onClick={() => setShowLeadTimePopup((prev) => !prev)}
              aria-label="View transit and lead time details"
              className={`inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-[11px] font-bold whitespace-nowrap transition-all cursor-pointer ${
                so.leadTime != null && so.leadTime <= 3
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>{so.leadTime != null ? `${so.leadTime}d` : '—'}</span>
              {hasTransitDetails && (
                <span className="w-1 h-1 rounded-full bg-blue-500 shrink-0" title="Transit details available" />
              )}
            </button>

            {/* Clean, Non-intrusive Hover/Click Popup */}
            {showLeadTimePopup && hasTransitDetails && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-40 bg-slate-900/95 text-white backdrop-blur-xs shadow-xl rounded-lg p-2 text-left min-w-[150px] max-w-[210px] border border-slate-700/80 pointer-events-none animate-in fade-in duration-150">
                <div className="space-y-1 text-[9px] font-medium leading-tight">
                  {so.region && (
                    <div className="flex items-start justify-between gap-1.5">
                      <span className="text-slate-400">Region:</span>
                      <span className="text-slate-100 font-semibold text-right truncate">
                        {so.region}
                      </span>
                    </div>
                  )}

                  {/*so.transitLevel && (
                    <div className="flex items-center justify-between gap-1.5">
                      <span className="text-slate-400">Transit Tier:</span>
                      <span className="font-mono font-bold text-blue-300">
                        {so.transitLevel}
                      </span>
                    </div>
                  )*/}

                  {so.transitDays != null && (
                    <div className="flex items-center justify-between gap-1.5">
                      <span className="text-slate-400">Transit Days:</span>
                      <span className="font-bold text-emerald-300 font-mono">
                        {so.transitDays} cal {so.transitDays === 1 ? 'day' : 'days'}
                      </span>
                    </div>
                  )}

                  {so.leadTime != null && (
                    <div className="flex items-center justify-between gap-1.5 pt-0.5 border-t border-slate-800 text-[8.5px]">
                      <span className="text-slate-400">Lead Time:</span>
                      <span className="font-bold text-amber-300">{so.leadTime} days</span>
                    </div>
                  )}
                </div>

                {/* Subtle arrow pointer */}
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45 border-l border-t border-slate-700/80" />
              </div>
            )}
          </div>
        </td>

        {/* C: Deadline (ENHANCED) */}
        <td className="p-1.5 sm:p-2 align-top min-w-[150px] sm:min-w-[170px]">
          <fieldset disabled={!canEditDeadline} className="min-w-0">
            <CustomDatePicker
              value={getPendingValue(so.id, 'deadline', so.deadline)}
              onChange={(newDate) => handleLocalFieldChange(so.id, 'deadline', newDate)}
            />
          </fieldset>

          {/* NEW: Priority & Shipping Window Badges */}
          {showDeadlineCalculationPriorityLabels && (
            <>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {so.deadlinePriority && (
                  <span
                    className={`text-[8px] px-1 py-0.5 rounded font-mono font-bold ${
                      so.deadlinePriority === 1
                        ? 'bg-purple-100 text-purple-700'
                        : so.deadlinePriority === 2
                        ? 'bg-blue-100 text-blue-700'
                        : so.deadlinePriority === 3
                        ? 'bg-teal-100 text-teal-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                    title={`Calculated via Priority ${so.deadlinePriority}`}
                  >
                    {so.deadlinePriority === 1 && `P1: Cancel - ${so.transitDays || 0}d`}
                    {so.deadlinePriority === 2 && `P2: Delivery - ${so.transitDays || 0}d`}
                    {so.deadlinePriority === 3 && `P3: Cust Lead Time`}
                    {so.deadlinePriority === 4 && `P4: Default 5d`}
                  </span>
                )}

                {so.startShipDate && so.deadlinePriority === 1 && (
                  <span
                    className="text-[8px] text-gray-500 font-medium whitespace-nowrap"
                    title={`Start Ship Date: ${formatDateDisplay(so.startShipDate)}`}
                  >
                    Start: {formatDateDisplay(so.startShipDate)}
                  </span>
                )}
              </div>

              {/* NEW: Exceptions to Flag */}
              {Array.isArray(so.deadlineExceptions) && so.deadlineExceptions.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {so.deadlineExceptions.map((exc, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-1 p-1 rounded text-[8px] sm:text-[9px] font-medium leading-tight bg-red-50 text-red-700 border border-red-200"
                    >
                      <span className="font-bold shrink-0">⚠</span>
                      <span className="break-words">{exc.message || exc.code}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* NEW: >$5,000 High-Value Fallback Warning Prompt */}
          {so.requiresDeadlineConfirmation && (
            <div className="mt-1.5 p-1.5 rounded-md bg-amber-50 border border-amber-300 space-y-1">
              <div className="flex items-center gap-1 text-[8px] sm:text-[8px] font-bold text-amber-800">
                <span>⚠ Confirmation Required</span>
              </div>
              {showDeadlineCalculationPriorityLabels && (
                <p className="text-[8px] text-amber-700 leading-tight">
                  Order &gt; $5,000 with 5-day default.
                </p>
              )}
              <button
                type="button"
                onClick={() => setShowHighValueModal(true)}
                className="w-full py-0.5 px-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-[8px] sm:text-[9px] font-bold transition shadow-xs cursor-pointer"
              >
                Review & Confirm
              </button>
            </div>
          )}

          {pendingUpdates[so.id]?.deadline !== undefined && (
            <div className="mt-1 flex items-center gap-1">
              <button
                type="button"
                onClick={() => saveField(so.id, 'deadline', pendingUpdates[so.id].deadline, targetIds)}
                disabled={isSaving || !canEditDeadline}
                className="flex-1 py-1 rounded text-[9px] sm:text-[10px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-xs cursor-pointer"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => cancelField(so.id, 'deadline')}
                disabled={isSaving || !canEditDeadline}
                className="flex-1 py-1 rounded text-[9px] sm:text-[10px] font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-all cursor-pointer"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Cancel Date */}
          {(so.hasCancelDate === 'true' || so.hasCancelDate === true || pendingUpdates[so.id]?.cancelDate !== undefined) && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="flex items-center space-x-1 text-red-600 font-bold text-sm">
                <span>Cancel Date:</span>
              </div>

              <fieldset disabled={!canEditCancelDate} className="min-w-0">
                <CustomDatePicker
                  value={getPendingValue(so.id, 'cancelDate', so.cancelDate)}
                  onChange={(newDate) => {
                    if (canEditCancelDate) saveField(so.id, 'cancelDate', newDate, targetIds);
                  }}
                />
              </fieldset>
            </div>
          )}

          {canEditCancelDate &&
            (so.hasCancelDate === 'false' || so.hasCancelDate === false) &&
            !so.cancelDate &&
            pendingUpdates[so.id]?.cancelDate === undefined && (
              <button
                type="button"
                onClick={() => handleLocalFieldChange(so.id, 'cancelDate', '')}
                className="mt-2 text-[9px] sm:text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 cursor-pointer"
              >
                + Add Cancel Date
              </button>
            )}
        </td>

        {/* F: Amount */}
        <td className="p-1.5 sm:p-2 align-top text-right font-bold text-gray-900 text-[11px] sm:text-xs whitespace-nowrap">
          {formatCurrency(so.amount)}
        </td>

        {/* G: PICKUP OR SHIP */}
        <td className="p-1.5 sm:p-2 align-top min-w-[120px]">
          <select
            value={so.pickupOrShip || 'PICKUP'}
            disabled={!canEditPickupOrShip}
            onChange={(e) => onPersistUpdate(so.id, 'pickupOrShip', e.target.value, targetIds)}
            aria-label="Pickup or ship"
            className="w-full px-1.5 sm:px-2 py-1 border border-gray-200 rounded text-[10px] sm:text-[11px] font-medium bg-white outline-none cursor-pointer"
          >
            {SHIPPING_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </td>

        {/* H: Payment Terms */}
        <td className="p-1.5 sm:p-2 text-center align-top">
          <div className="flex flex-col items-center gap-1 pb-1">
            <label
              className={`inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-medium ${
                so.isPrepaymentRequired
                  ? 'text-emerald-700'
                  : so.paymentTerms
                  ? 'text-gray-700'
                  : 'text-gray-400'
              }`}
            >
              {so.isPrepaymentRequired && (
                <input
                  type="checkbox"
                  disabled={!canEditPaymentTerms}
                  checked={!!so.isPrepaymentRequired}
                  readOnly
                  className="rounded border-emerald-300 text-emerald-700 w-3 h-3"
                />
              )}
              <span className="truncate max-w-[80px] sm:max-w-[100px]" title={so.paymentTerms}>
                {so.isPrepaymentRequired ? 'PREPAYMENT' : so.paymentTerms || 'N/A'}
              </span>
            </label>
            {so.prepaymentReceived && so.prepaymentReceivedDate && (
              <span className="inline-flex items-center gap-1 text-[8px] sm:text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 sm:px-1.5 py-0.5 rounded">
                <Check size={9} /> {formatDateDisplay(so.prepaymentReceivedDate)}
              </span>
            )}
          </div>
          {!so.prepaymentReceived && so.isPrepaymentRequired && (
            <button
              disabled={!canEditPaymentTerms}
              onClick={() => onOpenPrepaymentModal(so)}
              className="px-1.5 sm:px-2 py-0.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-[9px] font-bold transition-all shadow-sm cursor-pointer whitespace-nowrap"
            >
              Confirm
            </button>
          )}
        </td>

        {/* I: PLANNED (AS REQUIRED) */}
        <td className="p-1.5 sm:p-2 align-top min-w-[240px] sm:min-w-[280px]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {/* I1: PRODUCTION PLANNED */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-1.5 sm:p-2 space-y-1.5 relative">
              {!canEditPlannedProduction && isBlockedByPrepayment && (
                <div
                  className="absolute inset-0 bg-white/70 backdrop-blur-[0.5px] rounded-lg flex items-center justify-center z-10"
                  title="Prepayment required"
                >
                  <Lock size={12} className="text-red-500" />
                </div>
              )}
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  disabled={isBlockedByPrepayment && !canEditPlannedProduction}
                  checked={localProd}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setLocalProd(checked);
                    if (checked && !localProdDate) {
                      setLocalProdDate(new Date().toISOString().split('T')[0]);
                    } else if (!checked) {
                      setLocalProdDate('');
                    }
                  }}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-[10px] sm:text-[11px] font-bold text-gray-700">Production</span>
              </label>

              {localProd && (
                <div className="space-y-1 pt-1 border-t border-slate-200/60">
                  <input
                    type="date"
                    disabled={canEditPlannedProductionDate && isBlockedByPrepayment}
                    value={localProdDate}
                    onChange={(e) => setLocalProdDate(e.target.value)}
                    className="w-full px-1 sm:px-1.5 py-0.5 border border-gray-200 rounded text-[9px] sm:text-[10px] bg-white outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
                  />
                  {localProdDate && (
                    <div className="text-[8px] sm:text-[9px] text-indigo-700 font-bold bg-indigo-50 px-1 py-0.5 rounded text-center truncate">
                      Planned
                    </div>
                  )}
                </div>
              )}

              {hasProdChanges && (
                <button
                  type="button"
                  onClick={handleProdSave}
                  disabled={isSaving || isBlockedByPrepayment || !canSaveProdChanges}
                  className="w-full py-1 rounded text-[9px] sm:text-[10px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all shadow-sm cursor-pointer"
                >
                  {isSaving ? (
                    <span className="flex items-center justify-center gap-1">
                      <RefreshCw size={10} className="animate-spin" /> Saving...
                    </span>
                  ) : (
                    'Save'
                  )}
                </button>
              )}
            </div>

            {/* I2: MATERIALS ORDERED */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-1.5 sm:p-2 space-y-1.5 relative">
              {isBlockedByPrepayment && (
                <div
                  className="absolute inset-0 bg-white/70 backdrop-blur-[0.5px] rounded-lg flex items-center justify-center z-10"
                  title="Prepayment required"
                >
                  <Lock size={12} className="text-red-500" />
                </div>
              )}
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  disabled={isBlockedByPrepayment || !canEditPlannedMaterials}
                  checked={localMat}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setLocalMat(checked);
                    if (checked && !localMatDate) {
                      setLocalMatDate(new Date().toISOString().split('T')[0]);
                    } else if (!checked) {
                      setLocalMatDate('');
                    }
                  }}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-[10px] sm:text-[11px] font-bold text-gray-700">Materials</span>
              </label>

              {localMat && (
                <div className="space-y-1 pt-1 border-t border-slate-200/60">
                  <input
                    type="date"
                    disabled={isBlockedByPrepayment || !canEditPlannedMaterialsDate}
                    value={localMatDate}
                    onChange={(e) => setLocalMatDate(e.target.value)}
                    className="w-full px-1 sm:px-1.5 py-0.5 border border-gray-200 rounded text-[9px] sm:text-[10px] bg-white outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
                  />
                  {localMatDate && (
                    <div className="text-[8px] sm:text-[9px] text-emerald-700 font-bold bg-emerald-50 px-1 py-0.5 rounded text-center truncate">
                      Ordered
                    </div>
                  )}
                </div>
              )}

              {hasMatChanges && (
                <button
                  type="button"
                  onClick={handleMatSave}
                  disabled={isSaving || isBlockedByPrepayment || !canSaveMatChanges}
                  className="w-full py-1 rounded text-[9px] sm:text-[10px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all shadow-sm cursor-pointer"
                >
                  {isSaving ? (
                    <span className="flex items-center justify-center gap-1">
                      <RefreshCw size={10} className="animate-spin" /> Saving...
                    </span>
                  ) : (
                    'Save'
                  )}
                </button>
              )}
            </div>
          </div>
        </td>

        {/* J: ASSIGNED TO */}
        <td className="p-1.5 sm:p-2 align-top min-w-[110px]">
          <div className="relative">
            {isBlockedByPrepayment && (
              <div className="absolute inset-y-0 left-0 pl-1.5 flex items-center pointer-events-none">
                <Lock size={10} className="text-red-500" />
              </div>
            )}
            <select
              value={so.assignedTo || ''}
              disabled={isBlockedByPrepayment || !canEditAssignedTo}
              onChange={(e) => onPersistUpdate(so.id, 'assignedTo', e.target.value, targetIds)}
              aria-label="Assigned to"
              className={`w-full ${
                isBlockedByPrepayment ? 'pl-5' : 'px-1.5 sm:px-2'
              } py-1 border border-gray-200 rounded text-[10px] sm:text-[11px] font-semibold bg-white outline-none cursor-pointer`}
            >
              <option value="Unassigned">— Unassigned</option>
              {employees.map((employee) => (
                <option key={employee.name} value={employee.name}>
                  {employee.name}
                </option>
              ))}
            </select>
          </div>
        </td>

        {/* K: PACKED STATUS */}
        <td className="p-1.5 sm:p-2 align-top min-w-[140px]">
          {(() => {
            if (isGroupRow) {
              const childOrders = group?.orders || [];
              const totalFamilyUnits =
                Number(so?.units) ||
                childOrders.reduce((sum, c) => sum + (Number(c.units) || 0), 0) ||
                0;

              let totalPackedUnits = 0;
              childOrders.forEach((child) => {
                const childPLs = (child.packingLists || '')
                  .split(',')
                  .map((p) => p.trim())
                  .filter(Boolean);
                const packedUnitsObj = child.packedUnits || {};

                if (childPLs.length > 0) {
                  childPLs.forEach((pl) => {
                    totalPackedUnits += Number(packedUnitsObj[pl]) || 0;
                  });
                } else {
                  totalPackedUnits += Number(child.totalPackedUnits) || 0;
                }
              });

              const pct =
                totalFamilyUnits > 0
                  ? Math.min(Math.round((totalPackedUnits / totalFamilyUnits) * 1000) / 10, 100)
                  : 0;
              const isComplete = totalFamilyUnits > 0 && totalPackedUnits >= totalFamilyUnits;

              return (
                <div className="flex flex-col gap-1">
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden border border-gray-200">
                    <div
                      className={`h-full transition-all duration-500 rounded-full ${
                        isComplete ? 'bg-emerald-500' : pct > 0 ? 'bg-blue-500' : 'bg-transparent'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  <div className="flex justify-between items-center text-[8px] sm:text-[9px] font-mono pt-0.5">
                    <span className="text-gray-600 font-semibold">
                      {totalPackedUnits}/{totalFamilyUnits}
                    </span>
                    <span
                      className={
                        isComplete
                          ? 'text-emerald-600 font-bold'
                          : totalPackedUnits > 0
                          ? 'text-amber-600 font-bold'
                          : 'text-gray-400 font-bold'
                      }
                    >
                      {isComplete ? 'COMPLETE' : totalPackedUnits > 0 ? `${pct}%` : '0%'}
                    </span>
                  </div>
                </div>
              );
            }

            const plArray = (so?.packingLists || '')
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean);

            const packedUnitsObj = so?.packedUnits || {};
            const totalSOUnits = formatNum(so?.units) || 0;
            const currentPLValue = getPendingValue(so.id, 'packingLists', so.packingLists);
            const packingListsReadOnly = !canEditPackingLists;

            if (plArray.length === 0) {
              return (
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    placeholder="PL #"
                    value={currentPLValue}
                    readOnly={packingListsReadOnly}
                    onChange={(e) => handleLocalFieldChange(so.id, 'packingLists', e.target.value)}
                    aria-label="Packing list numbers"
                    title={
                      packingListsReadOnly
                        ? 'Editing PL# requires permission in Settings'
                        : 'Packing list numbers, comma-separated if multiple'
                    }
                    className={`w-full px-1.5 sm:px-2 py-1 border border-gray-200 rounded text-[9px] sm:text-[10px] font-mono outline-none ${
                      packingListsReadOnly
                        ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                        : 'bg-white focus:ring-1 focus:ring-indigo-500'
                    }`}
                  />
                  {pendingUpdates[so.id]?.packingLists !== undefined && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={!canEditPackingLists || isSaving}
                        onClick={() =>
                          saveField(so.id, 'packingLists', pendingUpdates[so.id].packingLists, targetIds)
                        }
                        className="flex-1 px-1 py-0.5 text-[8px] rounded bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        disabled={!canEditPackingLists || isSaving}
                        onClick={() => cancelField(so.id, 'packingLists')}
                        className="flex-1 px-1 py-0.5 text-[8px] rounded bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden border border-gray-200">
                    <div className="h-full bg-transparent rounded-full" style={{ width: '0%' }} />
                  </div>
                  <div className="flex justify-between items-center text-[8px] font-mono text-gray-400">
                    <span>0/{totalSOUnits}</span>
                    <span className="font-bold">0%</span>
                  </div>
                </div>
              );
            }

            let runningTotal = 0;
            const plDetails = plArray.map((plNumber) => {
              const plQty = Number(packedUnitsObj[plNumber]) || 0;
              runningTotal += plQty;
              const cumulativePercent =
                totalSOUnits > 0
                  ? Math.min(Math.round((runningTotal / totalSOUnits) * 1000) / 10, 100)
                  : 0;
              return { plNumber, plQty, cumulativePercent };
            });

            const isFullyPacked = runningTotal >= totalSOUnits && totalSOUnits > 0;

            return (
              <div className="flex flex-col gap-1.5">
                <input
                  type="text"
                  placeholder="PL #"
                  value={currentPLValue}
                  readOnly={packingListsReadOnly}
                  onChange={(e) => handleLocalFieldChange(so.id, 'packingLists', e.target.value)}
                  aria-label="Packing list numbers"
                  title={
                    packingListsReadOnly
                      ? 'Editing PL# requires permission in Settings'
                      : 'Packing list numbers, comma-separated if multiple'
                    }
                  className={`w-full px-1.5 sm:px-2 py-1 border border-gray-200 rounded text-[9px] sm:text-[10px] font-mono outline-none ${
                    packingListsReadOnly
                      ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                      : 'bg-white focus:ring-1 focus:ring-indigo-500'
                  }`}
                />
                {pendingUpdates[so.id]?.packingLists !== undefined && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={!canEditPackingLists || isSaving}
                      onClick={() =>
                        saveField(so.id, 'packingLists', pendingUpdates[so.id].packingLists, targetIds)
                      }
                      className="flex-1 px-1 py-0.5 text-[8px] rounded bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      disabled={!canEditPackingLists || isSaving}
                      onClick={() => cancelField(so.id, 'packingLists')}
                      className="flex-1 px-1 py-0.5 text-[8px] rounded bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {plDetails.map((item, index) => (
                  <div key={item.plNumber || index} className="flex flex-col gap-0.5">
                    <div className="flex justify-between items-center text-[9px] sm:text-[10px] font-mono">
                      <span className="text-gray-500 text-[8px] sm:text-[9px]">
                        {item.plQty} un ({item.cumulativePercent}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden border border-gray-200">
                      <div
                        className={`h-full transition-all duration-500 rounded-full ${
                          item.cumulativePercent >= 100 ? 'bg-emerald-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${item.cumulativePercent}%` }}
                      />
                    </div>
                  </div>
                ))}

                <div className="flex justify-between items-center text-[8px] font-mono pt-0.5 border-t border-gray-100 text-gray-400">
                  <span>
                    Total: {runningTotal}/{totalSOUnits}
                  </span>
                  <span
                    className={
                      isFullyPacked ? 'text-emerald-600 font-bold' : 'text-amber-600 font-bold'
                    }
                  >
                    {isFullyPacked ? 'COMPLETE' : 'PARTIAL'}
                  </span>
                </div>
              </div>
            );
          })()}
        </td>

        {/* L: SHIPPED STATUS */}
        <td className="p-1.5 sm:p-2 align-top min-w-[120px]">
          <select
            value={shippingStatus || ''}
            disabled={!canEditShippingStatus}
            onChange={(e) => onPersistUpdate(so.id, 'shippingStatus', e.target.value, targetIds)}
            aria-label="Shipping status"
            className={`w-full px-1.5 sm:px-2 py-1 border rounded text-[10px] sm:text-[11px] font-semibold outline-none transition-colors cursor-pointer ${
              shippingStatus === 'shipped' || shippingStatus === 'delivered'
                ? 'bg-purple-50 border-purple-200 text-purple-700 font-bold'
                : shippingStatus === 'partiallyshipped' || shippingStatus === 'intransit' || shippingStatus === 'pickedup'
                ? 'bg-amber-50 border-amber-200 text-amber-700 font-bold'
                : shippingStatus === 'ready' || shippingStatus === 'readyforshipping'
                ? 'bg-cyan-50 border-cyan-200 text-cyan-700 font-bold'
                : shippingStatus === 'issue' || shippingStatus === 'onhold'
                ? 'bg-amber-50 border-amber-200 text-amber-700 font-bold'
                : 'bg-white border-gray-200 text-gray-500'
            }`}
          >
            {shippingStatus &&
              !SHIPPED_STATUS_OPTIONS.some(
                (opt) => opt.value.toLowerCase().trim() === so.shippingStatus
              ) && <option value={so.shippingStatus}>{so.shippingStatus}</option>}

            {SHIPPED_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </td>

        {/* M: INVOICE STATUS */}
        <td className="p-1.5 sm:p-2 align-top min-w-[140px]">
          {(() => {
            const getInvStr = (orderObj) => {
              const candidates = [
                orderObj.invoiceNumber,
                orderObj._raw?.invoiceNumber,
                orderObj.invoice,
                orderObj._raw?.invoice,
              ];
              for (const c of candidates) {
                const s = c != null ? String(c).trim() : '';
                if (s && s.toLowerCase() !== 'n/a' && s !== '-') return s;
              }
              return '';
            };

            if (isGroupRow) {
              const childOrders = group?.orders || [];
              const total = childOrders.length;
              const invoiced = childOrders.filter((c) => getInvStr(c) !== '').length;
              const pct = total > 0 ? Math.round((invoiced / total) * 100) : 0;
              const isComplete = total > 0 && invoiced >= total;

              return (
                <div className="flex flex-col gap-1">
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden border border-gray-200">
                    <div
                      className={`h-full transition-all duration-500 rounded-full ${
                        isComplete ? 'bg-emerald-500' : pct > 0 ? 'bg-blue-500' : 'bg-transparent'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between items-center text-[8px] sm:text-[9px] font-mono pt-0.5">
                    <span className="text-gray-600 font-semibold">
                      {invoiced}/{total} SOs
                    </span>
                    <span
                      className={
                        isComplete
                          ? 'text-emerald-600 font-bold'
                          : invoiced > 0
                          ? 'text-blue-600 font-bold'
                          : 'text-gray-400 font-bold'
                      }
                    >
                      {isComplete ? 'COMPLETE' : pct > 0 ? `${pct}%` : '0%'}
                    </span>
                  </div>
                </div>
              );
            }

            const currentVal = getPendingValue(so.id, 'invoiceNumber', so.invoiceNumber);
            const hasInvoice =
              currentVal &&
              String(currentVal).trim() !== '' &&
              String(currentVal).trim().toLowerCase() !== 'n/a' &&
              String(currentVal).trim() !== '-';

            return (
              <div className="flex flex-col gap-1">
                <input
                  type="text"
                  placeholder="Invoice #"
                  value={currentVal}
                  readOnly={!canEditInvoiceNumber}
                  onChange={(e) => handleLocalFieldChange(so.id, 'invoiceNumber', e.target.value)}
                  aria-label="Invoice number"
                  title={
                    !canEditInvoiceNumber
                      ? 'Editing Invoice # requires permission in Settings'
                      : 'Invoice number'
                  }
                  className={`w-full px-1.5 sm:px-2 py-1 border border-gray-200 rounded text-[9px] sm:text-[10px] font-mono outline-none ${
                    !canEditInvoiceNumber
                      ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                      : 'bg-white focus:ring-1 focus:ring-indigo-500'
                  }`}
                />

                {pendingUpdates[so.id]?.invoiceNumber !== undefined && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <button
                      type="button"
                      disabled={!canEditInvoiceNumber || isSaving}
                      onClick={() =>
                        saveField(so.id, 'invoiceNumber', pendingUpdates[so.id].invoiceNumber, targetIds)
                      }
                      className="flex-1 px-1 py-0.5 text-[8px] rounded bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      disabled={!canEditInvoiceNumber || isSaving}
                      onClick={() => cancelField(so.id, 'invoiceNumber')}
                      className="flex-1 px-1 py-0.5 text-[8px] rounded bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {!pendingUpdates[so.id]?.invoiceNumber && (
                  <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden border border-gray-200 mt-0.5">
                    <div
                      className={`h-full transition-all duration-500 rounded-full ${
                        hasInvoice ? 'bg-emerald-500' : 'bg-transparent'
                      }`}
                      style={{ width: hasInvoice ? '100%' : '0%' }}
                    />
                  </div>
                )}
                {!pendingUpdates[so.id]?.invoiceNumber && (
                  <div className="flex justify-between items-center text-[8px] font-mono text-gray-400">
                    <span>{hasInvoice ? '1/1' : '0/1'}</span>
                    <span className={hasInvoice ? 'text-emerald-600 font-bold' : ''}>
                      {hasInvoice ? '100%' : '0%'}
                    </span>
                  </div>
                )}
              </div>
            );
          })()}
        </td>

        {/* N: CUSTOM DOCUMENTS */}
        <td className="p-1.5 sm:p-2 align-top text-[10px] sm:text-[11px] min-w-[140px]">
          <label className="flex items-center gap-1 cursor-pointer select-none">
            <input
              type="checkbox"
              disabled={!canEditCustomDocs}
              checked={localNeedsDocs}
              onChange={(e) => setLocalNeedsDocs(e.target.checked)}
              className="rounded text-indigo-600"
            />
            <span>Required</span>
          </label>

          {localNeedsDocs && (
            <div className="mt-1 space-y-1">
              <input
                type="date"
                disabled={!canEditCustomDocsSubmissionDate}
                value={localDocsDate}
                onChange={(e) => setLocalDocsDate(e.target.value)}
                aria-label="Date sent to customer or broker"
                className="w-full border rounded text-[9px] sm:text-[10px] px-1 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
              />

              <span className="text-[8px] sm:text-[9px] text-gray-500 block">
                Date Sent to Customer/Broker
              </span>
            </div>
          )}

          {hasDocsChanges && (
            <div className="mt-1.5">
              <button
                type="button"
                onClick={handleCustomDocsSave}
                disabled={!canSubmitDocs || isSaving || !canSaveCustomDocsChanges}
                className={`w-full py-1 rounded text-[9px] sm:text-[10px] font-bold text-white transition-all shadow-sm ${
                  canSubmitDocs
                    ? 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer'
                    : 'bg-gray-300 cursor-not-allowed opacity-60'
                }`}
              >
                {isSaving ? (
                  <span className="flex items-center justify-center gap-1">
                    <RefreshCw size={10} className="animate-spin" /> Saving...
                  </span>
                ) : (
                  'Submit Docs'
                )}
              </button>
            </div>
          )}
        </td>

        {/* --- DELIVERY APPOINTMENT CELL --- */}
        <td className="p-1.5 sm:p-2 align-top text-[10px] sm:text-[11px] min-w-[190px]">
          <label className="flex items-center gap-1 cursor-pointer select-none font-bold">
            <input
              type="checkbox"
              disabled={!canEditDeliveryAppointment}
              checked={localNeedsAppt}
              onChange={(e) => {
                const checked = e.target.checked;
                setLocalNeedsAppt(checked);
                if (!checked) {
                  setLocalApptDate('');
                  setLocalApptTime('');
                  setLocalConfNo('');
                  setLocalApptConfirmed(false);
                  const payload = {
                    needsDeliveryAppt: false,
                    deliveryApptDate: '',
                    deliveryApptTime: '',
                    deliveryConfirmationNo: '',
                    deliveryApptConfirmed: false,
                  };
                  onPersistUpdate(so.id, 'deliveryApptPayload', payload, targetIds);
                }
              }}
              className="rounded text-indigo-600"
            />
            <span>Required</span>
          </label>

          {localNeedsAppt && (
            <div className="mt-1.5 space-y-1.5 p-2 bg-slate-50 border border-slate-200 rounded-lg">
              <div>
                <span className="text-[8px] text-gray-500 font-bold block mb-0.5">Appt Date</span>
                <input
                  type="date"
                  disabled={!canEditDeliveryAppointmentDate}
                  value={localApptDate}
                  onChange={(e) => setLocalApptDate(e.target.value)}
                  className="w-full border rounded text-[9px] px-1 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
                />
              </div>

              <div>
                <span className="text-[8px] text-gray-500 font-bold block mb-0.5">
                  Time / Window (e.g. 1PM-4PM)
                </span>
                <input
                  type="text"
                  disabled={!canEditDeliveryAppointmentTime}
                  placeholder="e.g. 1PM TO 4PM"
                  value={localApptTime}
                  onChange={(e) => setLocalApptTime(e.target.value)}
                  className="w-full border rounded text-[9px] px-1 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500 bg-white font-medium"
                />
              </div>

              <div>
                <span className="text-[8px] text-gray-500 font-bold block mb-0.5">
                  Load ID / Appt #
                </span>
                <input
                  type="text"
                  disabled={!canEditDeliveryConfirmationNumber}
                  placeholder="Confirmation #"
                  value={localConfNo}
                  onChange={(e) => setLocalConfNo(e.target.value)}
                  className="w-full border rounded text-[9px] px-1 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500 bg-white font-mono font-semibold"
                />
              </div>

              <label className="flex items-center gap-1 cursor-pointer select-none py-0.5">
                <input
                  type="checkbox"
                  disabled={!canEditDeliveryAppointmentConfirmed}
                  checked={localApptConfirmed}
                  onChange={(e) => setLocalApptConfirmed(e.target.checked)}
                  className="rounded text-emerald-600 focus:ring-emerald-500 w-3 h-3"
                />
                <span className="text-[9px] font-extrabold text-gray-700">Appt Confirmed</span>
              </label>

              {hasApptChanges && (
                <button
                  type="button"
                  onClick={handleDeliveryApptSave}
                  disabled={isSaving || !canSaveApptChanges}
                  className="w-full py-1 mt-1 rounded text-[9px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-all cursor-pointer"
                >
                  {isSaving ? 'Saving...' : 'Confirm Appt'}
                </button>
              )}

              {localApptConfirmed && !hasApptChanges && (
                <div className="text-[8px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 py-0.5 rounded text-center truncate">
                  ✓ Scheduled & Confirmed
                </div>
              )}
            </div>
          )}
        </td>

        {/* O: TRACKING LINK */}
        <td className="p-1.5 sm:p-2 align-top min-w-[130px]">
          <div className="flex items-center gap-1">
            <input
              type="text"
              disabled={!canEditTrackingLink}
              placeholder="URL"
              value={getPendingValue(so.id, 'trackingLink', so.trackingLink)}
              onChange={(e) => handleLocalFieldChange(so.id, 'trackingLink', e.target.value)}
              aria-label="Tracking link URL"
              className="flex-1 min-w-0 px-1.5 sm:px-2 py-1 border border-gray-200 rounded text-[9px] sm:text-[10px] outline-none"
            />

            {getPendingValue(so.id, 'trackingLink', so.trackingLink) && (
              <a
                href={getPendingValue(so.id, 'trackingLink', so.trackingLink)}
                target="_blank"
                rel="noreferrer"
                aria-label="Open tracking link"
                className="text-indigo-600 shrink-0 hover:text-indigo-800"
              >
                <ExternalLink size={12} />
              </a>
            )}
          </div>

          {pendingUpdates[so.id]?.trackingLink !== undefined && (
            <div className="mt-1 flex items-center gap-1">
              <button
                type="button"
                disabled={!canEditTrackingLink || isSaving}
                onClick={() =>
                  saveField(so.id, 'trackingLink', pendingUpdates[so.id].trackingLink, targetIds)
                }
                className="px-1.5 sm:px-2 py-0.5 text-[9px] rounded bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer"
              >
                {isSaving ? (
                  <span className="flex items-center justify-center gap-1">
                    <RefreshCw size={10} className="animate-spin" /> Saving...
                  </span>
                ) : (
                  'Save'
                )}
              </button>

              <button
                type="button"
                disabled={!canEditTrackingLink || isSaving}
                onClick={() => cancelField(so.id, 'trackingLink')}
                className="px-1.5 sm:px-2 py-0.5 text-[9px] rounded bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer"
              >
                Cancel
              </button>
            </div>
          )}
        </td>

        {/* P: DEPARTURE DATE */}
        <td className="p-1.5 sm:p-2 align-top min-w-[120px]">
          <input
            type="date"
            disabled={!canEditDepartureDate}
            value={
              pendingUpdates[so.id]?.departureDate !== undefined
                ? pendingUpdates[so.id].departureDate
                : so.departureDate || ''
            }
            onChange={(e) => setLocalFieldValue(so.id, 'departureDate', e.target.value)}
            aria-label="Departure date"
            className="w-full px-1 sm:px-1.5 py-1 border border-gray-200 rounded text-[9px] sm:text-[10px]"
          />

          {pendingUpdates[so.id]?.departureDate !== undefined && (
            <button
              type="button"
              disabled={!canEditDepartureDate || isSaving}
              onClick={() =>
                saveField(so.id, 'departureDate', pendingUpdates[so.id].departureDate, targetIds)
              }
              className="mt-1 px-1.5 sm:px-2 py-0.5 text-[9px] rounded bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer"
            >
              Save
            </button>
          )}
        </td>

        {/* Q: ETA */}
        <td className="p-1.5 sm:p-2 align-top min-w-[100px]">
          <input
            type="date"
            disabled={!canEditEta}
            value={
              pendingUpdates[so.id]?.eta !== undefined
                ? pendingUpdates[so.id].eta
                : so.eta || ''
            }
            onChange={(e) => setLocalFieldValue(so.id, 'eta', e.target.value)}
            aria-label="ETA date"
            className="w-full px-1 sm:px-1.5 py-1 border border-gray-200 rounded text-[9px] sm:text-[10px]"
          />

          {pendingUpdates[so.id]?.eta !== undefined && (
            <button
              type="button"
              disabled={!canEditEta || isSaving}
              onClick={() => saveField(so.id, 'eta', pendingUpdates[so.id].eta, targetIds)}
              className="mt-1 px-1.5 sm:px-2 py-0.5 text-[9px] rounded bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer"
            >
              Save
            </button>
          )}
        </td>

        {/* R: INVOICE SUBMISSION */}
        <td className="p-1.5 sm:p-2 align-top text-[10px] sm:text-[11px] min-w-[140px]">
          <label className="flex items-center gap-1 cursor-pointer select-none">
            <input
              type="checkbox"
              disabled={!canEditInvoiceSubmission}
              checked={localAccountingChecked}
              onChange={(e) => {
                const checked = e.target.checked;
                setLocalAccountingChecked(checked);
                if (checked && !localAccountingDate) {
                  setLocalAccountingDate(new Date().toISOString().split('T')[0]);
                }
                if (!checked) {
                  setLocalAccountingDate('');
                }
              }}
              className="rounded text-emerald-600 focus:ring-emerald-500"
            />
            <span>Confirmed</span>
          </label>

          {localAccountingChecked && (
            <div className="mt-1 space-y-1">
              <input
                type="date"
                disabled={!canEditInvoiceSubmissionDate}
                value={localAccountingDate}
                onChange={(e) => setLocalAccountingDate(e.target.value)}
                aria-label="Date submitted to customer Done by accounting"
                className="w-full border rounded text-[9px] sm:text-[10px] px-1 py-0.5 outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          )}

          {hasInvoiceSubmissionChanges && (
            <div className="mt-1.5">
              <button
                type="button"
                onClick={handleInvoiceSubmissionSave}
                disabled={isSaving || !canSaveInvoiceSubmissionChanges}
                className="w-full py-1 rounded text-[9px] sm:text-[10px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all shadow-sm cursor-pointer"
              >
                {isSaving ? (
                  <span className="flex items-center justify-center gap-1">
                    <RefreshCw size={10} className="animate-spin" /> Saving...
                  </span>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          )}
        </td>

        {/* S: Actions */}
        <td className="p-1.5 sm:p-2 text-center align-top">
          <div className="flex flex-col gap-1 items-stretch min-w-[75px] sm:min-w-[80px]">
            {canMarkComplete && (
              <button
                onClick={() => {
                  if (canMarkComplete) onComplete(so.id, targetIds);
                }}
                className="px-1.5 sm:px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[9px] sm:text-[10px] font-bold transition-colors cursor-pointer whitespace-nowrap"
              >
                MARK COMPLETE
              </button>
            )}

            {canDelete && !isGroupRow && (
              <button
                onClick={handleDeleteClick}
                className="px-1.5 sm:px-2 py-1 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded text-[9px] sm:text-[10px] font-bold transition-colors cursor-pointer"
              >
                DELETE
              </button>
            )}
          </div>
        </td>

        {/* U: Comments */}
        <td className="p-1.5 sm:p-2 align-top min-w-[400px]">
          <div className="flex items-center gap-1">
            <textarea
              placeholder="Enter comment..."
              value={getPendingValue(so.id, 'comments', so.comments)}
              onChange={(e) => {
                const value = e.target.value;

                handleLocalFieldChange(so.id, 'comments', value);
                // Clear any existing debounce timer
                clearTimeout(commentTimers.current[so.id]);

                // Auto-save after 800ms of inactivity
                commentTimers.current[so.id] = setTimeout(() => {
                  saveField(so.id, 'comments', value, targetIds);
                }, 800);
              }}
              aria-label="Enter your comment here..."
              className="flex-1 min-w-0 px-1.5 sm:px-2 py-1 border border-gray-200 rounded text-[9px] sm:text-[10px] outline-none resize-y"
            />

            {/* Clear Comment */}
            <button
              type="button"
              title="Clear comment"
              disabled={isSaving}
              onClick={() => {
                clearTimeout(commentTimers.current[so.id]);
                handleLocalFieldChange(so.id, 'comments', '');
                saveField(so.id, 'comments', '', targetIds);
              }}
              className="px-1.5 py-1 text-gray-500 hover:text-red-600 rounded"
            >
              <X size={13} />
            </button>
          </div>
        </td>

      </tr>

      {/* ───────────────────────────────────────────────────────────── */}
      {/* NEW: 100% MOBILE-FRIENDLY LEAD-TIME CONFIRMATION MODAL (> $5K) */}
      {/* ───────────────────────────────────────────────────────────── */}
      {showHighValueModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-md w-full p-4 sm:p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between border-b pb-3">
              <div>
                <h3 className="text-sm sm:text-base font-extrabold text-gray-900 flex items-center gap-2">
                  <span className="text-amber-500">⚠</span> Shipping Deadline Confirmation
                </h3>
                <p className="text-[11px] sm:text-xs text-gray-500 font-mono mt-0.5">
                  Order {so.soNumber} — Value: {formatCurrency(so.amount)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowHighValueModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-md text-base font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-[11px] sm:text-xs leading-relaxed">
              This order exceeds $5,000 and no customer-specific deadline or lead time was found. The
              system has assigned the default of <strong>5 business days</strong>. Please confirm
              whether this is sufficient.
            </div>

            <div className="space-y-3 pt-1">
              <div>
                <label className="block text-[11px] sm:text-xs font-bold text-gray-700 mb-1">
                  Adjust Lead Time (Business Days)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={modalBusinessDays}
                    onChange={(e) => setModalBusinessDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-indigo-500 text-center"
                  />
                  <span className="text-xs text-gray-500 font-medium">business days</span>
                </div>
              </div>

              {/* Recalculated Deadline Preview */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex items-center justify-between">
                <span className="text-[11px] text-gray-600 font-medium">Recalculated Deadline:</span>
                <span className="text-xs sm:text-sm font-bold font-mono text-indigo-700">
                  {formatDateDisplay(liveRecalculatedDeadline)}
                </span>
              </div>
              <p className="text-[9px] sm:text-[10px] text-gray-400 italic">
                * Automatically adjusts weekend dates backward to the previous Friday.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-center gap-2 pt-2 border-t">
              <button
                type="button"
                onClick={() => setShowHighValueModal(false)}
                className="w-full sm:w-auto flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-100 transition cursor-pointer text-center"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={handleConfirmHighValueDeadline}
                disabled={isSaving}
                className="w-full sm:w-auto flex-1 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold shadow-sm transition cursor-pointer text-center"
              >
                {isSaving ? 'Confirming...' : 'Confirm Deadline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ─────────────────────────────────────────────
// CALENDAR VIEW
// ─────────────────────────────────────────────
const normalizeDate = (dateVal) => {
  if (!dateVal) return '';
  try {
    const val = (typeof dateVal === 'object' && dateVal?.$date) ? dateVal.$date : dateVal;
    const str = String(val).trim();
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  } catch { return ''; }
};

// ─────────────────────────────────────────────
// CALENDAR VIEW (fully synced with OrderView)
// ─────────────────────────────────────────────
function CalendarView({
  orders,
  manualGroups = {},
  groupTagOverrides = {},
  rangeWeeks,
  weekOffset,
  onRangeChange,
  onOffsetChange,
  permissions,
  onOpenBulkEdit
}) {
  const DAY_NAMES_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const DAY_NAMES_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState([]);

  const [viewMode, setViewMode] = useState('week');
  const [monthOffset, setMonthOffset] = useState(0);
  const [downloadingDoc, setDownloadingDoc] = useState(null);

  const today = new Date();
  const monday = getMonday(today);
  monday.setDate(monday.getDate() + (weekOffset * 7));

  // ════════════════════════════════════════════════════════════════
  // SHARED DATA READERS — match OrderView logic 1:1
  // ════════════════════════════════════════════════════════════════

  const getInvoiceNumber = (so) => {
    if (!so) return '';
    const candidates = [
      so.invoiceNumber,
      so._raw?.invoiceNumber,
      so.invoice,
      so._raw?.invoice,
    ];
    for (const c of candidates) {
      const s = c != null ? String(c).trim() : '';
      if (s && s.toLowerCase() !== 'n/a' && s !== '-') return s;
    }
    return '';
  };

  const getPackingLists = (so) => {
    const raw = so?.packingLists || so?._raw?.packingLists || '';
    return String(raw).split(',').map(x => x.trim()).filter(Boolean);
  };

  const getPackedUnitsObj = (so) => so?.packedUnits || so?._raw?.packedUnits || {};

  const getSOUnits = (so) => Number(so?.units) || 0;

  const getShippingLabel = (so) => {
    const s = (so?.shippingStatus || '').trim();
    return s || 'Pending';
  };

  // ERPAG Download handler for Sales Order (GET) and Packing List (POST)
  const handleErpagDownload = async (type, so, plNumber = '') => {
    if (!so) return;
    const soNum = so.soNumber || so.salesOrderNumber || '';
    const pls = getPackingLists(so);
    const targetPl = plNumber || (pls.length > 0 ? pls[0] : '');
    const downloadKey = `${type}-${so.id}-${targetPl || soNum}`;

    try {
      setDownloadingDoc(downloadKey);

      if (type === 'so' && api?.OperationsPlanner?.downloadErpagSO) {
        const res = await api.OperationsPlanner.downloadErpagSO({
          orderId: so.id,
          soNumber: soNum,
        });
        if (res?.fileUrl || res?.url) {
          window.open(res.fileUrl || res.url, '_blank', 'noopener,noreferrer');
          return;
        }
        if (res instanceof Blob) {
          const url = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          a.href = url;
          a.download = `SO_${soNum || so.id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          return;
        }
      }

      if (type === 'pl' && api?.OperationsPlanner?.downloadErpagPackingList) {
        const res = await api.OperationsPlanner.downloadErpagPackingList({
          orderId: so.id,
          soNumber: soNum,
          packingListNumber: targetPl,
        });
        if (res?.fileUrl || res?.url) {
          window.open(res.fileUrl || res.url, '_blank', 'noopener,noreferrer');
          return;
        }
        if (res instanceof Blob) {
          const url = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          a.href = url;
          a.download = `PackingList_${targetPl || soNum || so.id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          return;
        }
      }

      const isSO = type === 'so';
      const params = new URLSearchParams({
        type: isSO ? 'so' : 'pl',
        orderId: so.id || '',
        soNumber: soNum,
        ...(targetPl ? { packingListNumber: targetPl, plid: targetPl } : {}),
      });

      const response = await fetch(
        `${API_BASE_URL}/weeklyplanner/erpag/download?${params.toString()}`,
        {
          method: isSO ? 'GET' : 'POST',
          headers: isSO
            ? { Accept: 'application/pdf, application/json' }
            : {
                'Content-Type': 'application/json',
                Accept: 'application/pdf, application/json',
              },
          body: isSO
            ? undefined
            : JSON.stringify({
                type: 'pl',
                orderId: so.id || '',
                soNumber: soNum,
                packingListNumber: targetPl,
                plid: targetPl,
              }),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(
          errData.message ||
            `Failed to download ${isSO ? 'Sales Order' : 'Packing List'} from ERPAG.`
        );
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        if (data.fileUrl || data.url || data.downloadUrl) {
          window.open(
            data.fileUrl || data.url || data.downloadUrl,
            '_blank',
            'noopener,noreferrer'
          );
        } else if (data.base64) {
          const link = document.createElement('a');
          link.href = `data:application/pdf;base64,${data.base64}`;
          link.download = isSO
            ? `SO_${soNum || so.id}.pdf`
            : `PackingList_${targetPl || soNum || so.id}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else {
          throw new Error('No downloadable file returned from ERPAG.');
        }
      } else {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = isSO
          ? `SO_${soNum || so.id}.pdf`
          : `PackingList_${targetPl || soNum || so.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error(`ERPAG ${type.toUpperCase()} download error:`, err);
      alert(
        err.message ||
          `Unable to download ${type === 'so' ? 'Sales Order' : 'Packing List'} from ERPAG.`
      );
    } finally {
      setDownloadingDoc(null);
    }
  };


  // GFS family labels should be visible alongside the date in calendar views.
  const isGfsFamilyItem = (item) => Boolean(
    item?.isGroupRow && /\bGFS\b|Gordon Food Service/i.test(
      `${item.poTagNumber || ''} ${item.customer || ''} ${item.groupKey || ''}`
    )
  );

  const getOrderHoverText = (item) =>
    `Customer: ${item?.customer || '—'}\nTag: ${item?.poTagNumber || item?.tag || '—'}`;

  /**
   * Invoice progress — matches OrderView Col M
   * - Family: X children with invoice# / total children
   * - Individual: 0/1 or 1/1
   */
  const getInvoiceProgress = (item) => {
    if (item?.isGroupRow) {
      const children = item.childOrders || [];
      const total = children.length;
      const invoiced = children.filter((c) => getInvoiceNumber(c) !== '').length;
      const pct = total > 0 ? Math.round((invoiced / total) * 100) : 0;
      return { total, invoiced, pct, invoiceNumber: null };
    }
    const invoiceNumber = getInvoiceNumber(item);
    const has = invoiceNumber !== '';
    return { total: 1, invoiced: has ? 1 : 0, pct: has ? 100 : 0, invoiceNumber };
  };

  /**
   * Packed progress — matches OrderView Col K
   * - Family: sum packed units across all children × all PLs / sum children units
   * - Individual: sum packed units across its PLs / its units
   */
  const getPackedProgress = (item) => {
    if (item?.isGroupRow) {
      const children = item.childOrders || [];
      const totalUnits = Number(item?.units) ||
        children.reduce((s, c) => s + getSOUnits(c), 0) || 0;
      let packed = 0;
      children.forEach((c) => {
        const pls = getPackingLists(c);
        const obj = getPackedUnitsObj(c);
        if (pls.length > 0) {
          pls.forEach(pl => { packed += Number(obj[pl]) || 0; });
        } else {
          packed += Number(c.totalPackedUnits) || 0;
        }
      });
      const pct = totalUnits > 0
        ? Math.min(Math.round((packed / totalUnits) * 1000) / 10, 100)
        : 0;
      return { total: totalUnits, packed, pct };
    }

    const totalUnits = getSOUnits(item);
    const pls = getPackingLists(item);
    const obj = getPackedUnitsObj(item);
    const packed = pls.length > 0
      ? pls.reduce((s, pl) => s + (Number(obj[pl]) || 0), 0)
      : Number(item?.totalPackedUnits) || 0;
    const pct = totalUnits > 0
      ? Math.min(Math.round((packed / totalUnits) * 1000) / 10, 100)
      : 0;
    return { total: totalUnits, packed, pct };
  };

  // ════════════════════════════════════════════════════════════════
  // FAMILY GROUPING — unchanged logic, now runs on merged orders
  // ════════════════════════════════════════════════════════════════
  const calendarItems = useMemo(() => {
    const items = [];
    const usedIds = new Set();
    const activeOrders = orders.filter(o => !o.completed && !o.isReturned);

    const pickEarliestDeadline = (ords) => {
      const dates = ords.map(o => normalizeDate(o.deadline)).filter(Boolean).sort();
      return dates[0] || '';
    };
    const pickEarliestcancelDate = (ords) => {
      const dates = ords.map(o => normalizeDate(o.cancelDate)).filter(Boolean).sort();
      return dates[0] || '';
    };

    const buildGroupEntry = (groupKey, type, parentPO, ords) => {
      const earliest = pickEarliestDeadline(ords);
      const cancelDate = pickEarliestcancelDate(ords);
      const totalAmount = ords.reduce((s, o) => s + (o.amount || 0), 0);
      const totalUnits = ords.reduce((s, o) => s + (o.units || 0), 0);
      const first = ords[0];
      const assignedSet = new Set(ords.map(o => (o.assignedTo || '').trim()).filter(Boolean));
      const assignedLabel = assignedSet.size === 0 ? 'Unassigned'
        : assignedSet.size === 1 ? [...assignedSet][0] : 'Mixed';

      return {
        id: groupKey, isGroupRow: true, groupKey, groupType: type,
        soNumber: parentPO, poTagNumber: parentPO,
        customer: first.customer, deadline: earliest, cancelDate,
        amount: totalAmount, units: totalUnits, assignedTo: assignedLabel,
        childOrders: ords, childCount: ords.length,
        childOrderIds: ords.map(o => o.id)
      };
    };

    Object.values(manualGroups).forEach(mg => {
      const ords = activeOrders.filter(o => mg.orderIds.includes(o.id));
      if (ords.length === 0) return;
      ords.forEach(o => usedIds.add(o.id));
      const groupKey = `manual-${mg.id}`;
      const parentPO = groupTagOverrides[groupKey] || mg.parentPO || 'FAMILY';
      items.push(buildGroupEntry(groupKey, 'manual', parentPO, ords));
    });

    const autoBuckets = {};
    activeOrders.forEach(so => {
      if (usedIds.has(so.id)) return;
      const meta = getAutoGroupMeta(so);
      if (!meta) return;
      if (!autoBuckets[meta.key]) autoBuckets[meta.key] = { ords: [], meta };
      autoBuckets[meta.key].ords.push(so);
    });

    Object.entries(autoBuckets).forEach(([bucketKey, bucket]) => {
      const ords = bucket.ords;
      if (ords.length < 2) return;
      ords.forEach(o => usedIds.add(o.id));
      const groupKey = bucketKey;
      let parentPO = groupTagOverrides[groupKey] || bucket.meta.familyName;

      if (!parentPO && bucket.meta.key.startsWith('auto-mm::')) {
        const cores = ords.map(o => {
          const m = (o.poTagNumber || o.tag || '').match(/(\d{6})/);
          return m ? m[1] : null;
        }).filter(Boolean).sort();
        if (cores.length > 0) {
          const min = cores[0], max = cores[cores.length - 1];
          parentPO = min === max ? min : `${min}-${max}`;
        }
      }
      if (!parentPO) parentPO = ords[0].poTagNumber || ords[0].soDate || 'FAMILY';
      items.push(buildGroupEntry(groupKey, 'auto', parentPO, ords));
    });

    activeOrders.forEach(so => {
      if (!usedIds.has(so.id)) items.push(so);
    });
    return items;
  }, [orders, manualGroups, groupTagOverrides]);

  const ordersByDate = useMemo(() => {
    const map = {};
    calendarItems.forEach(item => {
      const dateKey = normalizeDate(item.deadline);
      if (!dateKey) return;
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(item);
    });
    return map;
  }, [calendarItems]);

  // Weekly structure
  const weeks = useMemo(() => {
    const result = [];
    for (let w = 0; w < rangeWeeks; w++) {
      const weekStart = new Date(monday);
      weekStart.setDate(monday.getDate() + (w * 7));
      const days = [];
      for (let d = 0; d < 6; d++) {
        const dayDate = new Date(weekStart);
        dayDate.setDate(weekStart.getDate() + d);
        days.push({
          dayName: DAY_NAMES_SHORT[d],
          dateStr: dayDate.toISOString().split('T')[0],
          displayDate: dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          isToday: dayDate.toDateString() === today.toDateString(),
          isWeekend: d === 5
        });
      }
      result.push({
        weekNum: w,
        weekLabel: `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
        days
      });
    }
    return result;
  }, [monday, rangeWeeks]);

  // Monthly structure
  const monthView = useMemo(() => {
    const now = new Date();
    const targetMonth = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const year = targetMonth.getFullYear();
    const month = targetMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstOfMonth.getDay();
    const daysInMonth = lastOfMonth.getDate();
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(gridStart.getDate() - firstDayOfWeek);
    const totalDays = firstDayOfWeek + daysInMonth;
    const totalWeeks = Math.ceil(totalDays / 7);
    const weeks = [];
    const cursor = new Date(gridStart);
    for (let w = 0; w < totalWeeks; w++) {
      const days = [];
      for (let d = 0; d < 7; d++) {
        const dateStr = cursor.toISOString().split('T')[0];
        days.push({
          date: new Date(cursor),
          dateStr,
          dayNum: cursor.getDate(),
          isCurrentMonth: cursor.getMonth() === month,
          isToday: cursor.toDateString() === today.toDateString(),
          isWeekend: d === 0 || d === 6
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      weeks.push(days);
    }
    return {
      year, month,
      monthName: MONTH_NAMES[month],
      monthLabel: `${MONTH_NAMES[month]} ${year}`,
      weeks
    };
  }, [monthOffset]);

  /*
  const listViewData = useMemo(() => {
    const todayStr = today.toISOString().split('T')[0];
    const sortedDates = Object.keys(ordersByDate).sort();
    return sortedDates.map(dateStr => ({
      dateStr,
      displayLabel: new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'long', month: 'short', day: 'numeric', year: 'numeric'
      }),
      isToday: dateStr === todayStr,
      isPast: dateStr < todayStr,
      items: ordersByDate[dateStr]
    }));
  }, [ordersByDate]);
  */

  const rangeEndDate = new Date(monday);
  rangeEndDate.setDate(monday.getDate() + (rangeWeeks * 7) - 1);
  const gridColsClass = rangeWeeks === 1 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-3';

  // Selection helpers
  const allVisibleOrderIds = useMemo(() => {
    const ids = [];
    Object.values(ordersByDate).forEach(dayItems => {
      dayItems.forEach(item => {
        if (item.isGroupRow) item.childOrderIds.forEach(cid => ids.push(cid));
        else ids.push(item.id);
      });
    });
    return ids;
  }, [ordersByDate]);

  const isItemFullySelected = (item) => {
    if (item.isGroupRow) {
      return item.childOrderIds.length > 0 &&
        item.childOrderIds.every(cid => selectedCalendarIds.includes(cid));
    }
    return selectedCalendarIds.includes(item.id);
  };

  const toggleItemSelection = (item, e) => {
    e?.stopPropagation();
    if (item.isGroupRow) {
      const allChildSelected = item.childOrderIds.every(cid => selectedCalendarIds.includes(cid));
      if (allChildSelected) {
        setSelectedCalendarIds(prev => prev.filter(id => !item.childOrderIds.includes(id)));
      } else {
        setSelectedCalendarIds(prev => Array.from(new Set([...prev, ...item.childOrderIds])));
      }
    } else {
      setSelectedCalendarIds(prev =>
        prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
      );
    }
  };

  const handleSelectAllCalendar = () => {
    if (allVisibleOrderIds.length === 0) return;
    const isAllSelected = allVisibleOrderIds.every(id => selectedCalendarIds.includes(id));
    setSelectedCalendarIds(isAllSelected ? [] : allVisibleOrderIds);
  };

  const handleClearSelection = () => setSelectedCalendarIds([]);
  const handleBulkEditClick = () => {
    if (selectedCalendarIds.length === 0) return;
    if (onOpenBulkEdit) onOpenBulkEdit(selectedCalendarIds);
  };

  useEffect(() => { setSelectedCalendarIds([]); }, [rangeWeeks, weekOffset, monthOffset, viewMode]);

  const handlePrev = () => {
    if (viewMode === 'month') setMonthOffset(prev => prev - 1);
    else onOffsetChange(weekOffset - 1);
  };
  const handleNext = () => {
    if (viewMode === 'month') setMonthOffset(prev => prev + 1);
    else onOffsetChange(weekOffset + 1);
  };
  const handleToday = () => {
    if (viewMode === 'month') setMonthOffset(0);
    else onOffsetChange(0);
  };

  const headerLabel = viewMode === 'month'
    ? monthView.monthLabel
    : `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${rangeEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  // Clean helper to format numbers without floating-point precision bugs (e.g. 0.30000000000000004 -> 0.3)
  const formatNum = (val) => {
    const n = Number(val) || 0;
    return Number(n.toFixed(2)).toString(); // e.g. 0.30000000000000004 -> "0.3", 11.00 -> "11"
  };

  // ════════════════════════════════════════════════════════════════
  // REUSABLE PROGRESS BAR COMPONENT
  // ════════════════════════════════════════════════════════════════
  const ProgressBlock = ({ label, right, currentNum, totalNum, pct, unitLabel = '', barHeight = 'h-1.5' }) => {
    const isComplete = pct >= 100 && totalNum > 0;
    totalNum = formatNum(totalNum);
    return (
      <div className="flex flex-col gap-1">
        <div className="flex justify-between items-center text-[10px] font-mono gap-2">
          <span className="font-semibold text-gray-800">{label}</span>
          {right && (
            <span className="text-emerald-700 font-bold truncate max-w-[110px]" title={typeof right === 'string' ? right : ''}>
              {right}
            </span>
          )}
        </div>
        <div className={`w-full bg-gray-100 rounded-full ${barHeight} overflow-hidden border border-gray-200`}>
          <div
            className={`h-full transition-all duration-500 rounded-full ${
              isComplete ? 'bg-emerald-500' : pct > 0 ? 'bg-blue-500' : 'bg-transparent'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between items-center text-[9px] font-mono">
          <span className="text-gray-500 font-semibold">
            {currentNum}/{totalNum}{unitLabel ? ` ${unitLabel}` : ''}
          </span>
          <span className={isComplete ? 'text-emerald-600 font-bold' : currentNum > 0 ? 'text-amber-600 font-bold' : 'text-gray-400 font-bold'}>
            {isComplete ? 'COMPLETE' : currentNum > 0 ? `${pct}%` : '0%'}
          </span>
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // CARD RENDERER — synced with OrderView
  // ════════════════════════════════════════════════════════════════
  const renderOrderCard = (item, compact = false) => {
    const isGroup = item.isGroupRow;
    const badges = !isGroup ? getStatusBadges(item) : [];
    const isSelected = isItemFullySelected(item);
    const invProgress = getInvoiceProgress(item);
    const packProgress = getPackedProgress(item);
    // const shipLabel = getShippingLabel(item);

    if (compact) {
      return (
        <div
          key={item.id}
          className={`relative flex items-center gap-1 rounded border transition-all cursor-pointer group ${
            isSelected
              ? 'border-indigo-500 ring-1 ring-indigo-500/40 bg-indigo-50/60'
              : isGroup
                ? 'bg-indigo-50 border-indigo-300 hover:border-indigo-500 border-l-2 border-l-indigo-500'
                : 'bg-white border-gray-200 hover:border-indigo-300'
          }`}
          title={isGroup ? `${item.customer || 'Family'} · ${item.soNumber || item.poTagNumber} · ${formatCurrency(item.amount)}` : `${item.soNumber || 'Sales Order'}\n${getOrderHoverText(item)}`}
        >
          {permissions && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => toggleItemSelection(item, e)}
              onClick={(e) => e.stopPropagation()}
              className="rounded border-gray-300 text-indigo-600 cursor-pointer w-3 h-3 ml-1 shrink-0"
            />
          )}
          <button
            type="button"
            onClick={() => (isGroup ? setSelectedGroup(item) : setSelectedOrder(item))}
            className="flex-1 min-w-0 text-left px-1 py-0.5 flex items-center gap-1"
          >
            {isGroup && <Package size={8} className="text-indigo-600 shrink-0" />}
            <span className="font-mono font-bold text-indigo-700 text-[9px] truncate">
              {isGroup ? item.poTagNumber : item.soNumber}
            </span>
            {item.cancelDate && <AlertTriangle size={8} className="text-rose-500 shrink-0" />}
          </button>

          {invProgress.invoiced > 0 && (
            <span
              className={`text-[7px] font-bold px-1 py-0.5 rounded shrink-0 mr-0.5 border ${
                invProgress.pct >= 100
                  ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                  : 'bg-blue-50 text-blue-600 border-blue-200'
              }`}
              title={isGroup ? `Invoiced: ${invProgress.invoiced}/${invProgress.total}` : `Invoice: ${invProgress.invoiceNumber}`}
            >
              {isGroup ? `INV ${invProgress.invoiced}/${invProgress.total}` : invProgress.invoiceNumber}
            </span>
          )}
        </div>
      );
    }

    // ─── Full card ───
    return (
      <div
        key={item.id}
        title={isGroup ? `${item.customer || 'Family'} · ${item.poTagNumber || ''}` : `${item.soNumber || 'Sales Order'}\n${getOrderHoverText(item)}`}
        className={`relative flex items-stretch gap-1 rounded border transition-all ${
          isSelected
            ? 'border-indigo-500 ring-2 ring-indigo-500/30 bg-indigo-50/40'
            : isGroup
              ? 'bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-300 hover:border-indigo-500 hover:shadow-md border-l-4 border-l-indigo-500'
              : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-xs hover:bg-indigo-50/30'
        }`}
      >
        {permissions && (
          <div
            onClick={(e) => toggleItemSelection(item, e)}
            className="flex items-start pt-1.5 pl-1.5 cursor-pointer shrink-0"
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => toggleItemSelection(item, e)}
              onClick={(e) => e.stopPropagation()}
              className="rounded border-gray-300 text-indigo-600 cursor-pointer w-3.5 h-3.5"
            />
          </div>
        )}

        <button
          type="button"
          onClick={() => (isGroup ? setSelectedGroup(item) : setSelectedOrder(item))}
          className="flex-1 min-w-0 text-left p-1.5 text-[10px] space-y-0.5 cursor-pointer bg-transparent"
        >
          <div className="flex justify-between items-center gap-1">
            <span className="font-mono font-bold text-indigo-700 truncate">
              {isGroup ? (
                <span className="inline-flex items-center gap-1">
                  <Package size={9} /> {item.poTagNumber}
                </span>
              ) : item.soNumber}
            </span>
            {isGroup ? (
              <span className="px-1 py-0.5 text-[8px] rounded font-bold bg-indigo-600 text-white shrink-0">
                {item.childCount} SOs
              </span>
            ) : (
              badges.length > 0 && (
                <span className={`px-1 py-0.5 text-[8px] rounded font-bold border ${badges[0].class} shrink-0`}>
                  {badges[0].label}
                </span>
              )
            )}
          </div>

          <div className="text-gray-700 font-semibold truncate">{item.customer}</div>

          {item.cancelDate && (
            <div className="flex items-center gap-1 text-[9px] text-rose-600 font-bold py-0.5 mt-1 self-start">
              <AlertTriangle size={10} className="shrink-0 text-rose-500" />
              <span>Cancel: {formatDateDisplay(item.cancelDate)}</span>
            </div>
          )}

          <div className="flex justify-between text-gray-500">
            <span className="truncate">Tag: {item.poTagNumber || '-'}</span>
            <span className="truncate">
              {isGroup ? `${item.assignedTo}` : (item.assignedTo || 'Unassigned')}
            </span>
            <span className="font-bold text-gray-800">
              {formatCurrency(item.amount).replace('.00', '')}
            </span>
          </div>

          {/* PACKED STATUS */}
          <div className="mt-2 pt-1.5 border-t border-gray-100/80">
            <ProgressBlock
              label="PACKED"
              currentNum={packProgress.packed}
              totalNum={packProgress.total}
              pct={packProgress.pct}
              unitLabel={isGroup ? '' : 'un'}
            />
          </div>

          {/* INVOICED STATUS */}
          
          <div className="mt-1.5">
            <ProgressBlock
              label="INVOICED"
              right={!isGroup ? (invProgress.invoiceNumber || null) : null}
              currentNum={invProgress.invoiced}
              totalNum={invProgress.total}
              pct={invProgress.pct}
              unitLabel={isGroup ? 'SOs' : ''}
            />
          </div>          

          {/* Shipping status chip — matches OrderView label */}
          {/*
          <div className="mt-1.5 flex justify-between items-center text-[9px]">
            <span className="font-semibold text-gray-500 uppercase tracking-wider">Shipping</span>
            <span className={`px-1.5 py-0.5 rounded font-bold border ${
              /shipped|delivered/i.test(shipLabel)
                ? 'bg-purple-50 text-purple-700 border-purple-200'
                : /ready/i.test(shipLabel)
                  ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                  : /transit|partial/i.test(shipLabel)
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-gray-50 text-gray-600 border-gray-200'
            }`}>
              {shipLabel}
            </span>
          </div>
          */}
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-4">
        {/* ── Top controls ── */}
        <div className="no-print bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={handlePrev} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <span className="font-bold text-sm text-gray-900 min-w-[180px] text-center">
              {headerLabel}
            </span>
            <button onClick={handleNext} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <ChevronRight size={16} />
            </button>
            <button onClick={handleToday} className="text-xs bg-indigo-50 text-indigo-700 font-bold px-3 py-1.5 rounded-lg hover:bg-indigo-100 border border-indigo-200 transition-all">
              Today
            </button>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={() => window.print()} className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center gap-1.5">
              Print Calendar
            </button>
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1.5 text-xs font-bold transition-colors flex items-center gap-1.5 ${viewMode === 'week' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <Layers size={12} /> Weekly
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1.5 text-xs font-bold transition-colors flex items-center gap-1.5 border-l border-gray-200 ${viewMode === 'month' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <CalendarIcon size={12} /> Monthly
              </button>
            </div>

            {viewMode === 'week' && (
              <>
                <span className="text-xs font-semibold text-gray-600">Weeks:</span>
                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                  {CALENDAR_RANGES.map(r => (
                    <button key={r.value} onClick={() => onRangeChange(r.value)} className={`px-3 py-1.5 text-xs font-bold transition-colors ${rangeWeeks === r.value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Floating bulk bar ── */}
        {permissions && selectedCalendarIds.length > 0 && (
          <div className="no-print sticky top-2 z-30 bg-white p-3 rounded-xl border-2 border-indigo-300 shadow-lg flex flex-wrap items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
            <span className="text-xs text-indigo-700 font-bold bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded">
              {selectedCalendarIds.length} Order{selectedCalendarIds.length === 1 ? '' : 's'} Selected
            </span>
            <button onClick={handleBulkEditClick} className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors">
              <Truck size={12} /> Bulk Edit
            </button>
            <button onClick={handleClearSelection} className="text-gray-500 hover:text-gray-800 p-1.5 rounded transition-colors ml-auto flex items-center gap-1 text-xs font-bold">
              <X size={14} /> Clear
            </button>
          </div>
        )}

        <style>{`
          .print-only { display: none; }
          @page { size: letter landscape; margin: 0.3in; }
          @media print {
            html, body { width: 100%; height: auto; background: #fff !important; }
            body * { visibility: hidden !important; }
            .calendar-print-area, .calendar-print-area * { visibility: visible !important; }
            .calendar-print-area { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; margin: 0 !important; padding: 0 !important; }
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            .calendar-print-area .overflow-y-auto, .calendar-print-area .overflow-hidden { overflow: visible !important; }
            .calendar-print-area .min-h-300, .calendar-print-area .min-h-220, .calendar-print-area .min-h-160 { min-height: 0 !important; }
            .calendar-print-area .shadow-xs, .calendar-print-area .shadow-sm, .calendar-print-area .shadow-md { box-shadow: none !important; }
            .calendar-print-area button { color: #111827 !important; }
            .calendar-print-area .text-white { color: #111827 !important; }
            .calendar-print-area .bg-indigo-600 { background: #e5e7eb !important; }
            .calendar-print-area .bg-indigo-50, .calendar-print-area .bg-blue-50 { background: #f3f4f6 !important; }
            .calendar-print-area .ring-2 { box-shadow: none !important; }
            .calendar-print-area .grid { break-inside: avoid; }
            .calendar-print-area .weekly-days-grid { grid-template-columns: repeat(6, minmax(0, 1fr)) !important; gap: 0.08in !important; }
            .calendar-print-area .monthly-calendar-grid { grid-template-columns: repeat(7, minmax(0, 1fr)) !important; }
            .calendar-print-area .weekly-day-card { min-height: 0 !important; }
            .calendar-print-area .rounded-xl { border-radius: 0 !important; }
          }
        `}</style>

        <div className="calendar-print-area">
          <div className="print-only mb-3 border-b-2 border-gray-800 pb-2 text-center">
            <div className="text-lg font-extrabold tracking-wide">OPERATIONS PLANNER</div>
            <div className="text-sm font-semibold">Calendar — {viewMode === 'month' ? monthView.monthLabel : headerLabel}</div>
            <div className="text-[10px] text-gray-600">Printed calendar · {viewMode === 'month' ? 'Monthly view' : `${rangeWeeks}-week view`}</div>
          </div>

        {/* ── Weekly view ── */}
        {viewMode === 'week' && (
          <div className="space-y-3">
            {weeks.map(week => (
              <div key={week.weekNum} className="space-y-2">
                {rangeWeeks > 1 && (
                  <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider px-1">{week.weekLabel}</div>
                )}
                <div className={`weekly-days-grid grid ${gridColsClass} gap-2`}>
                  {week.days.map(day => {
                    const dayItems = ordersByDate[day.dateStr] || [];
                    const dayTotal = dayItems.reduce((sum, o) => sum + (o.amount || 0), 0);
                    const dayCount = dayItems.reduce((sum, o) => sum + (o.isGroupRow ? o.childCount : 1), 0);
                    const minH = rangeWeeks === 4 ? 'min-h-[160px]' : rangeWeeks === 2 ? 'min-h-[220px]' : 'min-h-[300px]';
                    return (
                      <div key={day.dateStr} className={`bg-white rounded-lg border flex flex-col ${minH} overflow-visible ${day.isToday ? 'border-indigo-500 ring-2 ring-indigo-500/20' : day.isWeekend ? 'border-gray-200 bg-gray-50/50' : 'border-gray-200'}`}>
                        <div className={`px-2 py-1.5 border-b flex items-center justify-between ${day.isToday ? 'bg-indigo-600 text-white' : day.isWeekend ? 'bg-gray-100 text-gray-500' : 'bg-white text-gray-800'}`}>
                          <div>
                            <div className="text-[9px] uppercase font-bold tracking-wider opacity-80">{day.dayName}</div>
                            <div className="text-xs font-extrabold">{day.displayDate}</div>
                            {dayItems.some(isGfsFamilyItem) && (
                              <div className="text-[9px] font-extrabold tracking-wide">GFS {day.dateStr}</div>
                            )}
                          </div>
                          {dayItems.length > 0 && (
                            <div className="text-right">
                              <div className="text-[9px] font-bold opacity-80">{dayCount} SO</div>
                              <div className="text-[9px] font-bold opacity-70">{formatCurrency(dayTotal).replace('.00', '')}</div>
                            </div>
                          )}
                        </div>
                        <div className="p-1.5 space-y-1 flex-1 overflow-y-auto">
                          {dayItems.length === 0 ? (
                            <div className="text-center text-gray-300 text-[10px] py-4">—</div>
                          ) : (
                            dayItems.map(item => renderOrderCard(item, false))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Monthly view ── */}
        {viewMode === 'month' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
              {DAY_NAMES_FULL.map((day, idx) => (
                <div key={day} className={`px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-center ${idx === 0 || idx === 6 ? 'text-gray-400' : 'text-gray-600'}`}>
                  {day}
                </div>
              ))}
            </div>
            <div className="monthly-calendar-grid grid grid-cols-7">
              {monthView.weeks.map((week, wIdx) =>
                week.map((day, dIdx) => {
                  const dayItems = ordersByDate[day.dateStr] || [];
                  const dayTotal = dayItems.reduce((sum, o) => sum + (o.amount || 0), 0);
                  const dayCount = dayItems.reduce((sum, o) => sum + (o.isGroupRow ? o.childCount : 1), 0);
                  const maxVisible = 3;
                  const hiddenCount = dayItems.length - maxVisible;
                  return (
                    <div
                      key={`${wIdx}-${dIdx}`}
                      className={`min-h-[120px] border-b border-r border-gray-200 p-1.5 flex flex-col ${
                        !day.isCurrentMonth ? 'bg-gray-50/60 text-gray-400'
                          : day.isToday ? 'bg-indigo-50/50 ring-2 ring-indigo-500 ring-inset'
                            : day.isWeekend ? 'bg-slate-50/40' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-bold ${
                          day.isToday ? 'bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center'
                            : !day.isCurrentMonth ? 'text-gray-400' : 'text-gray-800'
                        }`}>
                          {day.dayNum}
                        </span>
                        {dayItems.some(isGfsFamilyItem) && day.isCurrentMonth && (
                          <span className="text-[8px] font-extrabold text-indigo-700">GFS {day.dateStr}</span>
                        )}
                        {dayItems.length > 0 && day.isCurrentMonth && (
                          <span className="text-[9px] font-bold text-gray-500">
                            {dayCount} · {formatCurrency(dayTotal).replace('.00', '')}
                          </span>
                        )}
                      </div>
                      <div className="space-y-0.5 flex-1 overflow-hidden">
                        {dayItems.slice(0, maxVisible).map(item => renderOrderCard(item, true))}
                        {hiddenCount > 0 && (
                          <button
                            onClick={() => {
                              const nextItem = dayItems[maxVisible];
                              if (nextItem?.isGroupRow) setSelectedGroup(nextItem);
                              else setSelectedOrder(nextItem);
                            }}
                            className="w-full text-[9px] font-bold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded px-1 py-0.5 text-left transition-colors"
                          >
                            +{hiddenCount} more
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          INDIVIDUAL SO MODAL
          ═══════════════════════════════════════════════════════════ */}
      {selectedOrder && (() => {
        //const inv = getInvoiceProgress(selectedOrder);
        const pack = getPackedProgress(selectedOrder);
        //const shipLabel = getShippingLabel(selectedOrder);
        const orderPLs = getPackingLists(selectedOrder);
        const soDownloadKey = `so-${selectedOrder.id}-${selectedOrder.soNumber || ''}`;

        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-5" role="dialog" aria-modal="true" onMouseDown={e => e.target === e.currentTarget && setSelectedOrder(null)}>
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-xs" />
            <div className="relative w-full max-w-2xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-white flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg sm:text-xl font-extrabold text-gray-900">Sales Order {selectedOrder.soNumber}</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Complete order information</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <button
                    onClick={() => handleErpagDownload('so', selectedOrder)}
                    disabled={downloadingDoc === soDownloadKey}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-bold shadow-sm transition-colors flex items-center gap-1.5 cursor-pointer"
                    title="Download Sales Order from ERPAG"
                  >
                    {downloadingDoc === soDownloadKey ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    Download SO
                  </button>
                  {orderPLs.length <= 1 ? (
                    <button
                      onClick={() => handleErpagDownload('pl', selectedOrder, orderPLs[0] || '')}
                      disabled={downloadingDoc === `pl-${selectedOrder.id}-${orderPLs[0] || selectedOrder.soNumber || ''}`}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-bold shadow-sm transition-colors flex items-center gap-1.5 cursor-pointer"
                      title="Download Packing List from ERPAG"
                    >
                      {downloadingDoc === `pl-${selectedOrder.id}-${orderPLs[0] || selectedOrder.soNumber || ''}` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                      Download Packing List
                    </button>
                  ) : (
                    orderPLs.map((plNum, idx) => {
                      const plKey = `pl-${selectedOrder.id}-${plNum}`;
                      return (
                        <button
                          key={idx}
                          onClick={() => handleErpagDownload('pl', selectedOrder, plNum)}
                          disabled={downloadingDoc === plKey}
                          className="px-2.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-bold shadow-sm transition-colors flex items-center gap-1 cursor-pointer"
                          title={`Download Packing List ${plNum} from ERPAG`}
                        >
                          {downloadingDoc === plKey ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                          PL: {plNum}
                        </button>
                      );
                    })
                  )}
                  {permissions && onOpenBulkEdit && (
                    <button onClick={() => { onOpenBulkEdit([selectedOrder.id]); setSelectedOrder(null); }} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold shadow-sm transition-colors flex items-center gap-1.5">
                      <Truck size={12} /> Edit
                    </button>
                  )}
                  <button onClick={() => setSelectedOrder(null)} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"><X size={20} /></button>
                </div>
              </div>

              <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-[11px] font-bold text-gray-500 mb-2 uppercase">Status</div>
                  <div className="flex flex-wrap gap-1">
                    {getStatusBadges(selectedOrder).map((b, idx) => (
                      <span key={idx} className={`inline-flex px-2 py-0.5 rounded-md text-xs font-bold border ${b.class}`}>{b.label}</span>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                  <div className="text-[11px] font-bold text-indigo-500 mb-1 uppercase">Order Total</div>
                  <div className="text-xl font-extrabold text-indigo-700">{formatCurrency(selectedOrder.amount)}</div>
                </div>
              </div>

                {/* Progress cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <ProgressBlock
                      label="PL#"
                      currentNum={pack.packed}
                      totalNum={pack.total}
                      pct={pack.pct}
                      unitLabel="un"
                      barHeight="h-2"
                    />
                  </div>
                  {/*
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <ProgressBlock
                      label="INVOICED STATUS"
                      right={inv.invoiceNumber || null}
                      currentNum={inv.invoiced}
                      totalNum={inv.total}
                      pct={inv.pct}
                      barHeight="h-2"
                    />
                  </div>
                  */}
                </div>

                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200"><h3 className="text-sm font-extrabold">Customer Information</h3></div>
                  <div className="p-4 grid grid-cols-2 gap-4">
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Customer</div><div className="text-sm font-semibold">{selectedOrder.customer}</div></div>
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Tag</div><div className="text-sm font-semibold font-mono">{selectedOrder.poTagNumber || '—'}</div></div>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200"><h3 className="text-sm font-extrabold">Order Logistics</h3></div>
                  <div className="p-4 grid grid-cols-2 gap-x-5 gap-y-4">
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Order Date</div><div className="text-sm font-semibold">{formatDateDisplay(selectedOrder.soDate)}</div></div>
                    {selectedOrder.cancelDate && (
                      <div>
                        <div className="text-[11px] font-bold text-gray-400 uppercase">Cancel Date</div>
                        <div className="text-sm font-semibold text-rose-600">{formatDateDisplay(selectedOrder.cancelDate)}</div>
                      </div>
                    )}
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Deadline</div><div className="text-sm font-semibold">{formatDateDisplay(selectedOrder.deadline)}</div></div>
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Assigned To</div><div className="text-sm font-semibold">{selectedOrder.assignedTo || 'Unassigned'}</div></div>
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Shipping Method</div><div className="text-sm font-semibold">{selectedOrder.pickupOrShip}</div></div>
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">ETA</div><div className="text-sm font-semibold">{formatDateDisplay(selectedOrder.eta)}</div></div>
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Departure</div><div className="text-sm font-semibold">{formatDateDisplay(selectedOrder.departureDate)}</div></div>
                  </div>
                </div>
              </div>

              <div className="px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button onClick={() => setSelectedOrder(null)} className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-bold shadow-sm hover:bg-indigo-700 transition-colors">Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════
          FAMILY SO MODAL
          ═══════════════════════════════════════════════════════════ */}
      {selectedGroup && (() => {
        //const groupInv = getInvoiceProgress(selectedGroup);
        //const groupPack = getPackedProgress(selectedGroup);

        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-5" role="dialog" aria-modal="true" onMouseDown={e => e.target === e.currentTarget && setSelectedGroup(null)}>
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-xs" />
            <div className="relative w-full max-w-2xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Package size={20} className="text-indigo-600" />
                    <h2 className="text-lg sm:text-xl font-extrabold text-gray-900">Family {selectedGroup.poTagNumber}</h2>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1 font-medium">
                    {selectedGroup.childCount} Orders · Total {formatCurrency(selectedGroup.amount)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {permissions && onOpenBulkEdit && (
                    <button onClick={() => { onOpenBulkEdit(selectedGroup.childOrderIds); setSelectedGroup(null); }} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold shadow-sm transition-colors flex items-center gap-1.5">
                      <Truck size={12} /> Bulk Edit All
                    </button>
                  )}
                  <button onClick={() => setSelectedGroup(null)} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"><X size={20} /></button>
                </div>
              </div>

              {/* Family-wide progress summary */}
              <div className="px-4 sm:px-6 py-3 bg-slate-50 border-b border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
                { /*
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <ProgressBlock
                    label="PL#"
                    currentNum={groupPack.packed}
                    totalNum={groupPack.total}
                    pct={groupPack.pct}
                    unitLabel="un"
                    barHeight="h-2"
                  />
                </div>
                
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <ProgressBlock
                    label="FAMILY INVOICED"
                    currentNum={groupInv.invoiced}
                    totalNum={groupInv.total}
                    pct={groupInv.pct}
                    unitLabel="SOs"
                    barHeight="h-2"
                  />
                </div>
                */}
                
              </div>
             

              <div className="overflow-y-auto flex-1 p-3 sm:p-5 space-y-3 bg-slate-50/50">
                {selectedGroup.childOrders.map(child => {
                  const badges = getStatusBadges(child);
                  // const childInv = getInvoiceProgress(child);
                  const childPack = getPackedProgress(child);
                  const childShip = getShippingLabel(child);
                  const childPLs = getPackingLists(child);
                  const childSoKey = `so-${child.id}-${child.soNumber || ''}`;

                  return (
                    <div key={child.id} className="bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-2 transition-all hover:border-indigo-300">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-bold text-indigo-700 text-sm sm:text-base">{child.soNumber}</span>
                            {badges.length > 0 && <span className={`px-1.5 py-0.5 text-[9px] rounded font-bold border ${badges[0].class}`}>{badges[0].label}</span>}
                            <span className={`px-1.5 py-0.5 text-[9px] rounded font-bold border ${
                              /shipped|delivered/i.test(childShip) ? 'bg-purple-50 text-purple-700 border-purple-200'
                              : /ready/i.test(childShip) ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                              : /transit|partial/i.test(childShip) ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-gray-50 text-gray-600 border-gray-200'
                            }`}>
                              {childShip}
                            </span>
                          </div>
                          <div className="text-xs text-gray-700 font-semibold mt-0.5">{child.customer}</div>
                          {child.cancelDate && (
                            <div className="flex items-center gap-1 text-[9px] text-rose-600 font-bold bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200 mt-1.5 w-fit">
                              <AlertTriangle size={10} className="shrink-0 text-rose-500" />
                              <span>Cancel: {formatDateDisplay(child.cancelDate)}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-start gap-1.5 shrink-0 flex-wrap justify-end">
                          <div className="text-right mr-1">
                            <div className="font-extrabold text-gray-900 text-sm">{formatCurrency(child.amount)}</div>
                          </div>
                          <button
                            onClick={() => handleErpagDownload('so', child)}
                            disabled={downloadingDoc === childSoKey}
                            className="px-2 py-1 rounded bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-[9px] font-bold transition-colors flex items-center gap-1 cursor-pointer disabled:opacity-60"
                            title={`Download SO ${child.soNumber} from ERPAG`}
                          >
                            {downloadingDoc === childSoKey ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                            SO
                          </button>
                          {childPLs.length <= 1 ? (
                            <button
                              onClick={() => handleErpagDownload('pl', child, childPLs[0] || '')}
                              disabled={downloadingDoc === `pl-${child.id}-${childPLs[0] || child.soNumber || ''}`}
                              className="px-2 py-1 rounded bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[9px] font-bold transition-colors flex items-center gap-1 cursor-pointer disabled:opacity-60"
                              title="Download Packing List from ERPAG"
                            >
                              {downloadingDoc === `pl-${child.id}-${childPLs[0] || child.soNumber || ''}` ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                              PL
                            </button>
                          ) : (
                            childPLs.map((plNum, pIdx) => {
                              const cPlKey = `pl-${child.id}-${plNum}`;
                              return (
                                <button
                                  key={pIdx}
                                  onClick={() => handleErpagDownload('pl', child, plNum)}
                                  disabled={downloadingDoc === cPlKey}
                                  className="px-2 py-1 rounded bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[9px] font-bold transition-colors flex items-center gap-1 cursor-pointer disabled:opacity-60"
                                  title={`Download Packing List ${plNum} from ERPAG`}
                                >
                                  {downloadingDoc === cPlKey ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                                  {plNum}
                                </button>
                              );
                            })
                          )}
                          {permissions && onOpenBulkEdit && (
                            <button onClick={() => { onOpenBulkEdit([child.id]); setSelectedGroup(null); }} className="px-2 py-1 rounded bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 text-[9px] font-bold transition-colors">
                              Edit
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-2 text-[10px] text-gray-500 bg-gray-50 p-2.5 rounded-lg border border-gray-100 mt-1">
                        <div className="flex items-center gap-1.5">
                          <span className="uppercase font-bold tracking-wider text-gray-400">Tag:</span>
                          <span className="font-mono font-bold text-gray-700 max-w-[120px] truncate">{child.poTagNumber || child.tag || '—'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 border-l-0 sm:border-l border-gray-200 sm:pl-4">
                          <span className="uppercase font-bold tracking-wider text-gray-400">Due:</span>
                          <span className="font-medium text-gray-700">{formatDateDisplay(child.deadline)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 border-l-0 sm:border-l border-gray-200 sm:pl-4">
                          <span className="uppercase font-bold tracking-wider text-gray-400">Staff:</span>
                          <span className="font-medium text-gray-700 max-w-[100px] truncate">{child.assignedTo || 'Unassigned'}</span>
                        </div>
                      </div>

                      {/* Per-child progress — matches OrderView 1:1 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1 pt-2 border-t border-gray-100">
                        <ProgressBlock
                          label="PL#"
                          currentNum={childPack.packed}
                          totalNum={childPack.total}
                          pct={childPack.pct}
                          unitLabel="un"
                        />
                        {/*
                        <ProgressBlock
                          label="INVOICED"
                          right={childInv.invoiceNumber || null}
                          currentNum={childInv.invoiced}
                          totalNum={childInv.total}
                          pct={childInv.pct}
                        />
                        */}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button onClick={() => setSelectedGroup(null)} className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-bold shadow-sm hover:bg-indigo-700 transition-colors">Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

function CalendarViewBkp({
  orders,
  manualGroups = {},
  groupTagOverrides = {},
  rangeWeeks,
  weekOffset,
  onRangeChange,
  onOffsetChange,
  permissions,
  onOpenBulkEdit
}) {
  const DAY_NAMES_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const DAY_NAMES_FULL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState([]);

  const [viewMode, setViewMode] = useState('week');
  const [monthOffset, setMonthOffset] = useState(0);

  const today = new Date();
  const monday = getMonday(today);
  monday.setDate(monday.getDate() + (weekOffset * 7));

  // ════════════════════════════════════════════════════════════════
  // SHARED DATA READERS — match OrderView logic 1:1
  // ════════════════════════════════════════════════════════════════

  const getInvoiceNumber = (so) => {
    if (!so) return '';
    const candidates = [
      so.invoiceNumber,
      so._raw?.invoiceNumber,
      so.invoice,
      so._raw?.invoice,
    ];
    for (const c of candidates) {
      const s = c != null ? String(c).trim() : '';
      if (s && s.toLowerCase() !== 'n/a' && s !== '-') return s;
    }
    return '';
  };

  const getPackingLists = (so) => {
    const raw = so?.packingLists || so?._raw?.packingLists || '';
    return String(raw).split(',').map(x => x.trim()).filter(Boolean);
  };

  const getPackedUnitsObj = (so) => so?.packedUnits || so?._raw?.packedUnits || {};

  const getSOUnits = (so) => Number(so?.units) || 0;

  const getShippingLabel = (so) => {
    const s = (so?.shippingStatus || '').trim();
    return s || 'Pending';
  };


  // GFS family labels should be visible alongside the date in calendar views.
  const isGfsFamilyItem = (item) => Boolean(
    item?.isGroupRow && /\bGFS\b|Gordon Food Service/i.test(
      `${item.poTagNumber || ''} ${item.customer || ''} ${item.groupKey || ''}`
    )
  );

  const getOrderHoverText = (item) =>
    `Customer: ${item?.customer || '—'}\nTag: ${item?.poTagNumber || item?.tag || '—'}`;

  /**
   * Invoice progress — matches OrderView Col M
   * - Family: X children with invoice# / total children
   * - Individual: 0/1 or 1/1
   */
  const getInvoiceProgress = (item) => {
    if (item?.isGroupRow) {
      const children = item.childOrders || [];
      const total = children.length;
      const invoiced = children.filter((c) => getInvoiceNumber(c) !== '').length;
      const pct = total > 0 ? Math.round((invoiced / total) * 100) : 0;
      return { total, invoiced, pct, invoiceNumber: null };
    }
    const invoiceNumber = getInvoiceNumber(item);
    const has = invoiceNumber !== '';
    return { total: 1, invoiced: has ? 1 : 0, pct: has ? 100 : 0, invoiceNumber };
  };

  /**
   * Packed progress — matches OrderView Col K
   * - Family: sum packed units across all children × all PLs / sum children units
   * - Individual: sum packed units across its PLs / its units
   */
  const getPackedProgress = (item) => {
    if (item?.isGroupRow) {
      const children = item.childOrders || [];
      const totalUnits = Number(item?.units) ||
        children.reduce((s, c) => s + getSOUnits(c), 0) || 0;
      let packed = 0;
      children.forEach((c) => {
        const pls = getPackingLists(c);
        const obj = getPackedUnitsObj(c);
        if (pls.length > 0) {
          pls.forEach(pl => { packed += Number(obj[pl]) || 0; });
        } else {
          packed += Number(c.totalPackedUnits) || 0;
        }
      });
      const pct = totalUnits > 0
        ? Math.min(Math.round((packed / totalUnits) * 1000) / 10, 100)
        : 0;
      return { total: totalUnits, packed, pct };
    }

    const totalUnits = getSOUnits(item);
    const pls = getPackingLists(item);
    const obj = getPackedUnitsObj(item);
    const packed = pls.length > 0
      ? pls.reduce((s, pl) => s + (Number(obj[pl]) || 0), 0)
      : Number(item?.totalPackedUnits) || 0;
    const pct = totalUnits > 0
      ? Math.min(Math.round((packed / totalUnits) * 1000) / 10, 100)
      : 0;
    return { total: totalUnits, packed, pct };
  };

  // ════════════════════════════════════════════════════════════════
  // FAMILY GROUPING — unchanged logic, now runs on merged orders
  // ════════════════════════════════════════════════════════════════
  const calendarItems = useMemo(() => {
    const items = [];
    const usedIds = new Set();
    const activeOrders = orders.filter(o => !o.completed && !o.isReturned);

    const pickEarliestDeadline = (ords) => {
      const dates = ords.map(o => normalizeDate(o.deadline)).filter(Boolean).sort();
      return dates[0] || '';
    };
    const pickEarliestcancelDate = (ords) => {
      const dates = ords.map(o => normalizeDate(o.cancelDate)).filter(Boolean).sort();
      return dates[0] || '';
    };

    const buildGroupEntry = (groupKey, type, parentPO, ords) => {
      const earliest = pickEarliestDeadline(ords);
      const cancelDate = pickEarliestcancelDate(ords);
      const totalAmount = ords.reduce((s, o) => s + (o.amount || 0), 0);
      const totalUnits = ords.reduce((s, o) => s + (o.units || 0), 0);
      const first = ords[0];
      const assignedSet = new Set(ords.map(o => (o.assignedTo || '').trim()).filter(Boolean));
      const assignedLabel = assignedSet.size === 0 ? 'Unassigned'
        : assignedSet.size === 1 ? [...assignedSet][0] : 'Mixed';

      return {
        id: groupKey, isGroupRow: true, groupKey, groupType: type,
        soNumber: parentPO, poTagNumber: parentPO,
        customer: first.customer, deadline: earliest, cancelDate,
        amount: totalAmount, units: totalUnits, assignedTo: assignedLabel,
        childOrders: ords, childCount: ords.length,
        childOrderIds: ords.map(o => o.id)
      };
    };

    Object.values(manualGroups).forEach(mg => {
      const ords = activeOrders.filter(o => mg.orderIds.includes(o.id));
      if (ords.length === 0) return;
      ords.forEach(o => usedIds.add(o.id));
      const groupKey = `manual-${mg.id}`;
      const parentPO = groupTagOverrides[groupKey] || mg.parentPO || 'FAMILY';
      items.push(buildGroupEntry(groupKey, 'manual', parentPO, ords));
    });

    const autoBuckets = {};
    activeOrders.forEach(so => {
      if (usedIds.has(so.id)) return;
      const meta = getAutoGroupMeta(so);
      if (!meta) return;
      if (!autoBuckets[meta.key]) autoBuckets[meta.key] = { ords: [], meta };
      autoBuckets[meta.key].ords.push(so);
    });

    Object.entries(autoBuckets).forEach(([bucketKey, bucket]) => {
      const ords = bucket.ords;
      if (ords.length < 2) return;
      ords.forEach(o => usedIds.add(o.id));
      const groupKey = bucketKey;
      let parentPO = groupTagOverrides[groupKey] || bucket.meta.familyName;

      if (!parentPO && bucket.meta.key.startsWith('auto-mm::')) {
        const cores = ords.map(o => {
          const m = (o.poTagNumber || o.tag || '').match(/(\d{6})/);
          return m ? m[1] : null;
        }).filter(Boolean).sort();
        if (cores.length > 0) {
          const min = cores[0], max = cores[cores.length - 1];
          parentPO = min === max ? min : `${min}-${max}`;
        }
      }
      if (!parentPO) parentPO = ords[0].poTagNumber || ords[0].soDate || 'FAMILY';
      items.push(buildGroupEntry(groupKey, 'auto', parentPO, ords));
    });

    activeOrders.forEach(so => {
      if (!usedIds.has(so.id)) items.push(so);
    });
    return items;
  }, [orders, manualGroups, groupTagOverrides]);

  const ordersByDate = useMemo(() => {
    const map = {};
    calendarItems.forEach(item => {
      const dateKey = normalizeDate(item.deadline);
      if (!dateKey) return;
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(item);
    });
    return map;
  }, [calendarItems]);

  // Weekly structure
  const weeks = useMemo(() => {
    const result = [];
    for (let w = 0; w < rangeWeeks; w++) {
      const weekStart = new Date(monday);
      weekStart.setDate(monday.getDate() + (w * 7));
      const days = [];
      for (let d = 0; d < 6; d++) {
        const dayDate = new Date(weekStart);
        dayDate.setDate(weekStart.getDate() + d);
        days.push({
          dayName: DAY_NAMES_SHORT[d],
          dateStr: dayDate.toISOString().split('T')[0],
          displayDate: dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          isToday: dayDate.toDateString() === today.toDateString(),
          isWeekend: d === 5
        });
      }
      result.push({
        weekNum: w,
        weekLabel: `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
        days
      });
    }
    return result;
  }, [monday, rangeWeeks]);

  // Monthly structure
  const monthView = useMemo(() => {
    const now = new Date();
    const targetMonth = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const year = targetMonth.getFullYear();
    const month = targetMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstOfMonth.getDay();
    const daysInMonth = lastOfMonth.getDate();
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(gridStart.getDate() - firstDayOfWeek);
    const totalDays = firstDayOfWeek + daysInMonth;
    const totalWeeks = Math.ceil(totalDays / 7);
    const weeks = [];
    const cursor = new Date(gridStart);
    for (let w = 0; w < totalWeeks; w++) {
      const days = [];
      for (let d = 0; d < 7; d++) {
        const dateStr = cursor.toISOString().split('T')[0];
        days.push({
          date: new Date(cursor),
          dateStr,
          dayNum: cursor.getDate(),
          isCurrentMonth: cursor.getMonth() === month,
          isToday: cursor.toDateString() === today.toDateString(),
          isWeekend: d === 0 || d === 6
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      weeks.push(days);
    }
    return {
      year, month,
      monthName: MONTH_NAMES[month],
      monthLabel: `${MONTH_NAMES[month]} ${year}`,
      weeks
    };
  }, [monthOffset]);

  /*
  const listViewData = useMemo(() => {
    const todayStr = today.toISOString().split('T')[0];
    const sortedDates = Object.keys(ordersByDate).sort();
    return sortedDates.map(dateStr => ({
      dateStr,
      displayLabel: new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'long', month: 'short', day: 'numeric', year: 'numeric'
      }),
      isToday: dateStr === todayStr,
      isPast: dateStr < todayStr,
      items: ordersByDate[dateStr]
    }));
  }, [ordersByDate]);
  */

  const rangeEndDate = new Date(monday);
  rangeEndDate.setDate(monday.getDate() + (rangeWeeks * 7) - 1);
  const gridColsClass = rangeWeeks === 1 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-3';

  // Selection helpers
  const allVisibleOrderIds = useMemo(() => {
    const ids = [];
    Object.values(ordersByDate).forEach(dayItems => {
      dayItems.forEach(item => {
        if (item.isGroupRow) item.childOrderIds.forEach(cid => ids.push(cid));
        else ids.push(item.id);
      });
    });
    return ids;
  }, [ordersByDate]);

  const isItemFullySelected = (item) => {
    if (item.isGroupRow) {
      return item.childOrderIds.length > 0 &&
        item.childOrderIds.every(cid => selectedCalendarIds.includes(cid));
    }
    return selectedCalendarIds.includes(item.id);
  };

  const toggleItemSelection = (item, e) => {
    e?.stopPropagation();
    if (item.isGroupRow) {
      const allChildSelected = item.childOrderIds.every(cid => selectedCalendarIds.includes(cid));
      if (allChildSelected) {
        setSelectedCalendarIds(prev => prev.filter(id => !item.childOrderIds.includes(id)));
      } else {
        setSelectedCalendarIds(prev => Array.from(new Set([...prev, ...item.childOrderIds])));
      }
    } else {
      setSelectedCalendarIds(prev =>
        prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
      );
    }
  };

  const handleSelectAllCalendar = () => {
    if (allVisibleOrderIds.length === 0) return;
    const isAllSelected = allVisibleOrderIds.every(id => selectedCalendarIds.includes(id));
    setSelectedCalendarIds(isAllSelected ? [] : allVisibleOrderIds);
  };

  const handleClearSelection = () => setSelectedCalendarIds([]);
  const handleBulkEditClick = () => {
    if (selectedCalendarIds.length === 0) return;
    if (onOpenBulkEdit) onOpenBulkEdit(selectedCalendarIds);
  };

  useEffect(() => { setSelectedCalendarIds([]); }, [rangeWeeks, weekOffset, monthOffset, viewMode]);

  const handlePrev = () => {
    if (viewMode === 'month') setMonthOffset(prev => prev - 1);
    else onOffsetChange(weekOffset - 1);
  };
  const handleNext = () => {
    if (viewMode === 'month') setMonthOffset(prev => prev + 1);
    else onOffsetChange(weekOffset + 1);
  };
  const handleToday = () => {
    if (viewMode === 'month') setMonthOffset(0);
    else onOffsetChange(0);
  };

  const headerLabel = viewMode === 'month'
    ? monthView.monthLabel
    : `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${rangeEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  // Clean helper to format numbers without floating-point precision bugs (e.g. 0.30000000000000004 -> 0.3)
  const formatNum = (val) => {
    const n = Number(val) || 0;
    return Number(n.toFixed(2)).toString(); // e.g. 0.30000000000000004 -> "0.3", 11.00 -> "11"
  };

  // ════════════════════════════════════════════════════════════════
  // REUSABLE PROGRESS BAR COMPONENT
  // ════════════════════════════════════════════════════════════════
  const ProgressBlock = ({ label, right, currentNum, totalNum, pct, unitLabel = '', barHeight = 'h-1.5' }) => {
    const isComplete = pct >= 100 && totalNum > 0;
    totalNum = formatNum(totalNum);
    return (
      <div className="flex flex-col gap-1">
        <div className="flex justify-between items-center text-[10px] font-mono gap-2">
          <span className="font-semibold text-gray-800">{label}</span>
          {right && (
            <span className="text-emerald-700 font-bold truncate max-w-[110px]" title={typeof right === 'string' ? right : ''}>
              {right}
            </span>
          )}
        </div>
        <div className={`w-full bg-gray-100 rounded-full ${barHeight} overflow-hidden border border-gray-200`}>
          <div
            className={`h-full transition-all duration-500 rounded-full ${
              isComplete ? 'bg-emerald-500' : pct > 0 ? 'bg-blue-500' : 'bg-transparent'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between items-center text-[9px] font-mono">
          <span className="text-gray-500 font-semibold">
            {currentNum}/{totalNum}{unitLabel ? ` ${unitLabel}` : ''}
          </span>
          <span className={isComplete ? 'text-emerald-600 font-bold' : currentNum > 0 ? 'text-amber-600 font-bold' : 'text-gray-400 font-bold'}>
            {isComplete ? 'COMPLETE' : currentNum > 0 ? `${pct}%` : '0%'}
          </span>
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════
  // CARD RENDERER — synced with OrderView
  // ════════════════════════════════════════════════════════════════
  const renderOrderCard = (item, compact = false) => {
    const isGroup = item.isGroupRow;
    const badges = !isGroup ? getStatusBadges(item) : [];
    const isSelected = isItemFullySelected(item);
    const invProgress = getInvoiceProgress(item);
    const packProgress = getPackedProgress(item);
    // const shipLabel = getShippingLabel(item);

    if (compact) {
      return (
        <div
          key={item.id}
          className={`relative flex items-center gap-1 rounded border transition-all cursor-pointer group ${
            isSelected
              ? 'border-indigo-500 ring-1 ring-indigo-500/40 bg-indigo-50/60'
              : isGroup
                ? 'bg-indigo-50 border-indigo-300 hover:border-indigo-500 border-l-2 border-l-indigo-500'
                : 'bg-white border-gray-200 hover:border-indigo-300'
          }`}
          title={isGroup ? `${item.customer || 'Family'} · ${item.soNumber || item.poTagNumber} · ${formatCurrency(item.amount)}` : `${item.soNumber || 'Sales Order'}\n${getOrderHoverText(item)}`}
        >
          {permissions && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => toggleItemSelection(item, e)}
              onClick={(e) => e.stopPropagation()}
              className="rounded border-gray-300 text-indigo-600 cursor-pointer w-3 h-3 ml-1 shrink-0"
            />
          )}
          <button
            type="button"
            onClick={() => (isGroup ? setSelectedGroup(item) : setSelectedOrder(item))}
            className="flex-1 min-w-0 text-left px-1 py-0.5 flex items-center gap-1"
          >
            {isGroup && <Package size={8} className="text-indigo-600 shrink-0" />}
            <span className="font-mono font-bold text-indigo-700 text-[9px] truncate">
              {isGroup ? item.poTagNumber : item.soNumber}
            </span>
            {item.cancelDate && <AlertTriangle size={8} className="text-rose-500 shrink-0" />}
          </button>

          {invProgress.invoiced > 0 && (
            <span
              className={`text-[7px] font-bold px-1 py-0.5 rounded shrink-0 mr-0.5 border ${
                invProgress.pct >= 100
                  ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                  : 'bg-blue-50 text-blue-600 border-blue-200'
              }`}
              title={isGroup ? `Invoiced: ${invProgress.invoiced}/${invProgress.total}` : `Invoice: ${invProgress.invoiceNumber}`}
            >
              {isGroup ? `INV ${invProgress.invoiced}/${invProgress.total}` : invProgress.invoiceNumber}
            </span>
          )}
        </div>
      );
    }

    // ─── Full card ───
    return (
      <div
        key={item.id}
        title={isGroup ? `${item.customer || 'Family'} · ${item.poTagNumber || ''}` : `${item.soNumber || 'Sales Order'}\n${getOrderHoverText(item)}`}
        className={`relative flex items-stretch gap-1 rounded border transition-all ${
          isSelected
            ? 'border-indigo-500 ring-2 ring-indigo-500/30 bg-indigo-50/40'
            : isGroup
              ? 'bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-300 hover:border-indigo-500 hover:shadow-md border-l-4 border-l-indigo-500'
              : 'bg-white border-gray-200 hover:border-indigo-300 hover:shadow-xs hover:bg-indigo-50/30'
        }`}
      >
        {permissions && (
          <div
            onClick={(e) => toggleItemSelection(item, e)}
            className="flex items-start pt-1.5 pl-1.5 cursor-pointer shrink-0"
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => toggleItemSelection(item, e)}
              onClick={(e) => e.stopPropagation()}
              className="rounded border-gray-300 text-indigo-600 cursor-pointer w-3.5 h-3.5"
            />
          </div>
        )}

        <button
          type="button"
          onClick={() => (isGroup ? setSelectedGroup(item) : setSelectedOrder(item))}
          className="flex-1 min-w-0 text-left p-1.5 text-[10px] space-y-0.5 cursor-pointer bg-transparent"
        >
          <div className="flex justify-between items-center gap-1">
            <span className="font-mono font-bold text-indigo-700 truncate">
              {isGroup ? (
                <span className="inline-flex items-center gap-1">
                  <Package size={9} /> {item.poTagNumber}
                </span>
              ) : item.soNumber}
            </span>
            {isGroup ? (
              <span className="px-1 py-0.5 text-[8px] rounded font-bold bg-indigo-600 text-white shrink-0">
                {item.childCount} SOs
              </span>
            ) : (
              badges.length > 0 && (
                <span className={`px-1 py-0.5 text-[8px] rounded font-bold border ${badges[0].class} shrink-0`}>
                  {badges[0].label}
                </span>
              )
            )}
          </div>

          <div className="text-gray-700 font-semibold truncate">{item.customer}</div>

          {item.cancelDate && (
            <div className="flex items-center gap-1 text-[9px] text-rose-600 font-bold py-0.5 mt-1 self-start">
              <AlertTriangle size={10} className="shrink-0 text-rose-500" />
              <span>Cancel: {formatDateDisplay(item.cancelDate)}</span>
            </div>
          )}

          <div className="flex justify-between text-gray-500">
            <span className="truncate">Tag: {item.poTagNumber || '-'}</span>
            <span className="truncate">
              {isGroup ? `${item.assignedTo}` : (item.assignedTo || 'Unassigned')}
            </span>
            <span className="font-bold text-gray-800">
              {formatCurrency(item.amount).replace('.00', '')}
            </span>
          </div>

          {/* PACKED STATUS */}
          <div className="mt-2 pt-1.5 border-t border-gray-100/80">
            <ProgressBlock
              label="PACKED"
              currentNum={packProgress.packed}
              totalNum={packProgress.total}
              pct={packProgress.pct}
              unitLabel={isGroup ? '' : 'un'}
            />
          </div>

          {/* INVOICED STATUS */}
          
          <div className="mt-1.5">
            <ProgressBlock
              label="INVOICED"
              right={!isGroup ? (invProgress.invoiceNumber || null) : null}
              currentNum={invProgress.invoiced}
              totalNum={invProgress.total}
              pct={invProgress.pct}
              unitLabel={isGroup ? 'SOs' : ''}
            />
          </div>          

          {/* Shipping status chip — matches OrderView label */}
          {/*
          <div className="mt-1.5 flex justify-between items-center text-[9px]">
            <span className="font-semibold text-gray-500 uppercase tracking-wider">Shipping</span>
            <span className={`px-1.5 py-0.5 rounded font-bold border ${
              /shipped|delivered/i.test(shipLabel)
                ? 'bg-purple-50 text-purple-700 border-purple-200'
                : /ready/i.test(shipLabel)
                  ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                  : /transit|partial/i.test(shipLabel)
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-gray-50 text-gray-600 border-gray-200'
            }`}>
              {shipLabel}
            </span>
          </div>
          */}
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-4">
        {/* ── Top controls ── */}
        <div className="no-print bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={handlePrev} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <span className="font-bold text-sm text-gray-900 min-w-[180px] text-center">
              {headerLabel}
            </span>
            <button onClick={handleNext} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <ChevronRight size={16} />
            </button>
            <button onClick={handleToday} className="text-xs bg-indigo-50 text-indigo-700 font-bold px-3 py-1.5 rounded-lg hover:bg-indigo-100 border border-indigo-200 transition-all">
              Today
            </button>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={() => window.print()} className="px-3 py-1.5 text-xs font-bold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center gap-1.5">
              Print Calendar
            </button>
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1.5 text-xs font-bold transition-colors flex items-center gap-1.5 ${viewMode === 'week' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <Layers size={12} /> Weekly
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1.5 text-xs font-bold transition-colors flex items-center gap-1.5 border-l border-gray-200 ${viewMode === 'month' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <CalendarIcon size={12} /> Monthly
              </button>
            </div>

            {viewMode === 'week' && (
              <>
                <span className="text-xs font-semibold text-gray-600">Weeks:</span>
                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                  {CALENDAR_RANGES.map(r => (
                    <button key={r.value} onClick={() => onRangeChange(r.value)} className={`px-3 py-1.5 text-xs font-bold transition-colors ${rangeWeeks === r.value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Floating bulk bar ── */}
        {permissions && selectedCalendarIds.length > 0 && (
          <div className="no-print sticky top-2 z-30 bg-white p-3 rounded-xl border-2 border-indigo-300 shadow-lg flex flex-wrap items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
            <span className="text-xs text-indigo-700 font-bold bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded">
              {selectedCalendarIds.length} Order{selectedCalendarIds.length === 1 ? '' : 's'} Selected
            </span>
            <button onClick={handleBulkEditClick} className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors">
              <Truck size={12} /> Bulk Edit
            </button>
            <button onClick={handleClearSelection} className="text-gray-500 hover:text-gray-800 p-1.5 rounded transition-colors ml-auto flex items-center gap-1 text-xs font-bold">
              <X size={14} /> Clear
            </button>
          </div>
        )}

        <style>{`
          .print-only { display: none; }
          @page { size: letter landscape; margin: 0.3in; }
          @media print {
            html, body { width: 100%; height: auto; background: #fff !important; }
            body * { visibility: hidden !important; }
            .calendar-print-area, .calendar-print-area * { visibility: visible !important; }
            .calendar-print-area { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; margin: 0 !important; padding: 0 !important; }
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            .calendar-print-area .overflow-y-auto, .calendar-print-area .overflow-hidden { overflow: visible !important; }
            .calendar-print-area .min-h-300, .calendar-print-area .min-h-220, .calendar-print-area .min-h-160 { min-height: 0 !important; }
            .calendar-print-area .shadow-xs, .calendar-print-area .shadow-sm, .calendar-print-area .shadow-md { box-shadow: none !important; }
            .calendar-print-area button { color: #111827 !important; }
            .calendar-print-area .text-white { color: #111827 !important; }
            .calendar-print-area .bg-indigo-600 { background: #e5e7eb !important; }
            .calendar-print-area .bg-indigo-50, .calendar-print-area .bg-blue-50 { background: #f3f4f6 !important; }
            .calendar-print-area .ring-2 { box-shadow: none !important; }
            .calendar-print-area .grid { break-inside: avoid; }
            .calendar-print-area .weekly-days-grid { grid-template-columns: repeat(6, minmax(0, 1fr)) !important; gap: 0.08in !important; }
            .calendar-print-area .monthly-calendar-grid { grid-template-columns: repeat(7, minmax(0, 1fr)) !important; }
            .calendar-print-area .weekly-day-card { min-height: 0 !important; }
            .calendar-print-area .rounded-xl { border-radius: 0 !important; }
          }
        `}</style>

        <div className="calendar-print-area">
          <div className="print-only mb-3 border-b-2 border-gray-800 pb-2 text-center">
            <div className="text-lg font-extrabold tracking-wide">OPERATIONS PLANNER</div>
            <div className="text-sm font-semibold">Calendar — {viewMode === 'month' ? monthView.monthLabel : headerLabel}</div>
            <div className="text-[10px] text-gray-600">Printed calendar · {viewMode === 'month' ? 'Monthly view' : `${rangeWeeks}-week view`}</div>
          </div>

        {/* ── Weekly view ── */}
        {viewMode === 'week' && (
          <div className="space-y-3">
            {weeks.map(week => (
              <div key={week.weekNum} className="space-y-2">
                {rangeWeeks > 1 && (
                  <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider px-1">{week.weekLabel}</div>
                )}
                <div className={`weekly-days-grid grid ${gridColsClass} gap-2`}>
                  {week.days.map(day => {
                    const dayItems = ordersByDate[day.dateStr] || [];
                    const dayTotal = dayItems.reduce((sum, o) => sum + (o.amount || 0), 0);
                    const dayCount = dayItems.reduce((sum, o) => sum + (o.isGroupRow ? o.childCount : 1), 0);
                    const minH = rangeWeeks === 4 ? 'min-h-[160px]' : rangeWeeks === 2 ? 'min-h-[220px]' : 'min-h-[300px]';
                    return (
                      <div key={day.dateStr} className={`bg-white rounded-lg border flex flex-col ${minH} overflow-visible ${day.isToday ? 'border-indigo-500 ring-2 ring-indigo-500/20' : day.isWeekend ? 'border-gray-200 bg-gray-50/50' : 'border-gray-200'}`}>
                        <div className={`px-2 py-1.5 border-b flex items-center justify-between ${day.isToday ? 'bg-indigo-600 text-white' : day.isWeekend ? 'bg-gray-100 text-gray-500' : 'bg-white text-gray-800'}`}>
                          <div>
                            <div className="text-[9px] uppercase font-bold tracking-wider opacity-80">{day.dayName}</div>
                            <div className="text-xs font-extrabold">{day.displayDate}</div>
                            {dayItems.some(isGfsFamilyItem) && (
                              <div className="text-[9px] font-extrabold tracking-wide">GFS {day.dateStr}</div>
                            )}
                          </div>
                          {dayItems.length > 0 && (
                            <div className="text-right">
                              <div className="text-[9px] font-bold opacity-80">{dayCount} SO</div>
                              <div className="text-[9px] font-bold opacity-70">{formatCurrency(dayTotal).replace('.00', '')}</div>
                            </div>
                          )}
                        </div>
                        <div className="p-1.5 space-y-1 flex-1 overflow-y-auto">
                          {dayItems.length === 0 ? (
                            <div className="text-center text-gray-300 text-[10px] py-4">—</div>
                          ) : (
                            dayItems.map(item => renderOrderCard(item, false))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Monthly view ── */}
        {viewMode === 'month' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
              {DAY_NAMES_FULL.map((day, idx) => (
                <div key={day} className={`px-2 py-2 text-[10px] font-bold uppercase tracking-wider text-center ${idx === 0 || idx === 6 ? 'text-gray-400' : 'text-gray-600'}`}>
                  {day}
                </div>
              ))}
            </div>
            <div className="monthly-calendar-grid grid grid-cols-7">
              {monthView.weeks.map((week, wIdx) =>
                week.map((day, dIdx) => {
                  const dayItems = ordersByDate[day.dateStr] || [];
                  const dayTotal = dayItems.reduce((sum, o) => sum + (o.amount || 0), 0);
                  const dayCount = dayItems.reduce((sum, o) => sum + (o.isGroupRow ? o.childCount : 1), 0);
                  const maxVisible = 3;
                  const hiddenCount = dayItems.length - maxVisible;
                  return (
                    <div
                      key={`${wIdx}-${dIdx}`}
                      className={`min-h-[120px] border-b border-r border-gray-200 p-1.5 flex flex-col ${
                        !day.isCurrentMonth ? 'bg-gray-50/60 text-gray-400'
                          : day.isToday ? 'bg-indigo-50/50 ring-2 ring-indigo-500 ring-inset'
                            : day.isWeekend ? 'bg-slate-50/40' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-bold ${
                          day.isToday ? 'bg-indigo-600 text-white w-6 h-6 rounded-full flex items-center justify-center'
                            : !day.isCurrentMonth ? 'text-gray-400' : 'text-gray-800'
                        }`}>
                          {day.dayNum}
                        </span>
                        {dayItems.some(isGfsFamilyItem) && day.isCurrentMonth && (
                          <span className="text-[8px] font-extrabold text-indigo-700">GFS {day.dateStr}</span>
                        )}
                        {dayItems.length > 0 && day.isCurrentMonth && (
                          <span className="text-[9px] font-bold text-gray-500">
                            {dayCount} · {formatCurrency(dayTotal).replace('.00', '')}
                          </span>
                        )}
                      </div>
                      <div className="space-y-0.5 flex-1 overflow-hidden">
                        {dayItems.slice(0, maxVisible).map(item => renderOrderCard(item, true))}
                        {hiddenCount > 0 && (
                          <button
                            onClick={() => {
                              const nextItem = dayItems[maxVisible];
                              if (nextItem?.isGroupRow) setSelectedGroup(nextItem);
                              else setSelectedOrder(nextItem);
                            }}
                            className="w-full text-[9px] font-bold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded px-1 py-0.5 text-left transition-colors"
                          >
                            +{hiddenCount} more
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          INDIVIDUAL SO MODAL
          ═══════════════════════════════════════════════════════════ */}
      {selectedOrder && (() => {
        //const inv = getInvoiceProgress(selectedOrder);
        const pack = getPackedProgress(selectedOrder);
        //const shipLabel = getShippingLabel(selectedOrder);

        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-5" role="dialog" aria-modal="true" onMouseDown={e => e.target === e.currentTarget && setSelectedOrder(null)}>
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-xs" />
            <div className="relative w-full max-w-2xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-white flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg sm:text-xl font-extrabold text-gray-900">Sales Order {selectedOrder.soNumber}</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Complete order information</p>
                </div>
                <div className="flex items-center gap-2">
                  {permissions && onOpenBulkEdit && (
                    <button onClick={() => { onOpenBulkEdit([selectedOrder.id]); setSelectedOrder(null); }} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold shadow-sm transition-colors flex items-center gap-1.5">
                      <Truck size={12} /> Edit
                    </button>
                  )}
                  <button onClick={() => setSelectedOrder(null)} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"><X size={20} /></button>
                </div>
              </div>

              <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <div className="text-[11px] font-bold text-gray-500 mb-2 uppercase">Status</div>
                  <div className="flex flex-wrap gap-1">
                    {getStatusBadges(selectedOrder).map((b, idx) => (
                      <span key={idx} className={`inline-flex px-2 py-0.5 rounded-md text-xs font-bold border ${b.class}`}>{b.label}</span>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                  <div className="text-[11px] font-bold text-indigo-500 mb-1 uppercase">Order Total</div>
                  <div className="text-xl font-extrabold text-indigo-700">{formatCurrency(selectedOrder.amount)}</div>
                </div>
              </div>

                {/* Progress cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <ProgressBlock
                      label="PL#"
                      currentNum={pack.packed}
                      totalNum={pack.total}
                      pct={pack.pct}
                      unitLabel="un"
                      barHeight="h-2"
                    />
                  </div>
                  {/*
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <ProgressBlock
                      label="INVOICED STATUS"
                      right={inv.invoiceNumber || null}
                      currentNum={inv.invoiced}
                      totalNum={inv.total}
                      pct={inv.pct}
                      barHeight="h-2"
                    />
                  </div>
                  */}
                </div>

                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200"><h3 className="text-sm font-extrabold">Customer Information</h3></div>
                  <div className="p-4 grid grid-cols-2 gap-4">
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Customer</div><div className="text-sm font-semibold">{selectedOrder.customer}</div></div>
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Tag</div><div className="text-sm font-semibold font-mono">{selectedOrder.poTagNumber || '—'}</div></div>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200"><h3 className="text-sm font-extrabold">Order Logistics</h3></div>
                  <div className="p-4 grid grid-cols-2 gap-x-5 gap-y-4">
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Order Date</div><div className="text-sm font-semibold">{formatDateDisplay(selectedOrder.soDate)}</div></div>
                    {selectedOrder.cancelDate && (
                      <div>
                        <div className="text-[11px] font-bold text-gray-400 uppercase">Cancel Date</div>
                        <div className="text-sm font-semibold text-rose-600">{formatDateDisplay(selectedOrder.cancelDate)}</div>
                      </div>
                    )}
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Deadline</div><div className="text-sm font-semibold">{formatDateDisplay(selectedOrder.deadline)}</div></div>
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Assigned To</div><div className="text-sm font-semibold">{selectedOrder.assignedTo || 'Unassigned'}</div></div>
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Shipping Method</div><div className="text-sm font-semibold">{selectedOrder.pickupOrShip}</div></div>
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">ETA</div><div className="text-sm font-semibold">{formatDateDisplay(selectedOrder.eta)}</div></div>
                    <div><div className="text-[11px] font-bold text-gray-400 uppercase">Departure</div><div className="text-sm font-semibold">{formatDateDisplay(selectedOrder.departureDate)}</div></div>
                  </div>
                </div>
              </div>

              <div className="px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button onClick={() => setSelectedOrder(null)} className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-bold shadow-sm hover:bg-indigo-700 transition-colors">Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════
          FAMILY SO MODAL
          ═══════════════════════════════════════════════════════════ */}
      {selectedGroup && (() => {
        //const groupInv = getInvoiceProgress(selectedGroup);
        //const groupPack = getPackedProgress(selectedGroup);

        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-5" role="dialog" aria-modal="true" onMouseDown={e => e.target === e.currentTarget && setSelectedGroup(null)}>
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-xs" />
            <div className="relative w-full max-w-2xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-white flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Package size={20} className="text-indigo-600" />
                    <h2 className="text-lg sm:text-xl font-extrabold text-gray-900">Family {selectedGroup.poTagNumber}</h2>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1 font-medium">
                    {selectedGroup.childCount} Orders · Total {formatCurrency(selectedGroup.amount)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {permissions && onOpenBulkEdit && (
                    <button onClick={() => { onOpenBulkEdit(selectedGroup.childOrderIds); setSelectedGroup(null); }} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold shadow-sm transition-colors flex items-center gap-1.5">
                      <Truck size={12} /> Bulk Edit All
                    </button>
                  )}
                  <button onClick={() => setSelectedGroup(null)} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"><X size={20} /></button>
                </div>
              </div>

              {/* Family-wide progress summary */}
              <div className="px-4 sm:px-6 py-3 bg-slate-50 border-b border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-3">
                { /*
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <ProgressBlock
                    label="PL#"
                    currentNum={groupPack.packed}
                    totalNum={groupPack.total}
                    pct={groupPack.pct}
                    unitLabel="un"
                    barHeight="h-2"
                  />
                </div>
                
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <ProgressBlock
                    label="FAMILY INVOICED"
                    currentNum={groupInv.invoiced}
                    totalNum={groupInv.total}
                    pct={groupInv.pct}
                    unitLabel="SOs"
                    barHeight="h-2"
                  />
                </div>
                */}
                
              </div>
             

              <div className="overflow-y-auto flex-1 p-3 sm:p-5 space-y-3 bg-slate-50/50">
                {selectedGroup.childOrders.map(child => {
                  const badges = getStatusBadges(child);
                  // const childInv = getInvoiceProgress(child);
                  const childPack = getPackedProgress(child);
                  const childShip = getShippingLabel(child);
                  return (
                    <div key={child.id} className="bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col gap-2 transition-all hover:border-indigo-300">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-bold text-indigo-700 text-sm sm:text-base">{child.soNumber}</span>
                            {badges.length > 0 && <span className={`px-1.5 py-0.5 text-[9px] rounded font-bold border ${badges[0].class}`}>{badges[0].label}</span>}
                            <span className={`px-1.5 py-0.5 text-[9px] rounded font-bold border ${
                              /shipped|delivered/i.test(childShip) ? 'bg-purple-50 text-purple-700 border-purple-200'
                              : /ready/i.test(childShip) ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                              : /transit|partial/i.test(childShip) ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-gray-50 text-gray-600 border-gray-200'
                            }`}>
                              {childShip}
                            </span>
                          </div>
                          <div className="text-xs text-gray-700 font-semibold mt-0.5">{child.customer}</div>
                          {child.cancelDate && (
                            <div className="flex items-center gap-1 text-[9px] text-rose-600 font-bold bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200 mt-1.5 w-fit">
                              <AlertTriangle size={10} className="shrink-0 text-rose-500" />
                              <span>Cancel: {formatDateDisplay(child.cancelDate)}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-start gap-2 shrink-0">
                          <div className="text-right">
                            <div className="font-extrabold text-gray-900 text-sm">{formatCurrency(child.amount)}</div>
                          </div>
                          {permissions && onOpenBulkEdit && (
                            <button onClick={() => { onOpenBulkEdit([child.id]); setSelectedGroup(null); }} className="px-2 py-1 rounded bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 text-[9px] font-bold transition-colors">
                              Edit
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-2 text-[10px] text-gray-500 bg-gray-50 p-2.5 rounded-lg border border-gray-100 mt-1">
                        <div className="flex items-center gap-1.5">
                          <span className="uppercase font-bold tracking-wider text-gray-400">Tag:</span>
                          <span className="font-mono font-bold text-gray-700 max-w-[120px] truncate">{child.poTagNumber || child.tag || '—'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 border-l-0 sm:border-l border-gray-200 sm:pl-4">
                          <span className="uppercase font-bold tracking-wider text-gray-400">Due:</span>
                          <span className="font-medium text-gray-700">{formatDateDisplay(child.deadline)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 border-l-0 sm:border-l border-gray-200 sm:pl-4">
                          <span className="uppercase font-bold tracking-wider text-gray-400">Staff:</span>
                          <span className="font-medium text-gray-700 max-w-[100px] truncate">{child.assignedTo || 'Unassigned'}</span>
                        </div>
                      </div>

                      {/* Per-child progress — matches OrderView 1:1 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1 pt-2 border-t border-gray-100">
                        <ProgressBlock
                          label="PL#"
                          currentNum={childPack.packed}
                          totalNum={childPack.total}
                          pct={childPack.pct}
                          unitLabel="un"
                        />
                        {/*
                        <ProgressBlock
                          label="INVOICED"
                          right={childInv.invoiceNumber || null}
                          currentNum={childInv.invoiced}
                          totalNum={childInv.total}
                          pct={childInv.pct}
                        />
                        */}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button onClick={() => setSelectedGroup(null)} className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-bold shadow-sm hover:bg-indigo-700 transition-colors">Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

// ─────────────────────────────────────────────
// COMPLETED ORDERS VIEW (180 Days)
// ─────────────────────────────────────────────
/**
 * Helper: Debounce function for API calls to prevent spamming the database while typing
 */
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

function CompletedOrdersView({ 
  completedOrders = [], 
  loading, 
  paginationMeta = { page: 1, totalPages: 1, total: 0, limit: 25 }, 
  onPageChange,
  permissions,
  undoComplete
}) {
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [dbResults, setDbResults] = useState([]);
  const [isSearchingDb, setIsSearchingDb] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  // 1. Filter completed orders locally first
  const localFilteredOrders = useMemo(() => {
    const t = searchTerm.trim().toLowerCase();
    if (!t) return completedOrders;

    return completedOrders.filter(so => {
      const soNum = (so.salesOrderNumber || so.soNumber || '').toLowerCase();
      const tag = (so.tag || so.poTagNumber || '').toLowerCase();
      const customer = (so.customer || '').toLowerCase();
      const invoice = (so.invoiceNumber || '').toLowerCase();
      const assigned = (so.owner || so.assignedTo || '').toLowerCase();

      return (
        soNum.includes(t) ||
        tag.includes(t) ||
        customer.includes(t) ||
        invoice.includes(t) ||
        assigned.includes(t)
      );
    });
  }, [completedOrders, searchTerm]);

  // 2. Fetch from Database API if local search has no matches
  useEffect(() => {
    let isCancelled = false;

    const searchDatabase = async () => {
      const t = debouncedSearchTerm.trim();
      
      // Skip DB search if input is empty or we found local matches
      if (!t || localFilteredOrders.length > 0) {
        setDbResults([]);
        setIsSearchingDb(false);
        setSearchError(null);
        return;
      }

      setIsSearchingDb(true);
      setSearchError(null);

      try {
        // NOTE: Adjust this URL to match your actual backend route!
        const response = await fetch(`${API_BASE_URL}/weeklyplanner/completed/search?q=${encodeURIComponent(t)}`);
        //const response = await api.OperationsPlanner.searchHistory(encodeURIComponent(t));
        
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        
        const data = await response.json();
        const results = Array.isArray(data) ? data : data.orders || [];

        if (!isCancelled) {
          setDbResults(results);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Database search error:', err);
          setSearchError('Could not fetch additional orders from database.');
          setDbResults([]);
        }
      } finally {
        if (!isCancelled) {
          setIsSearchingDb(false);
        }
      }
    };

    searchDatabase();

    return () => {
      isCancelled = true;
    };
  }, [debouncedSearchTerm, localFilteredOrders.length]);

  // 3. Combine local results + database fallback results to feed the table and exports
  const filteredOrders = useMemo(() => {
    if (localFilteredOrders.length > 0) return localFilteredOrders;
    
    // If local is empty but we have DB results, deduplicate and use them
    const existingIds = new Set(
      completedOrders.map(o => String(o.id || o._id || o.salesOrderNumber))
    );
    
    return dbResults.filter(
      dbOrder => !existingIds.has(String(dbOrder.id || dbOrder._id || dbOrder.salesOrderNumber))
    );
  }, [localFilteredOrders, dbResults, completedOrders]);

  // Export completed orders to Excel (unchanged, will now include DB results if searched)
  const exportCompletedToExcel = () => {   
    if (!filteredOrders || filteredOrders.length === 0) return;
    const data = filteredOrders.map(so => ({
      'SO Number': so.salesOrderNumber || so.soNumber,
      'Customer': so.customer,
      'Amount ($)': so.amount,
      'SO Date': formatDateDisplay(so.orderDateTime || so.soDate),
      'Invoice #': so.invoiceNumber || so.invoiceNumber || '-', // Note: kept your double invoiceNumber logic as requested
      'Status': so.status,
      'Completed Date': formatDateDisplay(so.invoiceDate || so.completedAt)
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Completed Orders');
    XLSX.writeFile(wb, `Completed_Orders_${getTodayStr()}.xlsx`);
  };

  const page = paginationMeta?.page || 1;
  const limit = paginationMeta?.limit || 20;
  const total = paginationMeta?.total || 0;
  const totalPages = paginationMeta?.totalPages || 1;

  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  const isFiltering = searchTerm.trim().length > 0;
  const isFromDb = localFilteredOrders.length === 0 && dbResults.length > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden mt-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-6 border-b border-gray-100">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <CheckCircle2 size={18} className="text-emerald-600" />
            Completed Sales Orders
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Showing historical orders (Last 180 days)</p>
        </div>
        {permissions && (
          <button
            onClick={exportCompletedToExcel}
            disabled={loading || filteredOrders.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50 transition-colors shrink-0 cursor-pointer"
          >
            <Download size={14} /> Export Completed
          </button>
        )}
      </div>

      {/* ─── SEARCH BAR ─── */}
      <div className="px-6 py-3 bg-gray-50/60 border-b border-gray-100">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search SO, tag, customer, invoice, employee…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 outline-none bg-white"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              aria-label="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {isFiltering && (
          <div className="mt-2 text-[11px] text-gray-500 font-semibold flex items-center gap-2">
            <span>
              Found <span className="text-emerald-700 font-bold">{filteredOrders.length}</span> matching order{filteredOrders.length === 1 ? '' : 's'}
              {filteredOrders.length !== completedOrders.length && !isFromDb && (
                <span className="text-gray-400"> of {completedOrders.length}</span>
              )}
            </span>
            {isSearchingDb && (
              <span className="text-indigo-600 animate-pulse ml-2 flex items-center gap-1">
                <RefreshCw size={10} className="animate-spin" /> Searching database...
              </span>
            )}
          </div>
        )}
        
        {searchError && (
          <div className="mt-2 text-[11px] text-red-600 font-semibold">
            {searchError}
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center">
          <RefreshCw size={20} className="animate-spin text-indigo-600 mx-auto mb-2" />
          <p className="text-xs text-gray-500">Processing archive...</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="text-[11px] text-gray-500 uppercase bg-gray-50 font-bold border-b border-gray-200">
                <tr>
                  <th className="p-1 w-[80px]">Sales Order</th>
                  <th className="p-1 min-w-[180px]">Customer</th>
                  <th className="p-2 text-right w-[20px]">Amount</th>
                  <th className="p-2 w-[100px]">SO Date</th>
                  <th className="p-2 w-[100px]">Deadline</th>
                  <th className="p-2 w-[130px]">Assigned</th>
                  <th className="p-2 w-[100px]">Ship Date</th>
                  <th className="p-2 w-[110px]">Invoice #</th>
                  <th className="p-2 w-[80px]">Tracking</th>
                  <th className="p-2 w-[80px]">View POD</th>
                  <th className="p-1 w-[110px]">Completed</th>
                  <th className="p-2 w-[110px]">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="p-8 text-center text-gray-400">
                      {isSearchingDb ? 'Searching database...' : isFiltering
                        ? `No completed orders match "${searchTerm}".`
                        : 'No completed orders found.'}
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map(so => (
                    <tr key={so.id || so._id || so.salesOrderNumber} className="hover:bg-gray-50 transition-colors">
                      <td className="p-1 align-top">
                        <div className="font-mono font-bold text-emerald-700 flex items-center gap-1">
                          {so.salesOrderNumber || so.soNumber}
                          {/* Optional tiny badge to show it came from DB search */}
                          {isFromDb && (
                            <span className="text-[8px] bg-indigo-100 text-indigo-700 px-1 py-0.5 rounded leading-none">DB</span>
                          )}
                        </div>
                        {(so.tag || so.poTagNumber) && (
                          <div className="text-[10px] text-gray-400 font-mono truncate" title={so.tag || so.poTagNumber}>
                            Tag: {so.tag || so.poTagNumber}
                          </div>
                        )}
                      </td>
                      <td className="p-1 align-top">
                        <div
                          className="font-semibold text-gray-900 leading-snug break-words"
                          title={so.customer}
                        >
                          {so.customer || '—'}
                        </div>
                      </td>
                      <td className="p-2 align-top text-right font-bold text-gray-900 whitespace-nowrap">
                        {formatCurrency(so.amount)}
                      </td>
                      <td className="p-2 align-top text-gray-600 whitespace-nowrap">
                        {formatDateDisplay(so.orderDateTime || so.soDate)}
                      </td>
                      <td className="p-2 align-top text-gray-600 whitespace-nowrap">
                        {formatDateDisplay(so.deadlineDate || so.deadline)}
                      </td>
                      <td className="p-2 align-top text-gray-700">
                        <span className="truncate block max-w-[130px]" title={so.owner || so.assignedTo || 'Unassigned'}>
                          {so.owner || so.assignedTo || 'Unassigned'}
                        </span>
                      </td>
                      <td className="p-2 align-top text-gray-600 whitespace-nowrap">
                        {formatDateDisplay(so.shippingDateManual || so.shippedStatus)}
                      </td>
                      <td className="p-2 align-top font-mono text-gray-600 whitespace-nowrap">
                        {so.invoiceNumber || '—'}
                      </td>
                      <td className="p-2 align-top">
                        {so.trackingLink ? (
                          <a
                            href={so.trackingLink}
                            target="_blank"
                            rel="noreferrer"
                            className="text-indigo-600 hover:underline text-[10px] font-bold whitespace-nowrap"
                          >
                            Track ↗
                          </a>
                        ) : (
                          <span className="text-gray-400 text-[10px]">N/A</span>
                        )}
                      </td>
                      <td className="p-2 align-top">
                        <span className="text-gray-400 text-[10px]">N/A</span>                     
                      </td>                      
                      <td className="p-1 align-top font-bold text-emerald-600 whitespace-nowrap">
                        {formatDateDisplay(so.invoiceDate || so.completedAt)}
                      </td>
                      <td> 
                        <button
                          type="button"
                          onClick={() => undoComplete(so.id, null, true)}
                          className="ml-2 px-2 py-1 bg-white border border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 rounded text-xs font-bold transition-colors shadow-sm"
                          title="Undo and return to active Planner"
                        >
                          Undo
                        </button>                        
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && !isFiltering && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Showing <span className="font-bold text-gray-700">{rangeStart}</span> to <span className="font-bold text-gray-700">{rangeEnd}</span> of <span className="font-bold text-gray-700">{total}</span> results
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onPageChange(page - 1)}
                  disabled={page === 1 || loading}
                  className="p-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                >
                  <ChevronLeft size={16} />
                </button>
                
                <div className="text-xs font-bold text-gray-700 px-2 min-w-[100px] text-center">
                  Page {page} of {totalPages}
                </div>
                
                <button
                  onClick={() => onPageChange(page + 1)}
                  disabled={page === totalPages || loading}
                  className="p-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {isFiltering && filteredOrders.length > 0 && (
            <div className="px-6 py-3 bg-emerald-50/50 border-t border-emerald-100 text-[11px] text-gray-600 font-semibold">
              Showing {filteredOrders.length} filtered result{filteredOrders.length === 1 ? '' : 's'} — pagination is disabled while filtering. Clear search to re-enable paging.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// MODALS
// ─────────────────────────────────────────────
function PrepaymentModal({ order, onClose, onConfirm, confirm }) {
  const [receivedDate, setReceivedDate] = useState(getTodayStr());
  const [adjustedDeadline, setAdjustedDeadline] = useState(() => {
    if (order.soDate && order.deadline) {
      const d = new Date(getTodayStr());
      d.setDate(d.getDate() + order.leadTime);
      return d.toISOString().split('T')[0];
    }
    return order.deadline;
  });

  const handleDateChange = (newDate) => {
    setReceivedDate(newDate);
    if (order.leadTime) {
      const d = new Date(newDate);
      d.setDate(d.getDate() + order.leadTime);
      setAdjustedDeadline(d.toISOString().split('T')[0]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-2xl">
        <div className="bg-indigo-600 p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign size={18} />
            <h3 className="font-bold text-sm">Confirm Payment Received</h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 text-xs text-gray-700">
          <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
            <p className="font-bold text-sm text-amber-900">{order.isGroupRow ? `SO Family: ${order.poTagNumber} (${order.childCount} Orders)` : order.customer}</p>
            <p className="mt-1 text-amber-800">SO <strong>{order.soNumber}</strong> · <strong>{formatCurrency(order.amount)}</strong></p>
            <p className="mt-1 text-[10px] text-amber-700">{order.paymentTerms}</p>
          </div>
          <div>
            <label className="block font-semibold text-gray-700 mb-1">Received Date:</label>
            <input type="date" value={receivedDate} onChange={(e) => handleDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none" />
          </div>
          <div className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg space-y-1.5">
            <div className="flex justify-between"><span className="text-gray-600">Lead Time:</span><span className="font-bold text-indigo-700">{order.leadTime} days</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Original Deadline:</span><span className="line-through text-gray-400">{formatDateDisplay(order.deadline)}</span></div>
            <div className="flex justify-between items-center pt-1.5 border-t border-indigo-200">
              <span className="font-bold text-indigo-900">New Deadline:</span>
              <input type="date" value={adjustedDeadline} onChange={(e) => setAdjustedDeadline(e.target.value)}
                className="px-2 py-1 border border-indigo-300 rounded font-bold text-indigo-900 bg-white" />
            </div>
          </div>
        </div>
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-100 transition-colors">Cancel</button>
          {confirm ? (
            <button disabled className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 opacity-85">
              <RefreshCw size={12} className="animate-spin text-white" /> Saving...            
            </button>
          ) : (        
            <button onClick={() => onConfirm(order.id, receivedDate, adjustedDeadline)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors">
              <Check size={14} /> Confirm
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function OrderEditModal({ order, onClose, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [local, setLocal] = useState({ ...order });
  const update = (f, v) => setLocal(p => ({ ...p, [f]: v }));

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await api.OperationsPlanner.editSoById(local);      
      if (response && response.success) {
        Object.keys(local).forEach(k => {
          if (local[k] !== order[k]) onUpdate(order.id, k, local[k]);
        });
        onClose();
      } else {
        alert(response?.message || "Failed to update order.");
      }
    } catch (error) {
      console.error("Failed to update SO:", error);
      const message = error?.data?.message || error?.message || "Failed to update sales order(s)";
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col animate-in fade-in zoom-in-95 duration-150">
        <div className="bg-indigo-600 p-4 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Package size={18} />
            <div>
              <h3 className="font-bold text-sm">Edit Sales Order Details</h3>
              <p className="text-[11px] opacity-80 font-mono">{order.soNumber} · {order.customer}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4 text-xs text-gray-700 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold mb-1">SO Date</label>
              <input type="date" value={local.soDate || ''} onChange={(e) => update('soDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 outline-none bg-white" />
            </div>
            <div>
              <label className="block font-semibold mb-1">Deadline</label>
              <input type="date" value={local.deadline || ''} onChange={(e) => update('deadline', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 outline-none bg-white" />
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
            <p className="font-bold text-slate-700">Production & Planning status</p>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={local.plannedProduction} onChange={(e) => update('plannedProduction', e.target.checked)} className="rounded text-indigo-600" />
              <span>Production Planned</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={local.plannedMaterials} onChange={(e) => update('plannedMaterials', e.target.checked)} className="rounded text-indigo-600" />
              <span>Materials Planned</span>
            </label>
          </div>
        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-100 transition-colors">Cancel</button>
          {loading ? (
            <button disabled className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 opacity-80">
              <RefreshCw size={12} className="animate-spin text-white" /> Saving...
            </button>
          ) : (           
            <button onClick={handleSave} className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors">
              <Check size={14} /> Save Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function BulkEditModal({ selectedIds, onClose, onApply, api, employees = [] }) {
  const [fields, setFields] = useState({ 
    deadline: '',
    pickupOrShip: 'PICKUP',
    plannedProduction: false,
    plannedProductionDate: '',
    plannedMaterials: false,
    plannedMaterialsDate: '',
    assignedTo: 'Unassigned',
    shippingStatus: '',
    departureDate: '', 
    eta: '',
    accountingChecked: false,
    accountingDate: ''
  });
  
  const [enabled, setEnabled] = useState({ 
    deadline: false,
    pickupOrShip: false,
    plannedProduction: false,
    plannedMaterials: false,
    assignedTo: false,
    shippingStatus: false, 
    departureDate: false, 
    eta: false,
    accountingChecked: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleApply = async () => {
    const changes = {};
    
    if (enabled.deadline) changes.deadline = fields.deadline;
    if (enabled.pickupOrShip) changes.pickupOrShip = fields.pickupOrShip;
    
    if (enabled.plannedProduction) {
      changes.plannedProduction = fields.plannedProduction;
      changes.plannedProductionDate = fields.plannedProduction ? fields.plannedProductionDate : '';
    }
    
    if (enabled.plannedMaterials) {
      changes.plannedMaterials = fields.plannedMaterials;
      changes.plannedMaterialsDate = fields.plannedMaterials ? fields.plannedMaterialsDate : '';
    }
    
    if (enabled.assignedTo) changes.assignedTo = fields.assignedTo;
    if (enabled.shippingStatus) changes.shippingStatus = fields.shippingStatus;
    if (enabled.departureDate) changes.departureDate = fields.departureDate;
    if (enabled.eta) changes.eta = fields.eta;
    
    if (enabled.accountingChecked) {
      changes.accountingChecked = fields.accountingChecked;
      changes.accountingDate = fields.accountingChecked ? fields.accountingDate : '';
    }

    if (Object.keys(changes).length === 0) {
      setError("Please select at least one field to update.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        orderIds: selectedIds,
        ...changes
      };

      const response = await api.OperationsPlanner.handlePersistUpdate(payload);

      if (onApply) onApply(changes, response);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to persist update');
    } finally {
      setIsSubmitting(false);
    }
  };

  const SHIPPING_OPTIONS = [
    { value: 'PICKUP', label: 'Pickup' },
    { value: 'ASA SHIP', label: 'ASA Ship' },
    { value: 'ROUTE LACHINE', label: 'Route Lachine' },
    { value: 'ROUTE BAY', label: 'Route Bay' },
    { value: 'SHIP CLIENT PORTAL', label: 'Client Portal' }
  ];

  const SHIPPED_STATUS_OPTIONS = [
    { value: '', label: 'Pending' },
    { value: 'Ready for shipping', label: 'Ready to shipping' },
    { value: 'Shipped', label: 'Shipped' },
    { value: 'Partially Shipped', label: 'Partially Shipped' },
    { value: 'Picked Up', label: 'Picked Up' },
    { value: 'In Transit', label: 'In Transit' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Issue', label: 'Issue' },
    { value: 'On Hold', label: 'On Hold' }
  ];

  const setDefault = false;

  const canWriteAccess = (value) => normalizeAccess(value) === ACCESS.WRITE;

  // ─────────────────────────────────────────────
  // PARSE PERMISSIONS ONCE (pure initializer)
  // ─────────────────────────────────────────────
  const [permissionsData] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions")) || {};
    } catch (e) {
      console.error("Invalid permissions JSON in localStorage:", e);
      return {};
    }
  });

  const canEditDeadline     = canWriteAccess(permissionsData?.allowEditDeadline)     || setDefault;
  const canEditPickupOrShip = canWriteAccess(permissionsData?.allowEditPickupOrShip) || setDefault;
  const canEditPlannedProduction     = canWriteAccess(permissionsData?.allowEditPlannedProduction)     || setDefault;
  const canEditPlannedMaterials      = canWriteAccess(permissionsData?.allowEditPlannedMaterials)      || setDefault;
  const canEditAssignedTo   = canWriteAccess(permissionsData?.allowEditAssignedTo)   || setDefault;
  const canEditShippingStatus = canWriteAccess(permissionsData?.allowEditShippingStatus) || setDefault;
  const canEditDepartureDate = canWriteAccess(permissionsData?.allowEditDepartureDate) || setDefault;
  const canEditEta           = canWriteAccess(permissionsData?.allowEditEta)           || setDefault;
  const canEditInvoiceSubmission     = canWriteAccess(permissionsData?.allowEditInvoiceSubmission)     || setDefault;
    
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[90vh]">
        
        <div className="bg-amber-600 p-4 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Truck size={18} />
            <h3 className="font-bold text-sm">Bulk Edit {selectedIds.length} Orders</h3>
          </div>
          <button 
            onClick={onClose} 
            disabled={isSubmitting}
            className="text-white/80 hover:text-white transition-colors disabled:opacity-50 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 text-xs text-gray-700 overflow-y-auto flex-1">
          <p className="text-[11px] text-gray-500 italic">Check fields to apply across all {selectedIds.length} selected orders.</p>
          
          {error && (
            <div className="p-2 bg-red-50 text-red-600 border border-red-200 rounded text-[11px] font-medium animate-in fade-in duration-150">
              {error}
            </div>
          )}

          {/* 1. DEADLINE */}
          <div className={`border rounded-lg p-3 transition-colors ${enabled.deadline ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={enabled.deadline} 
                disabled={!canEditDeadline || isSubmitting}
                onChange={(e) => setEnabled(p => ({ ...p, deadline: e.target.checked }))} 
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 w-4 h-4" 
              />
              <span className="font-bold flex-1">DEADLINE</span>
            </label>
            {canEditDeadline && enabled.deadline && (
              <input 
                type="date" 
                value={fields.deadline}
                disabled={isSubmitting}
                onChange={(e) => setFields(p => ({ ...p, deadline: e.target.value }))}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-amber-500 outline-none bg-white font-mono" 
              />
            )}
          </div>

          {/* 2. PICKUP OR SHIP */}
          <div className={`border rounded-lg p-3 transition-colors ${enabled.pickupOrShip ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={enabled.pickupOrShip} 
                disabled={!canEditPickupOrShip || isSubmitting}
                onChange={(e) => setEnabled(p => ({ ...p, pickupOrShip: e.target.checked }))} 
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 w-4 h-4" 
              />
              <span className="font-bold flex-1">PICKUP OR SHIP</span>
            </label>
            {canEditPickupOrShip && enabled.pickupOrShip && (
              <select
                value={fields.pickupOrShip}
                disabled={isSubmitting}
                onChange={(e) => setFields(p => ({ ...p, pickupOrShip: e.target.value }))}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-amber-500 outline-none bg-white font-medium"
              >
                {SHIPPING_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
          </div>

          {/* 3. PLANNED PRODUCTION */}
          <div className={`border rounded-lg p-3 transition-colors ${enabled.plannedProduction ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={enabled.plannedProduction} 
                disabled={!canEditPlannedProduction || isSubmitting}
                onChange={(e) => setEnabled(p => ({ ...p, plannedProduction: e.target.checked }))} 
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 w-4 h-4" 
              />
              <span className="font-bold flex-1">PLANNED PRODUCTION</span>
            </label>
            {canEditPlannedProduction && enabled.plannedProduction && (
              <div className="mt-2 space-y-2 pt-2 border-t border-amber-100">
                <div>
                  <input 
                    type="date" 
                    value={fields.plannedProductionDate}
                    disabled={isSubmitting}
                    onChange={(e) => setFields(p => ({ ...p, plannedProductionDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-amber-500 outline-none bg-white font-mono" 
                  />
                </div>
              </div>
            )}
          </div>

          {/* 4. PLANNED MATERIALS */}
          <div className={`border rounded-lg p-3 transition-colors ${enabled.plannedMaterials ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={enabled.plannedMaterials} 
                disabled={!canEditPlannedMaterials || isSubmitting}
                onChange={(e) => setEnabled(p => ({ ...p, plannedMaterials: e.target.checked }))} 
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 w-4 h-4" 
              />
              <span className="font-bold flex-1">PLANNED MATERIALS</span>
            </label>
            {canEditPlannedMaterials && enabled.plannedMaterials && (
              <div className="mt-2 space-y-2 pt-2 border-t border-amber-100">
                <div>
                  <input 
                    type="date" 
                    value={fields.plannedMaterialsDate}
                    disabled={isSubmitting}
                    onChange={(e) => setFields(p => ({ ...p, plannedMaterialsDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-amber-500 outline-none bg-white font-mono" 
                  />
                </div>
              </div>
            )}
          </div>

          {/* 5. ASSIGNED TO */}
          <div className={`border rounded-lg p-3 transition-colors ${enabled.assignedTo ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={enabled.assignedTo} 
                disabled={!canEditAssignedTo || isSubmitting}
                onChange={(e) => setEnabled(p => ({ ...p, assignedTo: e.target.checked }))} 
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 w-4 h-4" 
              />
              <span className="font-bold flex-1">ASSIGNED TO</span>
            </label>
            {canEditAssignedTo && enabled.assignedTo && (
              <select
                value={fields.assignedTo}
                disabled={isSubmitting}
                onChange={(e) => setFields(p => ({ ...p, assignedTo: e.target.value }))}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-amber-500 outline-none bg-white font-semibold"
              >
                <option value="Unassigned">— Unassigned</option>
                {employees.map(emp => (
                  <option key={emp._id || emp.name} value={emp.name}>{emp.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* 6. SHIPPED STATUS */}
          <div className={`border rounded-lg p-3 transition-colors ${enabled.shippingStatus ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={enabled.shippingStatus} 
                disabled={!canEditShippingStatus || isSubmitting}
                onChange={(e) => setEnabled(p => ({ ...p, shippingStatus: e.target.checked }))} 
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 w-4 h-4" 
              />
              <span className="font-bold flex-1">SHIPPED STATUS</span>
            </label>
            {canEditShippingStatus && enabled.shippingStatus && (
              <select
                value={fields.shippingStatus}
                disabled={isSubmitting}
                onChange={(e) => setFields(p => ({ ...p, shippingStatus: e.target.value }))}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-amber-500 outline-none bg-white font-semibold"
              >
                {SHIPPED_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
          </div>

          {/* 7. DEPARTURE DATE */}
          <div className={`border rounded-lg p-3 transition-colors ${enabled.departureDate ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={enabled.departureDate} 
                disabled={!canEditDepartureDate || isSubmitting}
                onChange={(e) => setEnabled(p => ({ ...p, departureDate: e.target.checked }))} 
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 w-4 h-4" 
              />
              <span className="font-bold flex-1">DEPARTURE DATE</span>
            </label>
            {canEditDepartureDate && enabled.departureDate && (
              <input 
                type="date" 
                value={fields.departureDate}
                disabled={isSubmitting}
                onChange={(e) => setFields(p => ({ ...p, departureDate: e.target.value }))}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-amber-500 outline-none bg-white font-mono" 
              />
            )}
          </div>

          {/* 8. ETA */}
          <div className={`border rounded-lg p-3 transition-colors ${enabled.eta ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={enabled.eta} 
                disabled={!canEditEta || isSubmitting}
                onChange={(e) => setEnabled(p => ({ ...p, eta: e.target.checked }))} 
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 w-4 h-4" 
              />
              <span className="font-bold flex-1">ETA</span>
            </label>
            {canEditEta && enabled.eta && (
              <input 
                type="date" 
                value={fields.eta}
                disabled={isSubmitting}
                onChange={(e) => setFields(p => ({ ...p, eta: e.target.value }))}
                className="w-full mt-2 px-3 py-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-amber-500 outline-none bg-white font-mono" 
              />
            )}
          </div>

          {/* 9. INVOICE SUBMISSION */}
          <div className={`border rounded-lg p-3 transition-colors ${enabled.accountingChecked ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input 
                type="checkbox" 
                checked={enabled.accountingChecked} 
                disabled={!canEditInvoiceSubmission || isSubmitting}
                onChange={(e) => setEnabled(p => ({ ...p, accountingChecked: e.target.checked }))} 
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500 w-4 h-4" 
              />
              <span className="font-bold flex-1">INVOICE SUBMISSION</span>
            </label>
            {canEditInvoiceSubmission && enabled.accountingChecked && (
              <div className="mt-2 space-y-2 pt-2 border-t border-amber-100">
                  <div>
                    <input 
                      type="date" 
                      value={fields.accountingDate}
                      disabled={isSubmitting}
                      onChange={(e) => setFields(p => ({ ...p, accountingDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-amber-500 outline-none bg-white font-mono" 
                    />
                  </div>
              </div>
            )}
          </div>

        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2 shrink-0">
          <button 
            onClick={onClose} 
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 cursor-pointer"
          >
            Cancel
          </button>
          <button 
            onClick={handleApply} 
            disabled={isSubmitting}
            className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors disabled:opacity-70 cursor-pointer"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <Check size={14} /> 
                Apply ({selectedIds.length})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Create Group Modal for grouping multiple sales orders into a family
function CreateGroupModal({ selectedCount, onClose, onCreate }) {
  const [parentPO, setParentPO] = useState('');

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-150">
        <div className="bg-indigo-600 p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link2 size={18} />
            <h3 className="font-bold text-sm">Create SO Family</h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 text-xs text-gray-700">
          <div className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg">
            <p className="font-bold text-indigo-900">📦 Grouping {selectedCount} sales orders as one family</p>
            <p className="mt-1 text-[10px] text-indigo-700">All rows will act as ONE — updating any field on the group row applies to all sub-SOs. Total amounts will sum up.</p>
          </div>
          <div>
            <label className="block font-bold mb-1 text-gray-700">Parent PO / Tag Number:</label>
            <input 
              type="text" 
              placeholder="e.g. PO-TJX-BULK-2025"
              value={parentPO} 
              onChange={(e) => setParentPO(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 outline-none font-mono font-bold uppercase" 
              autoFocus 
            />
            <p className="text-[10px] text-gray-500 mt-1">This will appear as the identifier on the virtual group header row.</p>
          </div>
        </div>
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-100 transition-colors">Cancel</button>
          <button 
            onClick={() => onCreate(parentPO.trim())}
            disabled={!parentPO.trim()}
            className={`px-5 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
              parentPO.trim() ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Check size={14} /> Create Family
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// OUTLOOK NOTIFICATIONS DRAWER COMPONENT
// ─────────────────────────────────────────────
function NotificationsPanel({
  open,
  onClose,
  notifications = [],
  onMarkAsRead,
  onMarkAllAsRead,
  userContext
}) {
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-out Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[9999] flex flex-col animate-in slide-in-from-right duration-200">
        
        {/* Header */}
        <div className="bg-gray-900 text-white p-4 sm:p-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <Bell size={20} className="text-indigo-400" />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-gray-900" />
              )}
            </div>
            <div>
              <h3 className="font-bold text-base sm:text-lg leading-tight">Notifications</h3>
              {/* }
              <p className="text-[11px] text-gray-400">
                {notifications.length === 0
                  ? 'All caught up'
                  : `${notifications.length} unread sales order${notifications.length === 1 ? '' : 's'}`}
              </p>
              {*/}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {
              /*
                notifications.length > 0 && (
                  <button
                    type="button"
                    onClick={onMarkAllAsRead}
                    className="px-2.5 py-1 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg flex items-center gap-1 transition-colors cursor-pointer"
                    title="Mark all as read"
                  >
                    <CheckCheck size={14} />
                    <span className="hidden sm:inline">Mark all read</span>
                  </button>
                )
              */
            }
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
              aria-label="Close notifications"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* User Scope Indicator */}
        {userContext?.allowedCustomers && userContext.allowedCustomers.length > 0 && (
          <div className="bg-slate-50 px-4 py-2 border-b border-gray-200 text-[11px] text-gray-600 flex items-center justify-between">
            <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">
              Active Scope:
            </span>
            <span className="font-bold text-indigo-700 truncate max-w-[260px]">
              {userContext.allowedCustomers.join(', ')}
            </span>
          </div>
        )}

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5 bg-slate-50/50">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-72 text-center p-6 space-y-2">
              <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-600 mb-1 border border-emerald-100">
                <CheckCircle2 size={24} />
              </div>
              <p className="font-bold text-gray-800 text-sm">You're all caught up!</p>
              <p className="text-xs text-gray-400 max-w-xs">
                No new or unread sales orders match your customer access scope right now.
              </p>
            </div>
          ) : (
            notifications.map((so) => (
              <div
                key={so.id}
                className="bg-white border border-gray-200 rounded-xl p-3.5 shadow-xs hover:border-indigo-300 transition-all flex items-start justify-between gap-3 relative group"
              >
                {/* Blue unread indicator bar */}
                <div className="absolute left-0 top-3 bottom-3 w-1 bg-indigo-600 rounded-r" />

                <div className="pl-1.5 min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-extrabold text-indigo-700 text-xs sm:text-sm">
                      {so.soNumber || so.salesOrderNumber}
                    </span>
                    {so.deadline && (
                      <span className="text-[10px] bg-slate-100 text-gray-600 font-semibold px-1.5 py-0.2 rounded border border-gray-200">
                        Due: {formatDateDisplay(so.deadline)}
                      </span>
                    )}
                  </div>

                  <p className="font-semibold text-gray-900 text-xs leading-snug break-words">
                    {so.customer}
                  </p>

                  <div className="flex items-center gap-3 text-[10px] text-gray-500 font-medium pt-0.5">
                    {so.poTagNumber && (
                      <span>Tag: <strong className="text-gray-700">{so.poTagNumber}</strong></span>
                    )}
                    <span>Amount: <strong className="text-gray-900">{formatCurrency(so.amount)}</strong></span>
                  </div>
                </div>

                {/* Mark as read Checkmark Button */}
                <button
                  type="button"
                  onClick={() => onMarkAsRead(so.id)}
                  title="Mark as read (dismiss)"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 border border-transparent hover:border-emerald-200 transition-all cursor-pointer shrink-0 mt-0.5"
                  aria-label={`Mark SO ${so.soNumber} as read`}
                >
                  <Check size={16} className="stroke-[2.5]" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* }
        <div className="p-3 bg-white border-t border-gray-200 flex justify-between items-center text-[11px] text-gray-500 px-4 shrink-0">
          <span>Persists per user profile</span>
          <button
            onClick={onClose}
            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-md transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
        {*/}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
// Settings DRAWER COMPONENT
// ─────────────────────────────────────────────
function SettingsPanel({ open, onClose, employees, onAddEmployee, onRemoveEmployee }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    const employeeName = name.trim();
    if (!employeeName || loading) return;

    try {
      setLoading(true);
      await onAddEmployee(employeeName.toUpperCase());
      setName("");
    } catch (error) {
      console.error("Failed to add employee:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (employee) => {
    if (loading) return;
    const confirmed = window.confirm(`Are you sure you want to remove ${employee.name}?`);
    if (!confirmed) return;

    try {
      setLoading(true);
      await onRemoveEmployee(employee._id);
    } catch (error) {
      console.error("Failed to remove employee:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 transition-opacity" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[9999] flex flex-col animate-in slide-in-from-right duration-200">
        <div className="bg-gray-900 text-white p-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Settings size={20} /> <h3 className="font-bold text-lg">Operations Settings</h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1 rounded hover:bg-white/10 transition-colors"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <UserCheck size={16} className="text-indigo-600" />
              <h4 className="font-bold text-sm text-gray-900">Fulfillment Employee Roster</h4>
            </div>
            <div className="flex gap-2 mb-4">
              <input type="text" placeholder="Employee name…" value={name} onChange={(e) => setName(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 outline-none uppercase font-semibold bg-white"
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()} />
              <button onClick={handleAdd} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-indigo-700 flex items-center gap-1 transition-colors">
                <Plus size={14} /> Add
              </button>
            </div>
            <div className="space-y-1.5">
              {employees.map((emp) => (
                <div
                  key={emp._id}
                  className="flex justify-between items-center bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg border border-gray-200 text-xs font-bold text-gray-800 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-[10px] font-bold">
                      {emp.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    {emp.name}
                  </span>

                  <button
                    onClick={() => handleRemove(emp)}
                    disabled={loading}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded disabled:opacity-50 transition-all"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}

              {employees.length === 0 && (
                <div className="text-center text-xs text-gray-400 py-6">
                  No employees added yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
// CustomDate Picker COMPONENT
// ─────────────────────────────────────────────
function CustomDatePicker({ value, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const modalRef = useRef(null);

  const initialDate = value ? new Date(value + 'T00:00:00') : new Date();
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());

  // Sync view month/year whenever modal opens
  useEffect(() => {
    if (isOpen) {
      const d = value ? new Date(value + 'T00:00:00') : new Date();
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [isOpen, value]);

  // Close on Escape key + lock background scroll while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(prev => prev - 1);
    } else {
      setViewMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(prev => prev + 1);
    } else {
      setViewMonth(prev => prev + 1);
    }
  };

  // Auto-save: selecting a day saves and closes
  const handleSelectDay = (day) => {
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    const dateStr = `${viewYear}-${m}-${d}`;
    onChange(dateStr);
    setIsOpen(false);
  };

  // Auto-clear: clears and closes
  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
  };

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();

  const days = [];
  for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const formatDisplay = (val) => {
    if (!val) return 'Select date';
    const [y, m, d] = val.split('-');
    if (!y || !m || !d) return val;
    const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const modal = (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        // Close only when clicking the backdrop itself, not the calendar
        if (modalRef.current && !modalRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      }}
    >
      <div
        ref={modalRef}
        className="relative bg-white border border-gray-200 rounded-xl shadow-2xl p-4 w-[90vw] max-w-[320px] text-xs animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Close (X) */}
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-sm px-1"
          title="Close"
        >
          ✕
        </button>

        {/* Month Header Navigation */}
        <div className="flex items-center justify-between mb-3 mt-3 px-1">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="font-bold text-gray-800 text-sm">
            {monthNames[viewMonth]} {viewYear}
          </span>
          <button
            type="button"
            onClick={handleNextMonth}
            className="p-1.5 hover:bg-gray-100 rounded text-gray-600 transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Weekday Labels */}
        <div className="grid grid-cols-7 gap-1 text-center font-bold text-gray-400 text-[11px] mb-1">
          <span>Su</span><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span>
        </div>

        {/* Day Grid */}
        <div className="grid grid-cols-7 gap-1 text-center mb-3">
          {days.map((day, idx) => {
            if (!day) return <div key={`empty-${idx}`} />;

            const dayStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = value === dayStr;

            return (
              <button
                key={day}
                type="button"
                onClick={() => handleSelectDay(day)}
                className={`py-2 rounded text-xs font-medium transition-colors ${
                  isSelected
                    ? 'bg-indigo-600 text-white font-bold'
                    : 'hover:bg-indigo-50 active:bg-indigo-100 text-gray-700'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Clear Button */}
        <div className="flex items-center justify-center pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={ handleClear }
            className="text-gray-500 hover:bg-red-50 hover:text-red-600 font-bold py-2 px-3 w-full rounded transition-colors text-center"
          >
            Clear selection
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative inline-block w-full" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(true)}
        className="w-full px-2 py-1 border border-gray-200 rounded text-[11px] font-semibold bg-white text-left flex items-center justify-between outline-none focus:ring-1 focus:ring-indigo-500 hover:border-indigo-300 transition-colors"
      >
        <span className={value ? 'text-gray-900 font-mono' : 'text-gray-400'}>
          {formatDisplay(value)}
        </span>
        <CalendarIcon size={12} className="text-gray-400 shrink-0 ml-1" />
      </button>

      {isOpen && createPortal(modal, document.body)}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
export default function WeeklyPlanner() {
  const [activeTab, setActiveTab] = useState('table');
  const [orders, setOrders] = useState(STATIC_ORDERS);
  const [loading, setLoading] = useState(false);
  const [isSorting, setIsSorting] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [savingId, setSavingId] = useState(null); 
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [apiSuccess, setApiSuccess] = useState('');
  const [apiError, setApiError] = useState('');
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [employees, setEmployees] = useState([]);

  // Bulk permissions
  const [canGroupFamily, setCanGroupFamily] = useState(false);
  const [bulkEdit, setBulkEdit] = useState(false);
  const [bulkMarkComplete, setBulkMarkComplete] = useState(false);
  const [bulkDelete, setBulkDelete] = useState(false);

  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const handleErpagDownload = async (type, so, plNumber = '') => {
    if (!so) return;
    const soNum = so.soNumber || so.salesOrderNumber || '';
    const targetPl = plNumber || (Array.isArray(so.packingLists) && so.packingLists.length > 0 ? so.packingLists[0] : '');
    const downloadKey = `${type}-${so.id}-${targetPl || soNum}`;

    try {
      setDownloadingDoc(downloadKey);

      // 1. Check if api.OperationsPlanner has dedicated ERPAG download methods
      if (type === 'so' && api?.OperationsPlanner?.downloadErpagSO) {
        const res = await api.OperationsPlanner.downloadErpagSO({ orderId: so.id, soNumber: soNum });
        if (res?.fileUrl || res?.url) {
          window.open(res.fileUrl || res.url, '_blank', 'noopener,noreferrer');
          return;
        }
        if (res instanceof Blob) {
          const url = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          a.href = url;
          a.download = `SO_${soNum || so.id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          return;
        }
      }

      if (type === 'pl' && api?.OperationsPlanner?.downloadErpagPackingList) {
        const res = await api.OperationsPlanner.downloadErpagPackingList({
          orderId: so.id,
          soNumber: soNum,
          packingListNumber: targetPl,
        });
        if (res?.fileUrl || res?.url) {
          window.open(res.fileUrl || res.url, '_blank', 'noopener,noreferrer');
          return;
        }
        if (res instanceof Blob) {
          const url = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          a.href = url;
          a.download = `PackingList_${targetPl || soNum || so.id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          return;
        }
      }

      // 2. Fallback to backend API endpoint for ERPAG document download
      const params = new URLSearchParams({
        type: type === 'so' ? 'so' : 'pl',
        orderId: so.id || '',
        soNumber: soNum,
        ...(targetPl ? { packingListNumber: targetPl } : {}),
      });

      const response = await fetch(`${API_BASE_URL}/weeklyplanner/erpag/download?${params.toString()}`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to download ${type === 'so' ? 'Sales Order' : 'Packing List'} from ERPAG.`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        if (data.fileUrl || data.url || data.downloadUrl) {
          window.open(data.fileUrl || data.url || data.downloadUrl, '_blank', 'noopener,noreferrer');
        } else if (data.base64) {
          const link = document.createElement('a');
          link.href = `data:application/pdf;base64,${data.base64}`;
          link.download = type === 'so' ? `SO_${soNum || so.id}.pdf` : `PackingList_${targetPl || soNum || so.id}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else {
          throw new Error('No downloadable file returned from ERPAG.');
        }
      } else {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = type === 'so' ? `SO_${soNum || so.id}.pdf` : `PackingList_${targetPl || soNum || so.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error(`ERPAG ${type.toUpperCase()} download error:`, err);
      alert(err.message || `Unable to download ${type === 'so' ? 'Sales Order' : 'Packing List'} from ERPAG.`);
    } finally {
      setDownloadingDoc(null);
    }
  };  

  // ─────────────────────────────────────────────
  // PERMISSIONS CHECK
  // ─────────────────────────────────────────────
  const [permissions] = useState(() => {
    try {
      const ks = JSON.parse(localStorage.getItem('permissions')) || {};

      // Bulk permissions
      setCanGroupFamily(ks?.canGroupFamily === 'write');
      setBulkEdit(ks?.allowBulkEdit === 'write');
      setBulkMarkComplete(ks?.allowBulkMarkComplete === 'write');
      setBulkDelete(ks?.allowBulkDelete === 'write');

      return ks?.ERPagCustomerShippingSchedule === 'write';
    } catch (e) {
      console.error("Invalid permissions JSON in localStorage:", e);
      return false;
    }
  });

  useEffect(() => {
    fetchEmployees();
  }, []);
  const fetchEmployees = async () => {
    try {
      const data = await api.OperationsPlanner.list();
      if (data.success) {
        setEmployees(data.employees);
      }
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    }
  };

  const handleAddEmployee = async (name) => {
    if (!permissions) {
      alert("Permission denied. You do not have write access.");
      return;
    }    
    const data = await api.OperationsPlanner.create({ name });
    if (!data.success) {
      throw new Error(data.message || "Failed to add employee");
    }
    setEmployees((prev) => [...prev, data.employee]);
    return data.employee;
  };

  const handleRemoveEmployee = async (employeeId) => {
    if (!permissions) {
      alert("Permission denied. You do not have write access.");
      return;
    }    
    const data = await api.OperationsPlanner.removeMember(employeeId);
    if (!data.success) {
      throw new Error(data.message || "Failed to remove employee");
    }
    setEmployees((prev) => prev.filter((employee) => employee._id !== employeeId));
  }; 

  const [customerShipPref, setCustomerShipPref] = useState(INITIAL_CUSTOMER_SHIP_PREFS);
  const [dataSource, setDataSource] = useState('static');
  const [manualGroups, setManualGroups] = useState({});
  const [groupTagOverrides, setGroupTagOverrides] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState('All');
  const [assignedFilter, setAssignedFilter] = useState('All');
  const [groupingMode, setGroupingMode] = useState('smart');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [prepaymentModalOrder, setPrepaymentModalOrder] = useState(null);
  const [editModalOrder, setEditModalOrder] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [bulkEditIds, setBulkEditIds] = useState(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [calendarRangeWeeks, setCalendarRangeWeeks] = useState(4);
  const [calendarWeekOffset, setCalendarWeekOffset] = useState(0);

  const [localOverrides, setLocalOverrides] = useState({});
  const mergedOrders = useMemo(() => {
    return orders.map(order => {
      const ov = localOverrides[order.id];
      return ov ? { ...order, ...ov } : order;
    });
  }, [orders, localOverrides]);
  const [sortConfig, setSortConfig] = useState({ key: 'soDate', direction: 'DESC' });

  // ─── NOTIFICATIONS STATE (DB BACKED) ───
  const [showNotifications, setShowNotifications] = useState(false);
  const userAccess = useMemo(() => getUserAccessContext(), []);
  const [readNotificationIds, setReadNotificationIds] = useState(new Set());  

  const handleSortRequest = (key) => {
    setIsSorting(true);
    setTimeout(() => {
      let direction = 'asc';
      if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
      setSortConfig({ key, direction });
      setIsSorting(false);
    }, 100);
  };

  const fetchWeeklyPlannerData = useCallback(async (rangeWeeks, weekOffset) => {
    setLoading(true); setApiError(''); setApiSuccess('');
    try {
      const params = new URLSearchParams({ rangeWeeks: String(rangeWeeks), weekOffset: String(weekOffset) }).toString();
      const res = await fetch(`${API_BASE_URL}/weeklyplanner/weekly-planner?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.success && json.data?.length > 0) {
        const { data, revenueMap = {}, unitsMap = {}, itemCountMap = {} } = json;
        const mapped = data.map(raw => mapApiOrderToPlanner(raw, revenueMap, unitsMap, itemCountMap));
        
        setOrders(mapped); 
        setDataSource('api');
        setLocalOverrides({});

        // Load the user's read notification IDs
        if (userAccess.userId) {
          const dbReadIds = await fetchUserReadNotificationsFromDb(
            userAccess.userId
          );

          setReadNotificationIds(dbReadIds);
        }

      } else {
        setApiError('No orders in this window.');
        setOrders(STATIC_ORDERS); 
        setDataSource('static');
      }
    } catch (err) {
      setApiError('API unreachable.');
      setOrders(STATIC_ORDERS); setDataSource('static');
    } finally { setLoading(false); }
  }, [userAccess.userId]);

  // Fetch family groups from the API and normalize them into an object keyed by _id
  const fetchFamilyGroups = useCallback(async () => {
    try {
      const data = await api.OperationsPlanner.listFamilyGroups();
      if (data?.success && Array.isArray(data.groups)) {
        // Convert DB array into object keyed by _id so existing logic keeps working
        const normalized = {};
        data.groups.forEach(g => {
          normalized[g._id] = {
            id: g._id,
            parentPO: g.parentPO,
            soNumbers: g.soNumbers || [],
            orderIds: g.orderIds || []
          };
        });
        setManualGroups(normalized);
      }
    } catch (err) {
      console.error('Failed to fetch family groups:', err);
    }
  }, []);
  useEffect(() => {
    fetchFamilyGroups();
  }, [fetchFamilyGroups]);  

  // Import SO by Order Number  
  const setImportSO = async () => {
    if (!permissions) {
      setApiError('Permission denied: You do not have write permissions to import SOs.');
      return;
    }    
    const importId = importInputRef.current?.value.trim().toUpperCase();
    if (!importId) {
      setApiError('Please enter SO Id.');
      return;
    }
    setImporting(true);
    setApiError('');
    try {
      const response = await api.OperationsPlanner.importSoByOrderNumber({ soNumber: importId });
      if(!response?.success) {
        setApiError(response?.message || 'Import failed.');        
      } else {
        if (importInputRef.current) {
          importInputRef.current.value = '';
        }        
        setApiSuccess('Order imported successfully!');        
        setLoading(true); 
        fetchWeeklyPlannerData(calendarRangeWeeks, calendarWeekOffset);        
      }
    } catch (error) {      
      console.error('Import SO error:', error);
    }
    setImporting(false);
  };

  const fetchCompletedOrders = useCallback(async (page = 1) => {    
    setLoadingCompleted(true);
    try {

      // const res = await fetch(`${API_BASE_URL}/weeklyplanner/completed-orders?page=${page}&limit=25&days=180`);

      const payload = { page, limit: 25, days: 180 };
      const data = await api.OperationsPlanner.completedHistory(payload);
      if (!data.ok) throw new Error(`HTTP ${data.status}`);
      const json = await data.json();
      if (json.success) {
        const { data, revenueMap = {} } = json;
        const mapped = data.map(raw => mapApiOrderToPlanner(raw, revenueMap));
        setCompletedOrders(mapped);
        setPaginationMeta(json.meta); 
      }
    } catch (err) {
      console.error("Error fetching completed orders:", err);
    } finally {
      setLoadingCompleted(false);
    }
  }, []);

  useEffect(() => {
    fetchWeeklyPlannerData(calendarRangeWeeks, calendarWeekOffset);
  }, [calendarRangeWeeks, calendarWeekOffset]);

  useEffect(() => {
    if (activeTab === 'completed') fetchCompletedOrders();
  }, [activeTab]);

  const activeOrders = useMemo(() => mergedOrders.filter(so => !so.completed && !so.isReturned), [mergedOrders]);

  // ─────────────────────────────────────────────
  // REFINED "NOT COMPLETED" FILTER LOGIC
  // ─────────────────────────────────────────────
  const [taskFilters, setTaskFilters] = useState({
    notPlanned: false,
    missingMaterials: false,
    notPacked: false,
    notShipped: false,
    notInvoiced: false,
    requiresInvoiceSub: false,
  });

  const isAnyTaskFilterActive = useMemo(() => Object.values(taskFilters).some(Boolean), [taskFilters]);

  const toggleTaskFilter = (filterKey) => {
    setTaskFilters(prev => ({ ...prev, [filterKey]: !prev[filterKey] }));
  };

  const clearTaskFilters = () => {
    setTaskFilters({
      notPlanned: false,
      missingMaterials: false,
      notPacked: false,
      notShipped: false,
      notInvoiced: false,
      requiresInvoiceSub: false,
    });
  };

  // Helper logic for incomplete states
  const isNotPlanned = (so) => !so.plannedProduction;
  const isMissingMaterials = (so) => !so.plannedMaterials;
  // const isNotPacked = (so) => !(so.status || '').trim(); // 'status' tracks Packed list #
    // ✅ Correct check for packed status
  const isNotPacked = (so) => !so.packingLists || String(so.packingLists).trim() === '';
  const isNotShipped = (so) => {
    const s = (so.shippingStatus || '').trim().toLowerCase();
    return s !== 'shipped' && s !== 'delivered';
  };
  const isNotInvoiced = (so) => !(so.invoiceNumber || '').trim();
  const isRequiresInvoiceSub = (so) => !so.accountingChecked;

  // Live count of incomplete items per category across active orders
  const openCounts = useMemo(() => {
    return {
      notPlanned: activeOrders.filter(isNotPlanned).length,
      missingMaterials: activeOrders.filter(isMissingMaterials).length,
      notPacked: activeOrders.filter(isNotPacked).length,
      notShipped: activeOrders.filter(isNotShipped).length,
      notInvoiced: activeOrders.filter(isNotInvoiced).length,
      requiresInvoiceSub: activeOrders.filter(isRequiresInvoiceSub).length,
    };
  }, [activeOrders]);

  const filteredActiveOrders = useMemo(() => {
    return activeOrders.filter(so => {
      // 1. Text Search Filter
      const t = searchTerm.trim().toLowerCase();
      const assignedVal = typeof so.assignedTo === 'string' ? so.assignedTo : (so.assignedTo?.name || '');
      
      // ─── Resolve Manual Family Tag ───
      let manualFamilyTag = '';
      Object.values(manualGroups).forEach(mg => {
        const isIdMatch = mg.orderIds?.includes(so.id) || mg.orderIds?.includes(so._id);
        const isSoMatch = mg.soNumbers?.includes(so.salesOrderNumber) || mg.soNumbers?.includes(so.soNumber);
        
        if (isIdMatch || isSoMatch) {
          const groupKey = `manual-${mg.id}`;
          manualFamilyTag = (groupTagOverrides[groupKey] || mg.parentPO || '').toLowerCase();
        }
      });

      // ─── Resolve Auto Family Tag ───
      let autoFamilyTag = '';
      const autoMeta = getAutoGroupMeta(so);
      if (autoMeta) {
        const groupKey = autoMeta.key;
        let familyName = groupTagOverrides[groupKey] || autoMeta.familyName;

        // Resolve Marmaxx dynamic min-max tag matches if needed
        if (!familyName && groupKey.startsWith('auto-mm::')) {
          const bucketOrders = activeOrders.filter(o => {
            const m = getAutoGroupMeta(o);
            return m && m.key === groupKey;
          });
          const cores = bucketOrders
            .map(o => {
              const m = (o.poTagNumber || o.tag || '').match(/(\d{6})/);
              return m ? m[1] : null;
            })
            .filter(Boolean)
            .sort();
          if (cores.length > 0) {
            const min = cores[0];
            const max = cores[cores.length - 1];
            familyName = min === max ? min : `${min}-${max}`;
          }
        }
        if (!familyName) {
          familyName = so.poTagNumber || so.soDate || 'FAMILY';
        }
        autoFamilyTag = familyName.toLowerCase();
      }

      // Check standard fields AND the resolved group family tags
      const matchSearch = !t || 
        (so.soNumber || '').toLowerCase().includes(t) ||
        (so.poTagNumber || '').toLowerCase().includes(t) ||
        (so.customer || '').toLowerCase().includes(t) ||
        (assignedVal && assignedVal.toLowerCase().includes(t)) ||
        manualFamilyTag.includes(t) ||
        autoFamilyTag.includes(t);

      // 2. Customer Filter
      const matchCust = customerFilter === 'All' || so.customer === customerFilter;

      // 3. Assigned Staff Filter
      const matchAssign = assignedFilter === 'All' ||
        (assignedFilter === 'Unassigned' ? (!so.assignedTo || so.assignedTo === 'Unassigned') : assignedVal === assignedFilter);

      // 4. Incomplete Fields Status Filters (AND Condition)
      const matchNotPlanned = !taskFilters.notPlanned || isNotPlanned(so);
      const matchMissingMaterials = !taskFilters.missingMaterials || isMissingMaterials(so);
      const matchNotPacked = !taskFilters.notPacked || isNotPacked(so);
      const matchNotShipped = !taskFilters.notShipped || isNotShipped(so);
      const matchNotInvoiced = !taskFilters.notInvoiced || isNotInvoiced(so);
      const matchRequiresInvoiceSub = !taskFilters.requiresInvoiceSub || isRequiresInvoiceSub(so);

      return matchSearch && matchCust && matchAssign && 
             matchNotPlanned && matchMissingMaterials && matchNotPacked && 
             matchNotShipped && matchNotInvoiced && matchRequiresInvoiceSub;
    });
  }, [
    activeOrders, 
    searchTerm, 
    customerFilter, 
    assignedFilter, 
    taskFilters, 
    manualGroups,       // Added dependency
    groupTagOverrides   // Added dependency
  ]);
    
  // Remove manual group by ID
  const handleRemoveManualGroup = async (groupId) => {
    if (!permissions) {
      alert("Permission denied. You do not have write access.");
      return;
    }    
    const confirmed = window.confirm(`Are you sure you want to ungroup this family?`);
    if (!confirmed) return;

    try {
      const response = await api.OperationsPlanner.deleteFamilyGroup(groupId);
      if (!response?.success) {
        setApiError(response?.message || 'Failed to delete family group.');
        return;
      }
      setManualGroups(prev => {
        const updated = { ...prev };
        delete updated[groupId];
        return updated;
      });
      setApiSuccess('Family group removed.');
    } catch (err) {
      setApiError(err?.message || 'Failed to delete family group.');
    }
  };  

  const rowStructure = useMemo(() => {
    const getSortValue = (item) => {
      // For a group, use the earliest (asc) / latest (desc) child value so the family
      // slots into the timeline like a normal order
      if (item._isGroup) {
        const values = item.orders
          .map(o => o[sortConfig.key] || '')
          .filter(Boolean)
          .sort();
        if (values.length === 0) return '';
        return sortConfig.direction === 'asc' ? values[0] : values[values.length - 1];
      }
      return item[sortConfig.key] || '';
    };

    const sortRows = (rows) => {
      return [...rows].sort((a, b) => {
        const aVal = getSortValue(a);
        const bVal = getSortValue(b);
        if (aVal === bVal) return 0;
        if (sortConfig.direction === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
      });
    };

    // Flat mode: no families
    if (groupingMode === 'flat') {
      return {
        rows: sortRows(filteredActiveOrders.map(o => ({ ...o, _isGroup: false })))
      };
    }

    const groups = [];
    const usedIds = new Set();

    // Manual groups
    Object.values(manualGroups).forEach(mg => {
      const ords = filteredActiveOrders.filter(o => mg.orderIds.includes(o.id));
      if (ords.length === 0) return;
      ords.forEach(o => usedIds.add(o.id));
      
      const groupKey = `manual-${mg.id}`;
      const familyTag = groupTagOverrides[groupKey] || mg.parentPO;

      // FIXED: We pass the unmodified `ords` array directly to preserve original child tags!
      groups.push({
        key: groupKey,
        type: 'manual',
        parentPO: familyTag,
        orders: ords, // <-- Keep original individual SOs without overwriting their poTagNumber!
        poTagNumber: familyTag,
        _isGroup: true
      });
    });

    // Auto PO groups
    // ─── AUTO GROUPS (customer-specific rules) ──────────────────────
    const autoBuckets = {}; // key → { ords: [], meta: {} }

    filteredActiveOrders.forEach(so => {
      if (usedIds.has(so.id)) return;

      const meta = getAutoGroupMeta(so);
      if (!meta) return;

      if (!autoBuckets[meta.key]) {
        autoBuckets[meta.key] = { ords: [], meta };
      }
      autoBuckets[meta.key].ords.push(so);
    });

    Object.entries(autoBuckets).forEach(([bucketKey, bucket]) => {
      const ords = bucket.ords;
      if (ords.length < 2) return; // need 2+ SOs to form a family

      ords.forEach(o => usedIds.add(o.id));

      const groupKey = bucketKey; // already prefixed with auto-...
      let familyName = groupTagOverrides[groupKey] || bucket.meta.familyName;

      // Marmaxx: build family name as minPO-maxPO from extracted cores
      if (!familyName && bucket.meta.key.startsWith('auto-mm::')) {
        const cores = ords
          .map(o => {
            const m = (o.poTagNumber || o.tag || '').match(/(\d{6})/);
            return m ? m[1] : null;
          })
          .filter(Boolean)
          .sort();
        if (cores.length > 0) {
          const min = cores[0];
          const max = cores[cores.length - 1];
          familyName = min === max ? min : `${min}-${max}`;
        } else {
          familyName = ords[0].poTagNumber || 'FAMILY';
        }
      }

      // Fallbacks
      if (!familyName) {
        familyName = ords[0].poTagNumber || ords[0].soDate || 'FAMILY';
      }

      groups.push({
        key: groupKey,
        type: 'auto',
        parentPO: familyName,
        poTagNumber: familyName,
        customer: ords[0].customer,
        orders: ords,
        _isGroup: true
      });
    });

    // Standalone orders
    const standalone = filteredActiveOrders
      .filter(o => !usedIds.has(o.id))
      .map(o => ({ ...o, _isGroup: false }));

    // ONE list — families and singles sorted together by date
    const rows = sortRows([...groups, ...standalone]);

    return { rows };
  }, [filteredActiveOrders, groupingMode, manualGroups, groupTagOverrides, sortConfig]);

  // Handle Update SO Fields
  const handleUpdateOrderField = (orderId, field, value, childIds = null) => {
    const idsToUpdate = childIds && childIds.length > 0 ? childIds : [orderId];

    if (field === 'deliveryApptPayload') {
      setLocalOverrides(prev => {
        const updated = { ...prev };
        idsToUpdate.forEach(id => {
          updated[id] = { 
            ...(prev[id] || {}), 
            needsDeliveryAppt: value.needsDeliveryAppt,
            deliveryApptDate: value.deliveryApptDate,
            deliveryApptTime: value.deliveryApptTime,
            deliveryConfirmationNo: value.deliveryConfirmationNo,
            deliveryApptConfirmed: value.deliveryApptConfirmed
          };
        });
        return updated;
      });

      setOrders(prev => prev.map(so => {
        if (!idsToUpdate.includes(so.id)) return so;
        return {
          ...so,
          needsDeliveryAppt: value.needsDeliveryAppt,
          deliveryApptDate: value.deliveryApptDate,
          deliveryApptTime: value.deliveryApptTime,
          deliveryConfirmationNo: value.deliveryConfirmationNo,
          deliveryApptConfirmed: value.deliveryApptConfirmed
        };
      }));
      return;
    }

    setLocalOverrides(prev => {
      const updated = { ...prev };
      idsToUpdate.forEach(id => {
        const payloadFields = { [field]: value };
        if (field === 'accountingChecked') {
          payloadFields.accountingChecked = value;
          payloadFields.accountingDate = value ? getTodayStr() : '';
        }
        updated[id] = { ...(prev[id] || {}), ...payloadFields };
      });
      return updated;
    });

    setOrders(prev => prev.map(so => {
      if (!idsToUpdate.includes(so.id)) return so;
      const updated = { ...so, [field]: value };
      if (field === 'pickupOrShip') setCustomerShipPref(p => ({ ...p, [so.customer]: value }));
      if (field === 'deadline') updated.leadTime = calculateLeadTimeInDays(so.soDate, value);
      if (field === 'accountingChecked') {
        updated.accountingChecked = value;
        updated.accountingDate = value ? getTodayStr() : '';
      }
      return updated;
    }));
  };
  
  // Handle Persist Update 
  const handlePersistUpdate = async (orderId, field, value, childIds = null) => {
    if (!permissions) {
      setApiError("Permission denied. You do not have write access to modify sales orders.");
      return;
    }    
    handleUpdateOrderField(orderId, field, value, childIds);
    const idsToUpdate = childIds && childIds.length > 0 ? childIds : [orderId];
    const payload = { orderIds: idsToUpdate, [field]: value };
    if (field === 'accountingChecked') {
      payload.accountingDate = value ? getTodayStr() : '';
    }
    setApiError(''); 
    setApiSuccess(''); 
    setSavingId(orderId); 
    try {
      const response = await api.OperationsPlanner.handlePersistUpdate(payload);      
      if (!response?.success) {
        setApiError(response?.message || 'Server error saving update.');
      } else {
        setApiSuccess('Field updated successfully.');

        // If updating trackingLink, reflect returned shipping status immediately
        const newShippingStatus = response?.shippingStatus || response?.data?.shippingStatus;
        if (field === 'trackingLink' && newShippingStatus) {
          handleUpdateOrderField(orderId, 'shippingStatus', newShippingStatus, childIds);
        }
      }
    } catch (error) { 
      setApiError("Failed to save changes."); 
    }
    setSavingId(null); 
  };   

  // Prepayment Feature
  const handleConfirmPrepaymentReceived = async (orderId, receivedDate, adjustedDeadline) => {  
    if (!permissions) {
      alert("Permission denied. You do not have write access.");
      return;
    }      
    setConfirm(true);
    const isGroup = prepaymentModalOrder?.isGroupRow;
    const idsToUpdate = isGroup ? prepaymentModalOrder.childOrderIds : [orderId];
    const overrideFields = { 
        orderIds: idsToUpdate, prepaymentReceived: true, prepaymentReceivedDate: receivedDate, deadline: adjustedDeadline 
    };
    try {
      const response = await api.OperationsPlanner.confirmReceived(overrideFields); 
      if (response && response.success) {
        setLocalOverrides(prev => {
          const updated = { ...prev };
          idsToUpdate.forEach(id => {
            const originalOrder = orders.find(o => o.id === id);
            updated[id] = { ...(prev[id] || {}), prepaymentReceived: true, prepaymentReceivedDate: receivedDate, deadline: adjustedDeadline,
              leadTime: originalOrder ? calculateLeadTimeInDays(originalOrder.soDate, adjustedDeadline) : 0
            };
          });
          return updated;
        });
        setOrders(prev => prev.map(so => idsToUpdate.includes(so.id) ? { ...so, prepaymentReceived: true, prepaymentReceivedDate: receivedDate, deadline: adjustedDeadline, leadTime: calculateLeadTimeInDays(so.soDate, adjustedDeadline) } : so));
        setPrepaymentModalOrder(null);          
      }
    } catch (error) {
      alert(error?.message || "Failed to update prepayment status.");
    } finally { setConfirm(false); }
  };  

  const handleToggleSelectOrder = (orderIdOrArray, selectedFlag = null) => {
    if (Array.isArray(orderIdOrArray)) {
      setSelectedOrderIds(prev => selectedFlag ? [...new Set([...prev, ...orderIdOrArray])] : prev.filter(id => !orderIdOrArray.includes(id)));
    } else {
      setSelectedOrderIds(prev => prev.includes(orderIdOrArray) ? prev.filter(id => id !== orderIdOrArray) : [...prev, orderIdOrArray]);
    }
  };

  const handleSelectAllActive = (e) => {
    if (e.target.checked) {
      // Collect every visible order ID (including children inside groups)
      const allIds = filteredActiveOrders.map(so => so.id);
      setSelectedOrderIds(allIds);
    } else {
      // Fully clear selection
      setSelectedOrderIds([]);
    }
  };

  // Handle creating a manual family group from selected orders
  const handleCreateManualGroup = async (parentPO) => {
    if (!permissions) {
      alert("Permission denied. You do not have write access.");
      return;
    }    
    if (selectedOrderIds.length < 2 || !parentPO) return;

    // Capture stable Sales Order Numbers
    const selectedSOs = orders
      .filter(o => selectedOrderIds.includes(o.id))
      .map(o => o.soNumber)
      .filter(Boolean);

    if (selectedSOs.length < 2) {
      setApiError('At least 2 valid Sales Orders are required to form a family.');
      return;
    }

    try {
      const payload = {
        parentPO,
        soNumbers: selectedSOs,
        orderIds: [...selectedOrderIds]
      };
      const response = await api.OperationsPlanner.createFamilyGroup(payload);

      if (!response?.success) {
        setApiError(response?.message || 'Failed to create family group.');
        return;
      }

      const g = response.group;
      setManualGroups(prev => ({
        ...prev,
        [g._id]: {
          id: g._id,
          parentPO: g.parentPO,
          soNumbers: g.soNumbers || [],
          orderIds: g.orderIds || []
        }
      }));

      setApiSuccess(`Family "${g.parentPO}" created with ${g.soNumbers.length} orders.`);
      setSelectedOrderIds([]);
      setShowCreateGroupModal(false);
    } catch (err) {
      setApiError(err?.message || 'Failed to create family group.');
    }
  };  

  const handleUngroup = async (groupKey) => {
    if (!permissions) {
      alert("Permission denied. You do not have write access.");
      return;
    }    

    if (groupKey.startsWith('manual-')) {
      const groupId = groupKey.replace('manual-', '');
      try {
        const response = await api.OperationsPlanner.deleteFamilyGroup(groupId);
        if (!response?.success) {
          setApiError(response?.message || 'Failed to ungroup family.');
          return;
        }
        setManualGroups(prev => {
          const updated = { ...prev };
          delete updated[groupId];
          return updated;
        });
        setApiSuccess('Family ungrouped successfully.');
      } catch (err) {
        setApiError(err?.message || 'Failed to ungroup family.');
      }
    }

    setGroupTagOverrides(prev => {
      const updated = { ...prev };
      delete updated[groupKey];
      return updated;
    });
  };

  const handleEditGroupTag = async (groupKey, newTag) => {
    if (!permissions) return;
    if (!newTag || !newTag.trim()) return;

    setGroupTagOverrides(prev => ({ ...prev, [groupKey]: newTag }));

    if (groupKey.startsWith('manual-')) {
      const groupId = groupKey.replace('manual-', '');
      try {
        const response = await api.OperationsPlanner.updateFamilyGroup(groupId, { parentPO: newTag });
        if (!response?.success) {
          setApiError(response?.message || 'Failed to update family tag.');
          return;
        }
        setManualGroups(prev => ({
          ...prev,
          [groupId]: { 
            ...prev[groupId], 
            parentPO: newTag.trim().toUpperCase() 
          }
        }));
        setApiSuccess('Family tag updated.');
      } catch (err) {
        setApiError(err?.message || 'Failed to update family tag.');
      }
    }
  };  

  const handleBulkEditApply = (changes) => {
    if (!permissions) {
      alert("Permission denied. You do not have write access.");
      return;
    }    
    if (!bulkEditIds || bulkEditIds.length === 0) return;
    setLocalOverrides(prev => {
      const updated = { ...prev };
      bulkEditIds.forEach(id => { updated[id] = { ...(prev[id] || {}), ...changes }; });
      return updated;
    });
    setOrders(prev => prev.map(so => bulkEditIds.includes(so.id) ? { ...so, ...changes } : so));
    setBulkEditIds(null); setSelectedOrderIds([]);
  };

  // Handle Mark Complete and Undo Complete Feature
  const handleCompleteOrder = async (orderId, childIds = null, undo = null, fullOrderObject = null) => {
      if (!permissions) {
        alert("Permission denied. You do not have write access.");
        return;
      }    
      const idsToComplete = childIds && childIds.length > 0 ? childIds : [orderId];
      
      // 1. Dynamic confirmation message
      const confirmMessage = undo 
        ? `Are you sure you want to undo and move this back to active orders?`
        : `Are you sure you want to mark this as Done?`;

      if (!window.confirm(confirmMessage)) return;

      // 2. Update fields
      const completeFields = { 
        completed: !undo ? true : false, 
        completedAt: !undo ? getTodayDateTimeStr() : '' 
      };

      try {
        const response = await api.OperationsPlanner.update({ orderIds: idsToComplete, ...completeFields });
        if (!response?.success) throw new Error(`Failed to ${undo ? 'undo' : 'complete'} order(s)`);
        
        // Update local overrides
        setLocalOverrides(prev => {
          const updated = { ...prev };
          idsToComplete.forEach(id => { updated[id] = { ...(prev[id] || {}), ...completeFields }; });
          return updated;
        });       

        // 3. INSTANT UI TRANSFER LOGIC
        if (undo) {
          setOrders(prev => {
            const exists = prev.some(so => idsToComplete.includes(so.id));
            if (exists) {
              return prev.map(so => idsToComplete.includes(so.id) ? { ...so, ...completeFields } : so);
            } else if (fullOrderObject) {
              return [{ ...fullOrderObject, ...completeFields }, ...prev];
            }
            return prev;
          });

          if (typeof setCompletedOrders === 'function') {
            setCompletedOrders(prev => prev.filter(so => !idsToComplete.includes(so.id)));
          }

        } else {
          // --- COMPLETING (Moving to History) ---
          setOrders(prev => prev.map(so => idsToComplete.includes(so.id) ? { ...so, ...completeFields } : so));
          
          // 👇 NEW: Instantly add it to the History tab without refreshing 👇
          if (typeof setCompletedOrders === 'function') {
            setCompletedOrders(prev => [{ ...fullOrderObject, ...completeFields }, ...prev]);
          }
        }

      } catch (error) { 
        alert(error.message || `Failed to ${undo ? 'undo' : 'complete'} order(s).`); 
      }
  };

  const handleDeleteOrder = async (orderId, childIds = null) => {
    if (!permissions) {
      alert("Permission denied. You do not have write access.");
      return;
    }    
    const idsToDelete = childIds && childIds.length > 0 ? childIds : [orderId];
    if (!window.confirm(`Are you sure you want to delete this order?`)) return;
    try {
      const response = await api.OperationsPlanner.delete({ orderIds: idsToDelete });
      if (!response?.success) throw new Error('Failed to delete order(s)');
      setOrders(prev => prev.filter(so => !idsToDelete.includes(so.id)));
    } catch (error) { alert(error.message || 'Failed to delete order(s).'); }
  };

  const exportActiveOrdersToExcel = () => {
    const data = filteredActiveOrders.map(so => ({
      'SO Date': so.soDate,
      'Deadline': so.deadline,
      'Lead Time': `${so.leadTime} days`,
      'Customer': so.customer,
      'SO Number': so.soNumber,
      'Tag Override': so.poTagNumber || '',
      'Amount ($)': so.amount,
      'Shipping Preference': so.pickupOrShip,
      'Payment Terms': so.paymentTerms || 'N/A',
      'Assigned To': so.assignedTo || 'Unassigned',
      'Packed Status': so.packedStatus || 'Pending',
      'Shipped Status': so.shippedStatus || 'Pending',
      'Invoice #': so.invoiceNumber || 'Pending',
      'Accounting Submission Date': so.accountingDate || 'Pending'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Active SO Planner');
    XLSX.writeFile(wb, `Fulfillment_Operations_Planner_${getTodayStr()}.xlsx`);
  };

  const customersList = useMemo(() => ['All', ...new Set(mergedOrders.map(s => s.customer).filter(Boolean))].sort(), [mergedOrders]);

  useEffect(() => {
    if (apiSuccess) { 
      const t = setTimeout(() => setApiSuccess(''), 2500); 
      return () => clearTimeout(t); 
    }
  }, [apiSuccess]);

  // Handle Bulk Complete for selected orders
  const handleBulkComplete = async () => {
    if (!permissions) {
      alert("Permission denied. You do not have write access.");
      return;
    }
    if (!selectedOrderIds || selectedOrderIds.length === 0) return;

    const count = selectedOrderIds.length;
    if (!window.confirm(`Are you sure you want to mark ${count} selected order(s) as Done?`)) return;

    const completeFields = { completed: true, completedAt: getTodayDateTimeStr() };

    try {
      const response = await api.OperationsPlanner.update({ 
        orderIds: selectedOrderIds, 
        ...completeFields 
      });

      if (!response?.success) throw new Error(response?.message || 'Failed to complete selected order(s)');

      // Update local state and clear selections
      setLocalOverrides(prev => {
        const updated = { ...prev };
        selectedOrderIds.forEach(id => { 
          updated[id] = { ...(prev[id] || {}), ...completeFields }; 
        });
        return updated;
      });

      setOrders(prev => prev.map(so => selectedOrderIds.includes(so.id) ? { ...so, ...completeFields } : so));
      setSelectedOrderIds([]);
      if (setApiSuccess) setApiSuccess(`${count} order(s) marked as complete.`);
    } catch (error) { 
      alert(error.message || 'Failed to complete order(s).'); 
    }
  };

  // Handle Bulk Delete for selected orders
  const handleBulkDelete = async () => {
    if (!permissions) {
      alert("Permission denied. You do not have write access.");
      return;
    }
    if (!selectedOrderIds || selectedOrderIds.length === 0) return;

    const count = selectedOrderIds.length;
    if (!window.confirm(`Are you sure you want to delete ${count} selected order(s)? This action cannot be undone.`)) return;

    try {
      const response = await api.OperationsPlanner.delete({ orderIds: selectedOrderIds });

      if (!response?.success) throw new Error(response?.message || 'Failed to delete selected order(s)');

      // Filter out deleted orders from state and clear selections
      setOrders(prev => prev.filter(so => !selectedOrderIds.includes(so.id)));
      setSelectedOrderIds([]);
      if (setApiSuccess) setApiSuccess(`${count} order(s) deleted successfully.`);
    } catch (error) { 
      alert(error.message || 'Failed to delete order(s).'); 
    }
  }; 

  // 1. Fetch read status from DB on mount / when user context loads
  useEffect(() => {
    let isCancelled = false;
    const loadReadStatus = async () => {
      if (!userAccess.userId) return;
      const dbReadIds = await fetchUserReadNotificationsFromDb(userAccess.userId);
      if (!isCancelled) {
        setReadNotificationIds(dbReadIds);
      }
    };
    loadReadStatus();
    return () => { isCancelled = true; };
  }, [userAccess.userId]);

  // 2. Mark single SO as read (Optimistic UI + DB Save)
  const handleMarkAsRead = useCallback(async (orderId) => {
    const idStr = String(orderId);
    
    // Immediate UI update
    setReadNotificationIds(prev => {
      const next = new Set(prev);
      next.add(idStr);
      return next;
    });

    // Update in-memory orders so so.readBy is immediately current
    setOrders(prev => prev.map(so => {
      if (String(so.id) === idStr) {
        const existing = so.readBy || [];
        return { ...so, readBy: [...existing, userAccess.userId] };
      }
      return so;
    }));

    // Persist to Database
    await persistReadNotificationsToDb(userAccess.userId, [idStr]);
  }, [userAccess.userId]);

    // 4. Calculate unread list: Checks both local DB read list AND so.readBy from the server
  const unreadNotifications = useMemo(() => {
    return activeOrders.filter(so => {
      const idStr = String(so.id);

      // (A) Check if this user ID is marked as read locally or fetched from DB
      if (readNotificationIds.has(idStr)) return false;

      // (B) Check if the SO record from MongoDB already has this user in `readBy`
      if (Array.isArray(so.readBy) && so.readBy.includes(userAccess.userId)) {
        return false;
      }

      // (C) Must not be marked completed
      if (so.completed) return false;

      // (D) Must match role-based customer permissions
      return isOrderAllowedForUser(so, userAccess);
    });
  }, [activeOrders, readNotificationIds, userAccess]);

  // 3. Mark all visible SOs as read (Optimistic UI + DB Save)
  const handleMarkAllAsRead = useCallback(async () => {
    const unreadIds = unreadNotifications.map(so => String(so.id));
    if (unreadIds.length === 0) return;

    // Immediate UI update
    setReadNotificationIds(prev => {
      const next = new Set(prev);
      unreadIds.forEach(id => next.add(id));
      return next;
    });

    // Update in-memory orders
    setOrders(prev => prev.map(so => {
      if (unreadIds.includes(String(so.id))) {
        const existing = so.readBy || [];
        return { ...so, readBy: [...existing, userAccess.userId] };
      }
      return so;
    }));

    // Persist to Database
    await persistReadNotificationsToDb(userAccess.userId, unreadIds);
  }, [unreadNotifications, userAccess.userId]);

  return (
    <>
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center text-white shrink-0 shadow-xs"><CalendarIcon size={20} /></div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Operations Planner</h2>
              <p className="text-xs text-gray-500">Sales order fulfillment, dispatch & schedules</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">          
            <span className={`text-[10px] px-2.5 py-1 rounded font-bold border transition-all duration-150 ${dataSource === 'api' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
              {dataSource === 'api' ? `● Live API · ${mergedOrders.length} Orders` : '⚠ Static Sample Data'}
            </span>
            <button onClick={() => fetchWeeklyPlannerData(calendarRangeWeeks, calendarWeekOffset)} className="flex items-center gap-1.5 bg-white border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors shadow-xs hover:bg-gray-50"><RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh</button>
            {permissions && (
              <>
                <button 
                onClick={exportActiveOrdersToExcel} 
                className="flex items-center gap-1.5 bg-white border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors shadow-xs hover:bg-gray-50"><Download size={13} /> Export Active</button>
                <button onClick={() => setShowSettings(true)} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 shadow-xs transition-colors"><Settings size={16} /></button>
              </>
            )}

            {/* ─── NOTIFICATION BELL ICON (OUTLOOK STYLE) ─── */}
            <button
              type="button"
              onClick={() => setShowNotifications(true)}
              className="relative p-2 border border-gray-300 rounded-lg hover:bg-gray-50 shadow-xs transition-colors cursor-pointer"
              title="Open Notifications"
              aria-label="Notifications"
            >
              <Bell size={16} className="text-gray-700" />
              {unreadNotifications.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.2 bg-red-600 text-white text-[10px] font-black rounded-full shadow-sm animate-pulse">
                  {unreadNotifications.length}
                </span>
              )}
            </button>            
          </div>
        </div>
      </header>

      <div className="p-6 bg-slate-50 min-h-screen">
        <div className="max-w-[1600px] mx-auto space-y-4">
          
          {/* Notification Toasts */}
          {apiSuccess && (
            <div className="bg-emerald-50 border-l-4 border-emerald-500 text-emerald-800 p-3 rounded-r-lg flex items-center justify-between shadow-xs animate-in slide-in-from-top-2 duration-200">
              <span className="text-xs font-bold flex items-center gap-1.5"><CheckCircle2 size={14} /> {apiSuccess}</span>
              <button onClick={() => setApiSuccess('')} className="text-emerald-600 hover:text-emerald-800"><X size={14} /></button>
            </div>
          )}

          {apiError && (
            <div className="bg-rose-50 border-l-4 border-rose-500 text-rose-800 p-3 rounded-r-lg flex items-center justify-between shadow-xs animate-in slide-in-from-top-2 duration-200">
              <span className="text-xs font-bold flex items-center gap-1.5"><AlertTriangle size={14} /> {apiError}</span>
              <button onClick={() => setApiError('')} className="text-rose-600 hover:text-rose-800"><X size={14} /></button>
            </div>
          )}

          <div className="flex bg-white rounded-xl p-1 border border-gray-200 shadow-xs gap-1">
            {[
              { id: 'table', icon: Layers, label: 'Orders', shortLabel: 'Orders' },
              { id: 'calendar', icon: CalendarIcon, label: 'Calendar', shortLabel: 'Calendar' },
              { id: 'completed', icon: CheckCircle2, label: 'Completed History', shortLabel: 'Completed' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-0 py-2 sm:py-2.5 px-2 sm:px-4 text-[11px] sm:text-sm font-bold rounded-lg flex items-center justify-center gap-1 sm:gap-2 transition-all ${
                  activeTab === tab.id
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <tab.icon size={15} className="shrink-0" />
                <span className="truncate">
                  <span className="sm:hidden">{tab.shortLabel}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </span>
              </button>
            ))}
          </div>

          {activeTab === 'table' && (
            <div className="space-y-3 sm:space-y-4 animate-in fade-in duration-200">

              {/* ─── SEARCH + FILTERS CARD ─── */}
              <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-xs space-y-3">

                {/* Row 1: Search + Customer + Staff + Grouping */}
                <div className="flex flex-col gap-2">

                  {/* Search — full width on mobile */}
                  <div className="relative w-full">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search SO, family, tag, customer, employee…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                    />
                  </div>

                  {/* Dropdowns + Grouping — stack on mobile, row on sm+ */}
                  <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2">

                    <select
                      value={customerFilter}
                      onChange={(e) => setCustomerFilter(e.target.value)}
                      className="w-full sm:w-auto sm:min-w-[160px] sm:max-w-[220px] px-3 py-2 border border-gray-300 rounded-lg text-xs font-semibold bg-white outline-none cursor-pointer truncate"
                    >
                      {customersList.map(c => (
                        <option key={c} value={c}>
                          {c === 'All' ? '— All Customers —' : c}
                        </option>
                      ))}
                    </select>

                    <select
                      value={assignedFilter}
                      onChange={(e) => setAssignedFilter(e.target.value)}
                      className="w-full sm:w-auto sm:min-w-[140px] px-3 py-2 border border-gray-300 rounded-lg text-xs font-semibold bg-white outline-none cursor-pointer"
                    >
                      <option value="All">— All Staff —</option>
                      <option value="Unassigned">Unassigned Only</option>
                      {employees.map(e => (
                        <option key={e.name} value={e.name}>{e.name}</option>
                      ))}
                    </select>

                    {/* Grouping toggle — full width on mobile */}
                    <div className="flex w-full sm:w-auto border border-gray-300 rounded-lg overflow-hidden sm:ml-auto">
                      <button
                        onClick={() => setGroupingMode('smart')}
                        className={`flex-1 sm:flex-none px-3 py-2 text-[11px] sm:text-xs font-bold transition-colors whitespace-nowrap ${
                          groupingMode === 'smart'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Smart Family Groups
                      </button>
                      <button
                        onClick={() => setGroupingMode('flat')}
                        className={`flex-1 sm:flex-none px-3 py-2 text-[11px] sm:text-xs font-bold transition-colors whitespace-nowrap ${
                          groupingMode === 'flat'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Flat View
                      </button>
                    </div>
                  </div>
                </div>

                {/* Row 2: Incomplete Filters */}
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 pt-2 border-t border-gray-100">
                  <span className="text-[9px] sm:text-[10px] font-bold text-gray-500 uppercase tracking-wider w-full sm:w-auto mb-1 sm:mb-0 sm:mr-1">
                    Filter Incomplete:
                  </span>

                  {[
                    { key: 'notPlanned', label: 'Not Planned', count: openCounts.notPlanned, icon: Layers },
                    { key: 'missingMaterials', label: 'Missing Materials', count: openCounts.missingMaterials, icon: Package },
                    { key: 'notPacked', label: 'Not Packed', count: openCounts.notPacked, icon: Package },
                    { key: 'notShipped', label: 'Not Shipped', count: openCounts.notShipped, icon: Truck },
                    { key: 'notInvoiced', label: 'Not Invoiced', count: openCounts.notInvoiced, icon: DollarSign },
                    { key: 'requiresInvoiceSub', label: 'Requires Invoice Sub', count: openCounts.requiresInvoiceSub, icon: UserCheck }
                  ].map((f) => {
                    const isActive = taskFilters[f.key];
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => toggleTaskFilter(f.key)}
                        className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-[11px] font-bold border transition-all select-none ${
                          isActive
                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-xs'
                            : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-600'
                        }`}
                      >
                        <f.icon size={11} className={`shrink-0 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                        <span className="whitespace-nowrap">{f.label}</span>
                        <span
                          className={`text-[8px] sm:text-[9px] px-1.5 py-0.5 rounded-full font-extrabold ${
                            isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {f.count}
                        </span>
                      </button>
                    );
                  })}

                  {isAnyTaskFilterActive && (
                    <button
                      type="button"
                      onClick={clearTaskFilters}
                      className="text-[10px] sm:text-[11px] text-indigo-600 hover:text-indigo-800 font-bold hover:underline flex items-center gap-1 transition-colors sm:ml-auto"
                    >
                      <X size={12} /> Clear Filters
                    </button>
                  )}
                </div>
              </div>

              {/* ─── IMPORT + BULK ACTIONS CARD ─── */}
              {permissions && (
                <div className="bg-white p-3 sm:p-4 rounded-xl border border-gray-200 shadow-xs">
                  <div className="flex flex-col gap-3">

                    {/* Import row — always full-width friendly */}
                    <div className="flex items-center gap-2 w-full">
                      <input
                        ref={importInputRef}
                        type="text"
                        placeholder="Enter the Sales Order (SO) number, e.g., SO-12345, SO-321456"
                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-xs uppercase font-semibold outline-none bg-white focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        onClick={setImportSO}
                        className="bg-indigo-600 text-white px-3 sm:px-4 py-2 rounded-lg text-xs font-bold hover:bg-indigo-700 transition-all flex items-center gap-1.5 shadow-xs shrink-0"
                      >                      
                        {importing ? ( 
                          <>
                            <RefreshCw size={10} className="animate-spin" />
                            <span>Importing...</span>                          
                          </>                         
                        ) : (
                          <>                         
                            <Import size={12} />
                            <span>Import</span>
                          </>                         
                        )}

                      </button>
                    </div>

                    {/* Selection action bar */}
                    {selectedOrderIds.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150">
                        <span className="text-[11px] sm:text-xs text-indigo-700 font-bold bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded shrink-0">
                          {selectedOrderIds.length} Selected
                        </span>

                        {canGroupFamily && selectedOrderIds.length >= 2 && (
                          <button
                            onClick={() => setShowCreateGroupModal(true)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors"
                          >
                            <Link2 size={12} />
                            <span className="whitespace-nowrap">Group as Family</span>
                          </button>
                        )}

                        {bulkEdit && (
                        <button
                          onClick={() => setBulkEditIds(selectedOrderIds)}
                          className="bg-amber-600 hover:bg-amber-700 text-white px-2.5 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors"
                        >
                          <Truck size={12} />
                          <span className="whitespace-nowrap">Bulk Edit</span>
                        </button>
                        )}

                        {bulkMarkComplete && (
                        <button
                          onClick={handleBulkComplete}
                          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] sm:text-xs font-bold transition-colors cursor-pointer whitespace-nowrap"
                        >
                          BULK COMPLETE
                        </button>
                        )}

                        {bulkDelete && (
                        <button
                          onClick={handleBulkDelete}
                          className="px-2.5 sm:px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-[10px] sm:text-xs font-bold transition-colors cursor-pointer whitespace-nowrap"
                        >
                          BULK DELETE
                        </button>
                        )}

                        <button
                          onClick={() => setSelectedOrderIds([])}
                          className="text-gray-400 hover:text-gray-700 p-1.5 rounded transition-colors ml-auto"
                          aria-label="Clear selection"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ─── TABLE CARD ─── */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden relative">
                {/* Responsive freeze rules: mobile = checkbox + customer only; desktop = all 4 */}
                <style>{`
                  .planner-table-container {
                    --w-check: 34px;
                    --w-col0: 70px;
                    --w-col1: 90px;
                  }
                  @media (min-width: 768px) {
                    .planner-table-container {
                      --w-check: 45px;
                      --w-col0: ${TABLE_COLUMNS[0]?.minW || '90px'};
                      --w-col1: ${TABLE_COLUMNS[1]?.minW || '120px'};
                    }
                  }

                  /* ── DESKTOP: freeze Checkbox + Date + Tag + Customer ── */
                  @media (min-width: 768px) {
                    .col-freeze-check {
                      position: sticky;
                      left: 0;
                      z-index: 30;
                    }
                    th.col-freeze-check { z-index: 40; }

                    .col-freeze-date {
                      position: sticky;
                      left: var(--w-check);
                      z-index: 30;
                    }
                    th.col-freeze-date { z-index: 40; }

                    .col-freeze-tag {
                      position: sticky;
                      left: calc(var(--w-check) + var(--w-col0));
                      z-index: 30;
                    }
                    th.col-freeze-tag { z-index: 40; }

                    .col-freeze-customer {
                      position: sticky;
                      left: calc(var(--w-check) + var(--w-col0) + var(--w-col1));
                      z-index: 30;
                    }
                    th.col-freeze-customer { z-index: 40; }
                  }

                  /* ── MOBILE: only Customer freezes; Checkbox/Date/Tag SCROLL ── */
                  @media (max-width: 767px) {
                    .col-freeze-check,
                    .col-freeze-date,
                    .col-freeze-tag {
                      position: static !important;
                      left: auto !important;
                      z-index: auto !important;
                    }

                    .col-freeze-customer {
                      position: sticky;
                      left: 0;           /* first frozen column on mobile */
                      z-index: 25;
                    }
                    th.col-freeze-customer {
                      z-index: 35;
                      /* keep header readable while body scrolls under it */
                      box-shadow: 2px 0 4px rgba(0,0,0,0.06);
                    }
                  }
                `}</style>

                { (loading || isSorting) && (
                  <div className="absolute inset-0 z-50 bg-white/60 flex items-center justify-center transition-all">
                    <RefreshCw size={24} className="animate-spin text-indigo-600" />
                  </div>
                )}

                <div className="overflow-auto max-h-[75vh] planner-table-container">
                  <table className="w-full text-xs text-left table-fixed min-w-[1800px] md:min-w-[2000px]">
                    <colgroup>
                      <col className="w-[34px] md:w-[45px]" />
                      {TABLE_COLUMNS.map((col, idx) => (
                        <col
                          key={col.key}
                          style={{
                            width:
                              idx === 0
                                ? 'var(--w-col0)'
                                : idx === 1
                                ? 'var(--w-col1)'
                                : col.minW,
                          }}
                        />
                      ))}
                    </colgroup>

                    <thead className="text-[10px] text-gray-500 uppercase bg-gray-50 font-bold tracking-wider">
                      <tr>
                        {/* Checkbox — fixed select-all logic */}
                        <th className="p-1.5 md:p-2 text-center sticky top-0 bg-gray-50 border-b border-gray-200 col-freeze-check">
                          <input
                            type="checkbox"
                            onChange={handleSelectAllActive}
                            checked={
                              filteredActiveOrders.length > 0 &&
                              filteredActiveOrders.every(so => selectedOrderIds.includes(so.id))
                            }
                            ref={(el) => {
                              if (el) {
                                const someSelected = filteredActiveOrders.some(so => selectedOrderIds.includes(so.id));
                                const allSelected =
                                  filteredActiveOrders.length > 0 &&
                                  filteredActiveOrders.every(so => selectedOrderIds.includes(so.id));
                                el.indeterminate = someSelected && !allSelected;
                              }
                            }}
                            className="rounded border-gray-300 text-indigo-600 cursor-pointer w-4 h-4"
                            aria-label="Select all orders"
                          />
                        </th>

                        {TABLE_COLUMNS.map((col, index) => {
                          const freezeClass =
                            index === 0
                              ? 'col-freeze-date'
                              : index === 1
                              ? 'col-freeze-tag'
                              : index === 2
                              ? 'col-freeze-customer shadow-[inset_-1px_0_0_#cbd5e1]'
                              : '';

                          return (
                            <th
                              key={col.key}
                              onClick={() => col.sortKey && handleSortRequest(col.sortKey)}
                              className={`p-1.5 md:p-2 whitespace-nowrap sticky top-0 z-20 bg-gray-50 border-b border-gray-200 ${freezeClass} ${
                                col.sortKey ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                              }`}
                            >
                              <div
                                className={`flex items-center gap-1 ${
                                  col.align === 'right'
                                    ? 'justify-end'
                                    : col.align === 'center'
                                    ? 'justify-center'
                                    : ''
                                }`}
                              >
                                {col.label}
                                {col.sortKey && (
                                  <span className="text-indigo-600 font-bold">
                                    {sortConfig.key === col.sortKey
                                      ? sortConfig.direction === 'asc'
                                        ? ' ↑'
                                        : ' ↓'
                                      : (
                                        <span className="opacity-20"> ⇅</span>
                                      )}
                                  </span>
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>

                    <tbody
                      className={
                        isSorting
                          ? 'opacity-30 pointer-events-none divide-y divide-gray-100'
                          : 'divide-y divide-gray-100'
                      }
                    >
                      {filteredActiveOrders.length === 0 ? (
                        <tr>
                          <td
                            colSpan={TOTAL_TABLE_COL_COUNT}
                            className="p-12 text-center text-gray-500 font-medium"
                          >
                            No Sales Orders found matching the selected filters.
                          </td>
                        </tr>
                      ) : (
                        rowStructure.rows.map((item) => {
                          if (item._isGroup) {
                            const g = item;
                            const vOrder = buildGroupVirtualOrder(g);
                            return (
                              <React.Fragment key={g.key}>
                                <OrderView
                                  so={vOrder}
                                  employees={employees}
                                  customerShipPref={customerShipPref}
                                  selectedOrderIds={selectedOrderIds}
                                  onToggleSelect={handleToggleSelectOrder}
                                  onPersistUpdate={handlePersistUpdate}
                                  onOpenPrepaymentModal={setPrepaymentModalOrder}
                                  onComplete={handleCompleteOrder}
                                  isGroupRow={true}
                                  isExpanded={expandedGroups[g.key]}
                                  onToggleExpand={() =>
                                    setExpandedGroups((p) => ({
                                      ...p,
                                      [g.key]: !p[g.key],
                                    }))
                                  }
                                  onUngroup={handleUngroup}
                                  onEditGroupTag={handleEditGroupTag}
                                  group={g}
                                  isSaving={savingId === vOrder.id}
                                  onDeleteSO={handleDeleteOrder}
                                  permissions={permissions}
                                />
                                {expandedGroups[g.key] &&
                                  g.orders.map((so) => (
                                    <OrderView
                                      key={so.id}
                                      so={so}
                                      employees={employees}
                                      customerShipPref={customerShipPref}
                                      selectedOrderIds={selectedOrderIds}
                                      onToggleSelect={handleToggleSelectOrder}
                                      onPersistUpdate={handlePersistUpdate}
                                      onOpenPrepaymentModal={setPrepaymentModalOrder}
                                      onComplete={handleCompleteOrder}
                                      isChildOfGroup={true}
                                      isSaving={savingId === so.id}
                                      onDeleteSO={handleDeleteOrder}
                                      permissions={permissions}
                                    />
                                  ))}
                              </React.Fragment>
                            );
                          }

                          // Single order
                          return (
                            <OrderView
                              key={item.id}
                              so={item}
                              employees={employees}
                              customerShipPref={customerShipPref}
                              selectedOrderIds={selectedOrderIds}
                              onToggleSelect={handleToggleSelectOrder}
                              onPersistUpdate={handlePersistUpdate}
                              onOpenPrepaymentModal={setPrepaymentModalOrder}
                              onComplete={handleCompleteOrder}
                              isSaving={savingId === item.id}
                              onDeleteSO={handleDeleteOrder}
                              permissions={permissions}
                            />
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          )}

          {activeTab === 'calendar' && (
            <CalendarView
              orders={mergedOrders}  /* ← was `orders`. MUST be mergedOrders */
              manualGroups={manualGroups}
              groupTagOverrides={groupTagOverrides}
              rangeWeeks={calendarRangeWeeks}
              weekOffset={calendarWeekOffset}
              onRangeChange={setCalendarRangeWeeks}
              onOffsetChange={setCalendarWeekOffset}
              permissions={permissions}
              onOpenBulkEdit={(ids) => setBulkEditIds(ids)}
            />
          )}

          {activeTab === 'completed' && <CompletedOrdersView completedOrders={completedOrders} loading={loadingCompleted} paginationMeta={paginationMeta} onPageChange={fetchCompletedOrders} permissions={permissions} undoComplete={handleCompleteOrder} />}
        </div>
      </div>
      
      {prepaymentModalOrder && <PrepaymentModal order={prepaymentModalOrder} onClose={() => setPrepaymentModalOrder(null)} onConfirm={handleConfirmPrepaymentReceived} confirm={confirm} />}
      {editModalOrder && <OrderEditModal order={editModalOrder} onClose={() => setEditModalOrder(null)} onUpdate={handleUpdateOrderField} />}
      {bulkEditIds && <BulkEditModal selectedIds={bulkEditIds} api={api} onClose={() => setBulkEditIds(null)} onApply={handleBulkEditApply} employees={employees} />}
      {showCreateGroupModal && <CreateGroupModal selectedCount={selectedOrderIds.length} onClose={() => setShowCreateGroupModal(false)} onCreate={handleCreateManualGroup} />}
      <NotificationsPanel
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={unreadNotifications}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
        userContext={userAccess}
      />        
      <SettingsPanel 
      open={showSettings} 
      onClose={() => setShowSettings(false)} 
      employees={employees} 
      onAddEmployee={handleAddEmployee} 
      onRemoveEmployee={handleRemoveEmployee} 
      />
    </>
  );
}